import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Card,
  Nav,
  Badge,
  Stack,
  Image,
  Modal,
  Spinner,
  Offcanvas,
  ListGroup,
} from "react-bootstrap";
import "../styles/AiotaMemberProfile.css";
import "../styles/EDCCInvoice.css";
import {
  Download,
  FileText,
  PenLine,
  TriangleAlert,
  Lock,
  Eye,
} from "lucide-react";
import heroBg from "../assets/images/HeroBanner.png";
import { useNavigate } from "react-router-dom";
import ChangePassword from "./ChangePassword";
import { GoSidebarCollapse } from "react-icons/go";
import aiotaLogo from "../assets/images/aiota_certi.png";
import layer from "../assets/images/Layer_certi.png";
import { RxHamburgerMenu } from "react-icons/rx";
import DeanACOT from "../assets/images/DeanACOT.png";
import html2pdf from "html2pdf.js";
import axios from "axios";
import { Clock, AlertTriangle } from "lucide-react";
import { generateReceiptPDF, openReceiptPDF } from "../utils/pdfGenerator";
import { countryCodes } from "../constants/countryCodes";
import FileList from "../components/FileList";
import { restrictionConfig } from "../constants/restrictionConfig";
import RestrictionModal from "../components/RestrictionModal";
// Toast-style alert for edit success/warning/error with consistent spacing and icon alignment
const EditMessageToast = ({ editMessage, sectionName }) => {
  if (!editMessage) return null;
  const iconMap = {
    success: (
      <i
        className="fas fa-check-circle flex-shrink-0"
        style={{ fontSize: "1rem" }}
        aria-hidden
      />
    ),
    danger: (
      <i
        className="fas fa-times-circle flex-shrink-0"
        style={{ fontSize: "1rem" }}
        aria-hidden
      />
    ),
    warning: (
      <i
        className="fas fa-exclamation-circle flex-shrink-0"
        style={{ fontSize: "1rem" }}
        aria-hidden
      />
    ),
  };
  const icon = iconMap[editMessage.type] || null;
  return (
    <Alert variant={editMessage.type} className="mb-4">
      <div
        className="d-flex align-items-start"
        style={{ gap: "0.75rem", lineHeight: 1.5 }}
      >
        <div className="flex-shrink-0" style={{ marginTop: "0.125rem" }}>
          {icon}
        </div>
        <div className="flex-grow-1" style={{ fontSize: "0.9375rem" }}>
          {sectionName && (
            <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
              <strong>Section:</strong> {sectionName}
            </div>
          )}
          <div>{editMessage.message}</div>
        </div>
      </div>
    </Alert>
  );
};

const AiotaMemberProfile = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem("aiota_member_active_tab") || "Approval Flow",
  );
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState("pending");
  const [editMessage, setEditMessage] = useState(null);
  const [editMessageSection, setEditMessageSection] = useState(""); // Track which section the message belongs to
  const [editingSection, setEditingSection] = useState("");
  const [originalData, setOriginalData] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSection, setSelectedSection] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const handleClose = () => setShowSidebar(false);
  const handleShow = () => setShowSidebar(true);
  // API base URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_BASEURL || "http://localhost:4890";
  const API_BASE_URL_ROLE =
    process.env.REACT_APP_API_BASE_ROLE || "http://localhost:7601";

  // PG Specialization dropdown options (must match registration form)
  const PG_SPECIALIZATION_OPTIONS = [
    "Paediatrics",
    "Neurology",
    "Orthopedics",
    "Oncology",
    "Hand Rehabilitation",
    "Rehabilitation",
    "Psychiatry",
    "Psychological Rehabilitation",
    "Developmental disabilities",
    "Sensory Integration",
    "Musculoskeletal Conditions",
    "Advanced",
  ];

  const [allMembers, setAllMembers] = useState(null);
  const [certificateSrc, setCertificateSrc] = useState(null);
  const [iotrCertificateSrc, setIotrCertificateSrc] = useState(null);
  const [idCardSrc, setIdCardSrc] = useState(null);
  const [isFullyApproved, setIsFullyApproved] = useState(false);
  // State for Good Standing and License Verification
  const [goodStandingStatus, setGoodStandingStatus] = useState({
    isGoodStandingDone: false,
    isGoodStandingApproved: false,
  });
  const [licenseVerificationStatus, setLicenseVerificationStatus] = useState({
    isLicenseVerificationDone: false,
    isLicenseVerificationApproved: false,
  });

  // Add these state variables for dropdown data
  const [countries, setCountries] = useState([]);
  const [statesPermanent, setStatesPermanent] = useState([]);
  const [districtsPermanent, setDistrictsPermanent] = useState([]);
  const [statesCorrespondence, setStatesCorrespondence] = useState([]);
  const [districtsCorrespondence, setDistrictsCorrespondence] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [sameAddress, setSameAddress] = useState(false);
  const [mobileNumberError, setMobileNumberError] = useState("");
  const [nameError, setNameError] = useState("");
  const [dobError, setDobError] = useState("");
  const [certificationNameError, setCertificationNameError] = useState("");
  const [adharPassportError, setAdharPassportError] = useState("");

  // State for Good Standing & License Verification
  const [showGoodStandingHistory, setShowGoodStandingHistory] = useState(false);
  const [showLicenseVerificationHistory, setShowLicenseVerificationHistory] =
    useState(false);
  const [goodStandingRequests, setGoodStandingRequests] = useState([]);
  const [licenseVerificationRequests, setLicenseVerificationRequests] =
    useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [showRestrictionModal, setShowRestrictionModal] = useState(false);
  const [showProfessionalStatusModal, setShowProfessionalStatusModal] = useState(false);
  const [showLicenseVerificationIneligibleModal, setShowLicenseVerificationIneligibleModal] = useState(false);

  const [certificationNameLocked, setCertificationNameLocked] = useState(false);
  // console.log("memberdata........................////////////////////////////////////////",memberData)

  // Handler to view document in new tab (works for both images and PDFs)
  const handleDocumentView = (fileUrl) => {
    if (!fileUrl) return;
    window.open(fileUrl, "_blank");
  };

  const handleDocumentDownload = async (fileUrl, docNameOrType) => {
    if (!fileUrl) return;

    // Use only the provided document name (clean filename without URL prefixes)
    let docName = docNameOrType || "document";

    // Remove any special characters from the name, keep only alphanumeric, spaces, dots, hyphens, underscores
    docName = docName.replace(/[^a-zA-Z0-9.\s_-]/g, "").trim();

    // If docName is empty or only numbers, use 'document'
    if (!docName || /^\d+$/.test(docName)) {
      docName = "document";
    }

    // Determine file extension from URL
    let extension = "";
    const urlMatch = fileUrl.match(/\.([a-zA-Z0-9]+)(\?|$)/);
    if (urlMatch) {
      extension = `.${urlMatch[1].toLowerCase()}`;
    }

    // Check if it's an image file
    const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(fileUrl);

    // Method 1: Try fetch with CORS (works if S3 allows CORS)
    try {
      const response = await fetch(fileUrl, {
        mode: "cors",
        credentials: "omit",
        cache: "no-cache",
      });

      if (response.ok) {
        const blob = await response.blob();

        // Determine file extension from blob type if not already set
        if (!extension) {
          if (blob.type.includes("png")) {
            extension = ".png";
          } else if (blob.type.includes("jpeg") || blob.type.includes("jpg")) {
            extension = ".jpg";
          } else if (blob.type.includes("gif")) {
            extension = ".gif";
          } else if (blob.type.includes("webp")) {
            extension = ".webp";
          } else if (blob.type.includes("pdf")) {
            extension = ".pdf";
          } else if (blob.type.includes("image")) {
            extension = ".jpg";
          }
        }

        // Create blob URL for download
        const blobUrl = window.URL.createObjectURL(blob);

        // Create download link - force download by using download attribute
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `${docName}${extension}`;
        link.setAttribute("download", `${docName}${extension}`);
        link.style.display = "none";
        link.rel = "noopener noreferrer";

        // Append to body and trigger download
        document.body.appendChild(link);
        link.click();

        // Cleanup after download
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);

        return; // Success, exit function
      }
    } catch (fetchError) {
      console.log("CORS fetch failed, trying alternative method:", fetchError);
    }

    // Method 2: For images, use img element + canvas to bypass CORS
    if (isImage) {
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";

        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = () => {
            // If crossOrigin fails, try without it
            const img2 = new Image();
            img2.onload = resolve;
            img2.onerror = reject;
            img2.src = fileUrl;
          };
          img.src = fileUrl;
        });

        // Create canvas and draw image
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const blobUrl = window.URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = blobUrl;
              link.download = `${docName}${extension || ".jpg"}`;
              link.setAttribute("download", `${docName}${extension || ".jpg"}`);
              link.style.display = "none";
              document.body.appendChild(link);
              link.click();
              setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
              }, 100);
            } else {
              // Fallback to direct link
              fallbackDownload(fileUrl, docName, extension);
            }
          },
          `image/${extension.replace(".", "") || "jpeg"}`,
        );

        return; // Success, exit function
      } catch (imgError) {
        console.log("Image canvas method failed, trying fallback:", imgError);
      }
    }

    // Method 3: Fallback - direct download link (may not work for cross-origin)
    fallbackDownload(fileUrl, docName, extension);
  };

  // Fallback download function
  const fallbackDownload = (fileUrl, docName, extension) => {
    try {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `${docName}${extension}`;
      link.setAttribute("download", `${docName}${extension}`);
      link.setAttribute("target", "_blank");
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    } catch (error) {
      console.error("All download methods failed:", error);
      // Last resort: open in new tab
      window.open(fileUrl, "_blank");
    }
  };

  // Fetch admins to set signatures
  const fetchAdminAllMembers = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }
      const API_BASE_URL =
        process.env.REACT_APP_BASEURL || "http://localhost:4890";
      const response = await fetch(`${API_BASE_URL}/getall`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      let membersData;
      if (Array.isArray(result)) {
        membersData = result;
      } else if (result.data && Array.isArray(result.data)) {
        membersData = result.data;
      } else {
        throw new Error("Unexpected response format from /getall");
      }
      setAllMembers(membersData); // Store in state
      return membersData;
    } catch (error) {
      console.error("Error fetching admin all members data:", error.message);
      setAllMembers(null);
      return null;
    }
  };
  useEffect(() => {
    fetchAdminAllMembers();
  }, []);
  useEffect(() => {
    // Get user info and token from localStorage
    const userData = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (!userData || !token) {
      navigate("/member-login");
      return;
    }
    try {
      const user = JSON.parse(userData);
      setUserInfo(user);
      console.log("User Info:", user);
      loadData(user, token);
    } catch (error) {
      console.error("Error parsing user data:", error);
      navigate("/member-login");
    }
  }, [navigate]);
  // Add this new useEffect to set initial tab based on approval status
  // useEffect(() => {
  //   // Only set initial tab based on approval status
  //   // Don't depend on memberData to avoid resetting tab during edits
  //   if (approvalStatus === "approved") {
  //     setActiveTab("Personal Information");
  //   } else {
  //     setActiveTab("Approval Flow");
  //   }
  // }, [approvalStatus]); // Only run when approval status changes

  useEffect(() => {
    localStorage.setItem("aiota_member_active_tab", activeTab);
  }, [activeTab]);

  // No modal on tab restore — modal is shown only when Apply Now is clicked

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_BASE_URL}/countries`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setCountries(result.data);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    fetchCountries();
  }, [API_BASE_URL]);

  // Fetch institutions on component mount
  useEffect(() => {
    const fetchInstitutions = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_BASE_URL}/getallinstitution`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setInstitutions(result.data);
        }
      } catch (error) {
        console.error("Error fetching institutions:", error);
      }
    };
    fetchInstitutions();
  }, [API_BASE_URL]);

  // Fetch states when country changes (Permanent Address)
  useEffect(() => {
    const fetchStatesPermanent = async () => {
      if (!memberData?.permanentAddress?.country) {
        setStatesPermanent([]);
        setDistrictsPermanent([]);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/states/${memberData.permanentAddress.country}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const result = await response.json();
        const statesData = result.data || result;
        if (Array.isArray(statesData)) {
          setStatesPermanent(statesData);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };
    fetchStatesPermanent();
  }, [memberData?.permanentAddress?.country, API_BASE_URL]);

  // Fetch districts when state changes (Permanent Address)
  useEffect(() => {
    const fetchDistrictsPermanent = async () => {
      if (
        !memberData?.permanentAddress?.country ||
        !memberData?.permanentAddress?.state
      ) {
        setDistrictsPermanent([]);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/districts/${memberData.permanentAddress.country}/${memberData.permanentAddress.state}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const result = await response.json();
        const districtsData = result.data || result;
        if (Array.isArray(districtsData)) {
          setDistrictsPermanent(districtsData);
        }
      } catch (error) {
        console.error("Error fetching districts:", error);
      }
    };
    fetchDistrictsPermanent();
  }, [
    memberData?.permanentAddress?.country,
    memberData?.permanentAddress?.state,
    API_BASE_URL,
  ]);

  // Fetch states when country changes (Correspondence Address)
  useEffect(() => {
    const fetchStatesCorrespondence = async () => {
      if (!memberData?.correspondenceAddress?.cocountry) {
        setStatesCorrespondence([]);
        setDistrictsCorrespondence([]);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/states/${memberData.correspondenceAddress.cocountry}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const result = await response.json();
        const statesData = result.data || result;
        if (Array.isArray(statesData)) {
          setStatesCorrespondence(statesData);
        }
      } catch (error) {
        console.error("Error fetching correspondence states:", error);
      }
    };
    fetchStatesCorrespondence();
  }, [memberData?.correspondenceAddress?.cocountry, API_BASE_URL]);

  // Fetch districts when state changes (Correspondence Address)
  useEffect(() => {
    const fetchDistrictsCorrespondence = async () => {
      if (
        !memberData?.correspondenceAddress?.cocountry ||
        !memberData?.correspondenceAddress?.costate
      ) {
        setDistrictsCorrespondence([]);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/districts/${memberData.correspondenceAddress.cocountry}/${memberData.correspondenceAddress.costate}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const result = await response.json();
        const districtsData = result.data || result;
        if (Array.isArray(districtsData)) {
          setDistrictsCorrespondence(districtsData);
        }
      } catch (error) {
        console.error("Error fetching correspondence districts:", error);
      }
    };
    fetchDistrictsCorrespondence();
  }, [
    memberData?.correspondenceAddress?.cocountry,
    memberData?.correspondenceAddress?.costate,
    API_BASE_URL,
  ]);
  // Add this useEffect after the existing useEffects
  useEffect(() => {
    // Check if all three approvals are complete
    const allApprovalsComplete =
      memberData?.secretaryApprovals === "approved" &&
      memberData?.treasurerApprovals === "approved" &&
      memberData?.presidentApprovals === "approved";

    // If currently on Approval Flow tab and all approvals are complete, switch to Personal Information
    if (allApprovalsComplete && activeTab === "Approval Flow") {
      setActiveTab("Personal Information");
    }
  }, [
    memberData?.secretaryApprovals,
    memberData?.treasurerApprovals,
    memberData?.presidentApprovals,
    activeTab,
  ]);
  // useEffect(() => {
  //   if (isFullyApproved) {
  //     setActiveTab("Personal Information");
  //   } else {
  //     setActiveTab("Approval Flow");
  //   }
  // }, [isFullyApproved]);

  const loadData = async (user, token) => {
    await fetchMemberData(user, token);
    await fetchEditPermissions(user, token);
  };
  const changePasswordAPI = async (passwordData) => {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authentication token not found. Please login again.");
    }
    const response = await fetch(`${API_BASE_URL_ROLE}/user-change-password`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(passwordData),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to change password");
    }
    return data;
  };
  const validateForm = () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("All fields are required.");
      return false;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirm password do not match.");
      return false;
    }

    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters long.");
      return false;
    }

    const passwordPattern =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!passwordPattern.test(newPassword)) {
      setPasswordError(
        "New password must contain at least one uppercase letter, one lowercase letter, one number, and one special character.",
      );
      return false;
    }

    if (currentPassword === newPassword) {
      setPasswordError("New password must be different from current password.");
      return false;
    }

    setPasswordError("");
    return true;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");
    if (!validateForm()) {
      return;
    }
    setPasswordLoading(true);
    try {
      const data = {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      };
      const response = await changePasswordAPI(data);
      setPasswordSuccess(response.message || "Password changed successfully!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => {
        setPasswordSuccess("");
        setActiveTab("Personal Information"); // Navigate back to default tab
      }, 2000);
    } catch (error) {
      console.error("Error changing password:", error);
      setPasswordError(
        error.message || "Failed to change password. Please try again.",
      );
    } finally {
      setPasswordLoading(false);
    }
  };
  const fetchMemberData = async (user, token) => {
    try {
      setLoading(true);
      setError(null);
      let apiEndpoint;
      if (user.rolename === "LifeMember") {
        apiEndpoint = `${API_BASE_URL}/getLifemembershipById/${user.details_id}`;
      } else if (user.rolename === "AssociateMember") {
        apiEndpoint = `${API_BASE_URL}/associate-membership/${user.details_id}`;
      } else {
        throw new Error("Unknown member type");
      }
      const response = await fetch(apiEndpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json().catch(() => ({}));
      const data = result.data;
      if (!response.ok) {
        const msg =
          result.message ||
          result.msg ||
          (response.status === 403 || response.status === 404
            ? "Your account may be inactive or access was denied. Please contact support."
            : "Failed to load member data.");
        setError(msg);
        setLoading(false);
        return;
      }
      // Function to format valid_till or expireOn
      const formatValidTill = (validTill, expireOn) => {
        if (expireOn) {
          // Parse expireOn (format: DD/MM/YYYY)
          const [day, month, year] = expireOn.split("/");
          const date = new Date(`${year}-${month}-${day}`);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            });
          }
        }
        // Fallback to valid_till
        if (validTill) {
          // If valid_till is a number (e.g., 2040), treat it as a year
          if (typeof validTill === "number" && validTill > 1970) {
            return validTill.toString(); // Return year as string (e.g., "2040")
          }
          // If valid_till is a valid date string, parse it
          const date = new Date(validTill);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            });
          }
        }
        return "";
      };
      if (data.secretary_approval_status) {
        setApprovalStatus(data.secretary_approval_status);
      }

      const baseS3Url =
        "https://aiota-live.s3.ap-south-1.amazonaws.com/certificates/";
      // console.log("data.lifeCertiticate", data.lifeCertiticate);
      // console.log("data.associateCertificate", data.associateCertificate);
      const lifeCertificateFilename =
        data.life_certificate ||
        data.old_certificate ||
        data.lifeCertiticate ||
        null;
      let lifeCertificateUrl = null;
      if (lifeCertificateFilename) {
        // If the filename already contains the base URL, use as is
        if (
          lifeCertificateFilename.startsWith("http://") ||
          lifeCertificateFilename.startsWith("https://")
        ) {
          lifeCertificateUrl = lifeCertificateFilename;
        } else {
          const encodedFilename = lifeCertificateFilename.replace(/ /g, "+");
          lifeCertificateUrl = `${baseS3Url}${encodedFilename}`;
        }
      }
      // Handle associate certificate separately
      let associateCertificateUrl = null;
      if (data.associateCertificate) {
        if (
          data.associateCertificate.startsWith("http://") ||
          data.associateCertificate.startsWith("https://")
        ) {
          associateCertificateUrl = data.associateCertificate;
        } else {
          const encodedFilename = data.associateCertificate.replace(/ /g, "+");
          associateCertificateUrl = `${baseS3Url}${encodedFilename}`;
        }
      }
      console.log("lifeCertificateUrl", lifeCertificateUrl);
      console.log("associateCertificateUrl", associateCertificateUrl);
      // --- Add similar logic for IOTR certificate and ID card ---
      console.log("data.iotr_certificate", data.iotr_certiticate);
      console.log("data.iotrCertificate", data.iotrCertiticate);
      const iotrCertificateFilename =
        data.iotrCertiticate ||
        data.old_iotr_certificate ||
        data.iotr_certiticate ||
        null;
      let iotrCertificateUrl = null;
      if (iotrCertificateFilename) {
        if (
          iotrCertificateFilename.startsWith("http://") ||
          iotrCertificateFilename.startsWith("https://")
        ) {
          iotrCertificateUrl = iotrCertificateFilename;
        } else {
          const encodedFilename = iotrCertificateFilename.replace(/ /g, "+");
          iotrCertificateUrl = `${baseS3Url}${encodedFilename}`;
        }
      }
      console.log("iotrCertificateUrl", iotrCertificateUrl);

      // Handle old_certificate separately for documents section
      let oldCertificateUrl = null;
      if (data.old_certificate) {
        if (
          data.old_certificate.startsWith("http://") ||
          data.old_certificate.startsWith("https://")
        ) {
          oldCertificateUrl = data.old_certificate;
        } else {
          const encodedFilename = data.old_certificate.replace(/ /g, "+");
          oldCertificateUrl = `${baseS3Url}${encodedFilename}`;
        }
      }

      // Handle old_iotr_certificate separately for documents section
      let oldIotrCertificateUrl = null;
      if (data.old_iotr_certificate) {
        if (
          data.old_iotr_certificate.startsWith("http://") ||
          data.old_iotr_certificate.startsWith("https://")
        ) {
          oldIotrCertificateUrl = data.old_iotr_certificate;
        } else {
          const encodedFilename = data.old_iotr_certificate.replace(/ /g, "+");
          oldIotrCertificateUrl = `${baseS3Url}${encodedFilename}`;
        }
      }

      const idCardFilename = data.id_card_certificate || data.id_card || null;
      let idCardUrl = null;
      if (idCardFilename) {
        if (
          idCardFilename.startsWith("http://") ||
          idCardFilename.startsWith("https://")
        ) {
          idCardUrl = idCardFilename;
        } else {
          const encodedFilename = idCardFilename.replace(/ /g, "+");
          idCardUrl = `${baseS3Url}${encodedFilename}`;
        }
      }

      const receiptCertificateFilename = data.receipt_certificate || null;
      console.log(
        "receiptCertificateFilename``````````",
        data.receipt_certificate,
      );
      let receiptCertificateUrl = null;
      if (receiptCertificateFilename) {
        if (
          receiptCertificateFilename.startsWith("http://") ||
          receiptCertificateFilename.startsWith("https://")
        ) {
          receiptCertificateUrl = receiptCertificateFilename;
        } else {
          const encodedFilename = receiptCertificateFilename.replace(/ /g, "+");
          receiptCertificateUrl = `${baseS3Url}${encodedFilename}`;
        }
      }
      console.log("receiptCertificateUrl", receiptCertificateUrl);

      console.log("idCardUrl", idCardUrl);
      console.log("data.ug_institution::::::hjhjhh", data.ug_institution);

      // Helper function to extract ID from object or string
      const extractId = (field) => {
        if (field === undefined || field === null) return "";
        if (typeof field === "number") return String(field);
        if (typeof field === "string") return field.trim();
        if (typeof field === "object") {
          if (field._id !== undefined && field._id !== null)
            return String(field._id).trim();
        }
        return "";
      };

      // Helper function to extract name from populated object or return ID/string as fallback
      const extractName = (field) => {
        if (field === undefined || field === null) return "";
        if (typeof field === "number") return String(field);
        if (typeof field === "string") return field.trim();
        if (typeof field === "object") {
          // Check for populated fields with name properties
          if (field.country_name) return field.country_name;
          if (field.state_name) return field.state_name;
          if (field.district_name) return field.district_name;
          if (field.name_and_place_institution)
            return field.name_and_place_institution;
          // Fallback to ID if no name property
          if (field._id !== undefined && field._id !== null)
            return String(field._id).trim();
        }
        return "";
      };

      // Helper to get specialization as string (API may return string or populated object)
      const getSpecializationValue = (field) => {
        if (field === undefined || field === null) return "";
        if (typeof field === "number") return String(field);
        if (typeof field === "string") return field.trim();
        if (typeof field === "object") {
          const name =
            field.name ||
            field.specialization_name ||
            field.specialization ||
            field.label;
          if (name != null && String(name).trim()) return String(name).trim();
          if (field._id != null) return String(field._id).trim();
        }
        return "";
      };

      const DISABLE_IOTR = process.env.REACT_APP_DISABLE_IOTR_NEW_APPLICANTS === 'true';
      const IOTR_CUTOFF_DATE = new Date('2026-06-12');

      // hideIotr is true if: toggle is ON AND member was approved (certificate issued) ON or AFTER cutoff AND is NOT an old member
      const approvalDate = data?.certificate_issued_date ? new Date(data.certificate_issued_date) : new Date(0);
      const hideIotr = DISABLE_IOTR && approvalDate >= IOTR_CUTOFF_DATE && !data?.is_old_member;

      const transformedData = {
        hideIotr: hideIotr,
        editRejections: data.editRejections || [],
        editApprovals: data.editApprovals || [],
        useredited: data.useredited || [],
        createdAt: data.createdAt || "",
        old_certificate_issued_date: data.old_certificate_issued_date || "",
        name: data.name || data.nametobe_printed || "",
        fullName: data.name || data.nametobe_printed || "",
        previousName: data.previous_name || "",
        dateOfBirth: normalizeDateToYYYYMMDD(data.dob) || "",
        gender: data.gender || "",
        mobileNumber: data.phone || "",
        phoneNumber: data.phone || "",
        countryCode: data.countryCode || "+91",
        aadhaarPassport: data.adhar_passport || data.aadhaar_passport || "",
        email: data.email || user.email || "",
        certificationName:
          data.certification_name_locked || data.nametobe_printed || data.name,
        certificationNameLocked: !!data.certification_name_locked,
        certificationName: data.nametobe_printed || data.name,
        secretaryApprovals: data.secretary_approval_status || "pending",
        secretaryRejectedReason: data.secretary_rejection_reason || "",
        treasurerApprovals: data.treasurer_approval_status || "pending",
        treasurerRejectedReasons: data.treasurer_rejected_reason || "",
        presidentApprovals: data.president_approval_status || "pending",
        presidentRejectedReason: data.president_rejected_reason || "",
        certificationName: data.nametobe_printed || data.name,
        permanentAddress: {
          building: data.flat_house_building || data.address || "",
          street: data.colony_street_locality,
          landmark: data.landmark,
          country: extractId(data.country), // Store ID for form submission
          countryName: extractName(data.country), // Store name for display
          state: extractId(data.state), // Store ID for form submission
          stateName: extractName(data.state), // Store name for display
          district: extractId(data.district), // Store ID for form submission
          districtName: extractName(data.district), // Store name for display
          pincode: data.pincode,
        },
        correspondenceAddress: {
          cobuilding: data.co_flat_house_building,
          costreet: data.Co_colony_street_locality,
          colandmark: data.co_landmark,
          cocountry: extractId(data.Co_country), // Store ID for form submission
          cocountryName: extractName(data.Co_country), // Store name for display
          costate: extractId(data.Co_state), // Store ID for form submission
          costateName: extractName(data.Co_state), // Store name for display
          codistrict: extractId(data.Co_district), // Store ID for form submission
          codistrictName: extractName(data.Co_district), // Store name for display
          copincode: data.Co_pincode,
        },
        ugDetails: {
          institution:
            extractId(data.ug_institution) ||
            extractId(data.other_institution) ||
            "", // Store ID for form submission
          institutionName:
            extractName(data.ug_institution) ||
            extractName(data.other_institution) ||
            "", // Store name for display
          degree: data.ug_degree || "",
          university: data.ug_university,
          completionDate: data.ug_completion_date,
          ug_non_accredited_college_name: data.ug_non_accredited_college_name,
        },
        pgDetails: {
          pginstitution: extractId(data.pg_institution) || "", // Store ID for form submission
          pginstitutionName: extractName(data.pg_institution) || "", // Store name for display
          abroadInstitution: data.pg_institution_abroad,
          degree: data.pg_degree,
          specialization:
            getSpecializationValue(data.pg_specialization) ||
            getSpecializationValue(data.pgSpecialization) ||
            getSpecializationValue(data.specialization) ||
            "",
          otherSpecialization: data.pg_other_specialization,
          completionDate: data.pg_completion_date,
          pg_non_accredited_college_name: data.pg_non_accredited_college_name,
        },
        otherQualifications: (() => {
          const rawOther = data.other_qualification || "";
          const rawQual =
            data.qualification || data.professional_qualifications || "";
          const isRadio = (v) => v === "PHD" || v === "PGDR";
          const qualification = isRadio(rawOther)
            ? rawOther
            : isRadio(rawQual)
              ? rawQual
              : "";
          const otherqualification =
            rawOther && !isRadio(rawOther)
              ? rawOther
              : rawQual && !isRadio(rawQual)
                ? rawQual
                : "";
          return {
            qualification,
            institution:
              extractId(data.other_specialization) ||
              extractId(data.other_institution) ||
              data.professionalqualification_obtained_from ||
              "",
            institutionName:
              extractName(data.other_specialization) ||
              extractName(data.other_institution) ||
              data.professionalqualification_obtained_from ||
              "",
            researchArea: data.research_area,
            othercompletiondate: data.other_completion_date,
            otherqualification:
              otherqualification || data.professional_qualifications || "",
          };
        })(),
        documents: [
          // {
          // type: "Profile Photo",
          // name: data.file_url ? data.file_url.split('/').pop() : "", // Extract filename from URL
          // url: data.file_url || "",
          // },
          {
            type: "Aadhar Copy",
            name: data.Aadhar_copy ? data.Aadhar_copy.split("-").pop() : "N/A",
            url: data.Aadhar_copy || "",
          },
          {
            type: "Internship Certificate",
            name: data.internship_completion_certficate
              ? data.internship_completion_certficate.split("-").pop()
              : "N/A",
            url: data.internship_completion_certficate || "",
          },
          {
            type: "Membership Application",
            name: data.character_certificate
              ? data.character_certificate.split("-").pop()
              : "N/A",
            url: data.membership_application || "",
          },
          {
            type: "College Leaving Certificate",
            name: data.college_leaving_certificate
              ? data.college_leaving_certificate.split("-").pop()
              : "N/A",
            url: data.college_leaving_certificate || "",
          },
          {
            type: "Provisional Certificate",
            name: data.provisional_certificate
              ? data.provisional_certificate.split("-").pop()
              : "N/A",
            url: data.provisional_certificate || "",
          },
          {
            type: "Color Passport Size Photo",
            name: data.file_url ? data.file_url.split("-").pop() : "N/A",
            url: data.file_url || "",
          },
          {
            type: "Life Certificate",
            name: data.Life_certiticate
              ? data.Life_certiticate.split("-").pop()
              : "N/A",
            url: data.Life_certiticate || "",
          },
          {
            type: "Resume",
            name: data.resume ? data.resume.split("-").pop() : "N/A",
            url: data.resume || "",
          },
          {
            type: "Pay Resume",
            name: data.pay_resume ? data.pay_resume.split("-").pop() : "N/A",
            url: data.pay_resume || "",
          },
          ...(hideIotr ? [] : [
            {
              type: "IOTR Certificate",
              name: data.Iotr_certiticate
                ? data.Iotr_certiticate.split("-").pop()
                : "N/A",
              url: data.Iotr_certiticate || "",
            }
          ]),
          {
            type: "ID Card",
            name: data.Id_card ? data.Id_card.split("-").pop() : "N/A",
            url: data.Id_card || "",
          },
          {
            type: "ACOT Certificate",
            name: data.Acot_certiticate
              ? data.Acot_certiticate.split("-").pop()
              : "N/A",
            url: data.Acot_certiticate || "",
          },
          {
            type: "Old Certificate",
            name: data.old_certificate
              ? data.old_certificate.includes("/")
                ? data.old_certificate.split("/").pop()
                : data.old_certificate.split("-").pop()
              : "N/A",
            url: oldCertificateUrl || "",
          },
          ...(hideIotr ? [] : [
            {
              type: "Old IOTR Certificate",
              name: data.old_iotr_certificate
                ? data.old_iotr_certificate.includes("/")
                  ? data.old_iotr_certificate.split("/").pop()
                  : data.old_iotr_certificate.split("-").pop()
                : "N/A",
              url: oldIotrCertificateUrl || "",
            }
          ]),
          {
            type: "Highest Qualification",
            name: data.highest_qualification
              ? data.highest_qualification.includes("/")
                ? data.highest_qualification.split("/").pop()
                : data.highest_qualification.split("-").pop()
              : "N/A",
            url: data.highest_qualification || "",
          },
        ],
        renewal: {
          transactionRef: "",
          transactionDate: "",
          receiptUrl: data.Transaction_screenshot || "",
          editable: false,
        },
        // Renewal approval workflow fields
        treasurerApproval:
          data.is_treasurer_renewal_approved ||
          data.treasurer_renewal_approval_status ||
          data.treasurer_approval_status ||
          "pending",
        treasurerRejected: data.treasurer_rejected || false,
        treasurerRejectedReason: data.treasurer_rejected_reason || "",
        secretaryApproval:
          data.is_secretary_renewal_approved ||
          data.is_secreatry_renewal_approved ||
          data.secretary_renewal_approval_status ||
          "pending",
        secretaryRejected: data.secreatry_rejected || false,
        secretary_rejection_reason: data.secretary_rejection_reason || "",
        secretary_renewal_rejection_reason:
          data.secretary_rejected_reason || "",
        certificateIssuedRenewal:
          data.is_certificate_id_card_issued_renewal === true ||
          data.s_certificate_id_card_issued_renewal === true,
        renewalPaymentStatus: data.renewal_payment_status || "",
        renewal_payment_status: data.renewal_payment_status || "",
        payment_status: data.payment_status || "",
        amount_IN: data.amount_IN || 0,
        valid_till: data.valid_till || "",
        certificateApproval: data.certificate_approval || "pending",
        rejectionReason: data.certificate_rejection_reason || "",
        certificateno: data.certificateno || "",
        membershipFrom: data.membership_from || "",
        membershipTo: data.membership_to || "",
        // New fields from API response
        amount_IN_renewal: data.amount_IN_renewal || 0,
        renewal_late_fee: data.renewal_late_fee || 0,
        is_treasurer_renewal_approved:
          data.is_treasurer_renewal_approved || "pending",
        is_secretary_renewal_approved:
          data.is_secretary_renewal_approved || "pending",
        memberDate: data.certficate_issued_date || new Date().toISOString(),
        // Renewal certificates
        renewalAssociateCertificates: data.renewalAssociateCertificates || [],
        renewedlifeCertiticate: data.renewedlifeCertiticate || null,
        validTill: (() => {
          // Prefer expireOn if present, else valid_till
          const raw = data.expireOn || data.valid_till || "";
          if (!raw) return "";
          // If already in DD/MM/YYYY format, return as is
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
          // If in ISO format (e.g., 2038-10-09T10:54:45.047+00:00)
          const date = new Date(raw);
          if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
          }
          // If it's a year (number), return as string
          if (typeof raw === "number" && raw > 1970) return raw.toString();
          return raw;
        })(),
        liscenceNo: data.liscenceNo || "",
        isLegacyNum: data.is_legacy_num || false,
        legacyLicenseNo: data.legacy_license_no || "",
        iotrNumber: hideIotr ? "" : (data.iotr || ""),
        lifeCertificateUrl: lifeCertificateUrl,
        associateCertificate: associateCertificateUrl,
        iotrCertificateUrl: iotrCertificateUrl,
        idCardUrl: idCardUrl,
        receiptCertificateUrl: receiptCertificateUrl,
        file_url: data.file_url || "",
        // Good Standing and License Verification status
        isGoodStandingDone: data.is_good_standing_done || false,
        isGoodStandingApproved: data.is_good_standing_approved || false,
        isLicenseVerificationDone: data.is_license_verification_done || false,
        isLicenseVerificationApproved:
          data.is_license_verification_approved || false,
      };
      setMemberData((prev) => {
        const next = { ...transformedData };
        if (prev) {
          // Preserve address fields if API returns empty
          if (!next.permanentAddress.country && prev.permanentAddress?.country)
            next.permanentAddress = {
              ...next.permanentAddress,
              country: prev.permanentAddress.country,
            };
          if (!next.permanentAddress.state && prev.permanentAddress?.state)
            next.permanentAddress = {
              ...next.permanentAddress,
              state: prev.permanentAddress.state,
            };
          if (
            !next.permanentAddress.district &&
            prev.permanentAddress?.district
          )
            next.permanentAddress = {
              ...next.permanentAddress,
              district: prev.permanentAddress.district,
            };
          if (
            !next.correspondenceAddress.cocountry &&
            prev.correspondenceAddress?.cocountry
          )
            next.correspondenceAddress = {
              ...next.correspondenceAddress,
              cocountry: prev.correspondenceAddress.cocountry,
            };
          if (
            !next.correspondenceAddress.costate &&
            prev.correspondenceAddress?.costate
          )
            next.correspondenceAddress = {
              ...next.correspondenceAddress,
              costate: prev.correspondenceAddress.costate,
            };
          if (
            !next.correspondenceAddress.codistrict &&
            prev.correspondenceAddress?.codistrict
          )
            next.correspondenceAddress = {
              ...next.correspondenceAddress,
              codistrict: prev.correspondenceAddress.codistrict,
            };
          // Preserve academics data if API returns empty/invalid (when other sections have errors)
          if (
            (!next.ugDetails?.institution ||
              next.ugDetails.institution === "") &&
            prev.ugDetails?.institution
          ) {
            next.ugDetails = {
              ...next.ugDetails,
              institution: prev.ugDetails.institution,
            };
          }
          if (
            (!next.ugDetails?.degree || next.ugDetails.degree === "") &&
            prev.ugDetails?.degree
          ) {
            next.ugDetails = {
              ...next.ugDetails,
              degree: prev.ugDetails.degree,
            };
          }
          if (
            (!next.pgDetails?.pginstitution ||
              next.pgDetails.pginstitution === "") &&
            prev.pgDetails?.pginstitution
          ) {
            next.pgDetails = {
              ...next.pgDetails,
              pginstitution: prev.pgDetails.pginstitution,
            };
          }
          if (
            (!next.pgDetails?.specialization ||
              next.pgDetails.specialization === "") &&
            prev.pgDetails?.specialization
          ) {
            next.pgDetails = {
              ...next.pgDetails,
              specialization: prev.pgDetails.specialization,
            };
          }
          if (
            (!next.otherQualifications?.qualification ||
              next.otherQualifications.qualification === "") &&
            prev.otherQualifications?.qualification
          ) {
            next.otherQualifications = {
              ...next.otherQualifications,
              qualification: prev.otherQualifications.qualification,
            };
          }
        }
        return next;
      });
      setCertificationNameLocked(!!data.certification_name_locked);

      // Update status states
      setGoodStandingStatus({
        isGoodStandingDone: data.is_good_standing_done || false,
        isGoodStandingApproved: data.is_good_standing_approved || false,
      });
      setLicenseVerificationStatus({
        isLicenseVerificationDone: data.is_license_verification_done || false,
        isLicenseVerificationApproved:
          data.is_license_verification_approved || false,
      });
    } catch (err) {
      console.error("Error fetching member data:", err);
      const msg =
        err?.message?.includes("403") || err?.message?.includes("404")
          ? "Your account may be inactive or access was denied. Please contact support."
          : "Failed to load member data. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Lock certification name when fully approved
  useEffect(() => {
    const lockCertificationName = async () => {
      if (isFullyApproved && !certificationNameLocked && userInfo?.details_id) {
        try {
          const token = localStorage.getItem("token");
          const response = await fetch(
            `${API_BASE_URL}/profile/lock-certification-name/${userInfo.details_id}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (response.ok) {
            setCertificationNameLocked(true);
            console.log("Certification name locked successfully");
          }
        } catch (error) {
          console.error("Error locking certification name:", error);
        }
      }
    };

    lockCertificationName();
  }, [
    isFullyApproved,
    certificationNameLocked,
    userInfo?.details_id,
    API_BASE_URL,
  ]);
  // New function to check edit request status
  const checkEditRequestStatus = async (section, tokenParam = null) => {
    try {
      const token = tokenParam || localStorage.getItem("token");
      if (!userInfo || !token) {
        return {
          success: false,
          canEdit: false,
          status: "unauthorized",
          message: "User not authenticated",
        };
      }
      const response = await fetch(
        `${API_BASE_URL}/profile/checkEditStatus/${userInfo.details_id
        }?section=${encodeURIComponent(section)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error checking edit status:", error);
      return {
        success: false,
        canEdit: false,
        status: "error",
        message: error.message,
      };
    }
  };

  // Function to check if a section has pending edits
  const hasPendingEditsInSection = (sectionName) => {
    if (!memberData?.useredited || !Array.isArray(memberData.useredited))
      return false;

    const sectionKeyMap = {
      "Personal Information": "personal_information_approved",
      Address: "address_approved",
      "Academic Details": "academic_details_approved",
      "Uploaded Documents": "documents_approved",
    };

    const approvalKey = sectionKeyMap[sectionName];
    if (!approvalKey) return false;

    return memberData.useredited.some(
      (edit) =>
        edit.section === sectionName && memberData[approvalKey] === false,
    );
  };

  // Function to check if a section has a pending edit request (at least one field still pending in latest edit)
  const hasPendingEditRequest = (sectionName) => {
    const latestEdit = getLatestEditStatusForSection(sectionName);
    if (!latestEdit) return false;
    // Allow new edit only when all fields in the latest edit are approved or rejected
    return !hasAllFieldsProcessedForSection(sectionName);
  };

  // Helper function to check if there are any changes
  const hasChanges = () => {
    if (!originalData || !memberData) return false;
    const changedFields = [];
    const compareObjects = (original, current, path = "") => {
      for (const key in current) {
        const fullPath = path ? `${path}.${key}` : key;
        if (
          typeof current[key] === "object" &&
          current[key] !== null &&
          !Array.isArray(current[key])
        ) {
          if (original[key]) {
            compareObjects(original[key], current[key], fullPath);
          }
        } else if (original[key] !== current[key]) {
          changedFields.push(fullPath);
        }
      }
    };
    compareObjects(originalData, memberData);
    return changedFields.length > 0;
  };

  // Updated handleEditClick function
  const handleEditClick = (section) => {
    // Check if there's a pending edit request for this section
    if (hasPendingEditRequest(section)) {
      setEditMessage({
        type: "warning",
        message:
          "You have a pending edit request for this section. Please wait for approval or rejection before making another edit request.",
      });
      setEditMessageSection(section);
      return;
    }

    setIsEditing(true);
    setEditingSection(section);

    // Ensure pgDetails is properly initialized with all fields before setting originalData
    const currentData = { ...memberData };
    if (!currentData.pgDetails) {
      currentData.pgDetails = {};
    }
    // Ensure all pgDetails fields are initialized, preserving existing values
    currentData.pgDetails = {
      pginstitution: currentData.pgDetails?.pginstitution || "",
      pginstitutionName: currentData.pgDetails?.pginstitutionName || "",
      abroadInstitution: currentData.pgDetails?.abroadInstitution || "",
      degree: currentData.pgDetails?.degree || "",
      specialization: currentData.pgDetails?.specialization || "",
      otherSpecialization: currentData.pgDetails?.otherSpecialization || "",
      completionDate: currentData.pgDetails?.completionDate || "",
      pg_non_accredited_college_name:
        currentData.pgDetails?.pg_non_accredited_college_name || "",
    };

    // Update memberData to ensure pgDetails is initialized
    setMemberData(currentData);
    setOriginalData(JSON.parse(JSON.stringify(currentData))); // Deep clone for rollback

    setEditMessage({
      type: "info",
      message:
        "You can now edit this section. Make your changes and click Save.",
    });
    // Clear mobile number error when starting to edit
    setMobileNumberError("");
  };
  // New function to submit edit request
  const submitEditRequest = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!userInfo || !token) {
        setEditMessage({
          type: "danger",
          message: "User not authenticated. Please log in again.",
        });
        setShowEditModal(false);
        return;
      }
      const userString = localStorage.getItem("user");
      const user = userString ? JSON.parse(userString) : null;
      const userId = user?.details_id;
      const response = await fetch(
        `${API_BASE_URL}/profile/submitEditRequest`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: userId,
            section: selectedSection,
          }),
        },
      );
      const result = await response.json();
      setEditMessage({
        type: response.ok ? "success" : "danger",
        message: result.message,
      });
      setShowEditModal(false);
    } catch (error) {
      console.error("Error submitting edit request:", error);
      setEditMessage({
        type: "danger",
        message: "Failed to submit edit request. Please try again.",
      });
      setShowEditModal(false);
    }
  };

  const fetchEditPermissions = async (token) => {
    // No longer needed - direct editing without approval
    return;
  };

  const saveChanges = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!userInfo || !token) {
        setEditMessage({
          type: "danger",
          message: "User not authenticated. Please log in again.",
        });
        return;
      }

      setEditMessage({
        type: "info",
        message: "Saving changes...",
      });

      const changedFields = [];

      // Helper to compare objects and find changed fields (only for the editing section)
      const compareObjects = (
        original,
        current,
        path = "",
        sectionFilter = null,
      ) => {
        for (const key in current) {
          const fullPath = path ? `${path}.${key}` : key;
          if (
            typeof current[key] === "object" &&
            current[key] !== null &&
            !Array.isArray(current[key])
          ) {
            // Only recurse into nested objects if they're part of the editing section
            if (original[key]) {
              compareObjects(
                original[key],
                current[key],
                fullPath,
                sectionFilter,
              );
            } else if (current[key] && Object.keys(current[key]).length > 0) {
              // New nested object - check if it's part of editing section
              compareObjects({}, current[key], fullPath, sectionFilter);
            }
          } else {
            // Compare primitive values
            const originalVal =
              original && original[key] !== undefined ? original[key] : null;
            const currentVal = current[key];
            if (originalVal !== currentVal) {
              changedFields.push(fullPath);
            }
          }
        }
      };

      compareObjects(originalData, memberData);

      // For Uploaded Documents, also allow save when user added new files (compareObjects skips arrays)
      const documentsWithNewFilesForCheck =
        editingSection === "Uploaded Documents"
          ? memberData?.documents?.filter(
            (doc) => doc.newFile && doc.newFile instanceof File,
          ) || []
          : [];

      console.log("Changed fields:", changedFields);
      console.log("Editing section:", editingSection);

      // Check if there are any changes before proceeding (all sections including Uploaded Documents)
      const hasDocumentFilesOnly =
        editingSection === "Uploaded Documents" &&
        documentsWithNewFilesForCheck.length > 0;
      if (changedFields.length === 0 && !hasDocumentFilesOnly) {
        setEditMessage({
          type: "warning",
          message: "No changes detected. Please make changes before saving.",
        });
        setEditMessageSection(editingSection);
        return;
      }

      if (editingSection === "Uploaded Documents") {
        // Handle file uploads
        const formData = new FormData();
        formData.append("section", "Uploaded Documents");

        // Create array to track which fields have files
        const fieldsWithFiles = [];

        console.log("=== FILE UPLOAD DEBUG ===");
        console.log("All documents:", memberData?.documents);
        console.log("Checking for newFile property...");

        // Check all documents for newFile
        memberData?.documents?.forEach((doc, idx) => {
          console.log(`Document ${idx}:`, {
            type: doc.type,
            hasNewFile: !!doc.newFile,
            newFile: doc.newFile,
            name: doc.name,
          });
        });

        // Find documents with new files
        const documentsWithNewFiles =
          memberData?.documents?.filter((doc) => {
            const hasFile = doc.newFile && doc.newFile instanceof File;
            console.log(`${doc.type}: hasFile=${hasFile}`, doc.newFile);
            return hasFile;
          }) || [];

        console.log(
          "Documents with new files count:",
          documentsWithNewFiles.length,
        );

        if (documentsWithNewFiles.length === 0) {
          setEditMessage({
            type: "warning",
            message:
              "No files selected for upload. Please select at least one file.",
          });
          return;
        }

        // Map document types to backend Multer field names
        const documentFieldMap = {
          "Internship Certificate": "internship_completion_certficate",
          "Degree Certificate": "provisional_certificate",
          "Provisional Certificate": "provisional_certificate",
          "ID Card": "Id_card",
          "Profile Photo": "file_url",
          "Color Passport Size Photo": "file_url",
          "Life Certificate": "lifeCertiticate",
          "IOTR Certificate": "iotr_certiticate",
          "Highest Qualification": "highest_qualification",
          // The following types currently don't have Multer fields on
          // /api/member-profile-edit/:userId/files and will be ignored
          "Character Certificate": null,
          "Aadhar Copy": null,
          "Membership Application": null,
          "College Leaving Certificate": null,
          Resume: null,
          "Pay Resume": null,
          "ACOT Certificate": null,
        };

        // Only send fields that Multer is configured to accept to avoid "Unexpected field"
        const allowedUploadFields = new Set([
          "internship_completion_certficate",
          "provisional_certificate",
          "Id_card",
          "id_card",
          "file_url",
          "profile_img",
          "lifeCertiticate",
          "iotr_certiticate",
          "mem_renewal_certificate_upload",
          "mem_renewal_profile_photo",
          "highest_qualification",
        ]);

        // Append files to FormData
        documentsWithNewFiles.forEach((doc) => {
          const fieldName = documentFieldMap[doc.type];
          if (fieldName && allowedUploadFields.has(fieldName) && doc.newFile) {
            console.log(`✓ Appending file: ${fieldName}`, {
              name: doc.newFile.name,
              type: doc.newFile.type,
              size: doc.newFile.size,
              isFile: doc.newFile instanceof File,
            });
            formData.append(fieldName, doc.newFile, doc.newFile.name);
            fieldsWithFiles.push(fieldName);
          } else if (fieldName && !allowedUploadFields.has(fieldName)) {
            console.warn(
              `✗ Skipping upload for document type "${doc.type}" because backend Multer is not configured for field "${fieldName}"`,
            );
          } else {
            console.warn(`✗ No field mapping for document type: ${doc.type}`);
          }
        });

        // Add changed fields to FormData
        formData.append("changedFields", JSON.stringify(fieldsWithFiles));

        console.log("Fields with files:", fieldsWithFiles);
        console.log("FormData entries:");

        // Log FormData contents
        for (let pair of formData.entries()) {
          if (pair[1] instanceof File) {
            console.log(
              `  ${pair[0]}: File(${pair[1].name}, ${pair[1].size} bytes)`,
            );
          } else {
            console.log(`  ${pair[0]}: ${pair[1]}`);
          }
        }

        const response = await fetch(
          `${API_BASE_URL}/api/member-profile-edit/${userInfo.details_id}/files`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              // Don't set Content-Type - let browser set it with boundary
            },
            body: formData,
          },
        );

        const result = await response.json();
        console.log("File upload response:", result);

        if (response.ok && fieldsWithFiles.length > 0) {
          setEditMessage({
            type: "success",
            message: "Successfully edited the field",
          });
          setEditMessageSection(editingSection);
          setIsEditing(false);
          setEditingSection("");
          setOriginalData(null);
          await fetchMemberData(userInfo, token);
        } else if (!response.ok) {
          setEditMessage({
            type: "danger",
            message: result.message || "Failed to update documents.",
          });
          setEditMessageSection(editingSection);
        }
      } else {
        // Handle JSON updates for other sections
        const updateData = {
          section: editingSection,
          changedFields: changedFields,
        };

        if (editingSection === "Personal Information") {
          if (
            nameError ||
            dobError ||
            certificationNameError ||
            adharPassportError
          ) {
            setEditMessage({
              type: "danger",
              message: "Please fix the validation errors before saving.",
            });
            setEditMessageSection("Personal Information");
            return;
          }
          // Name: letters, spaces, hyphens, apostrophes only
          const rawName = memberData?.name ?? memberData?.fullName ?? "";
          const nameVal = String(rawName).trim();
          const nameValid =
            /^[a-zA-Z\s\-']+$/.test(nameVal) && nameVal.length > 0;
          if (!nameValid && nameVal.length > 0) {
            setEditMessage({
              type: "danger",
              message:
                "Name can only contain letters, spaces, hyphens and apostrophes.",
            });
            setEditMessageSection("Personal Information");
            return;
          }
          if (nameVal.length === 0) {
            setEditMessage({
              type: "danger",
              message: "Name is required.",
            });
            setEditMessageSection("Personal Information");
            return;
          }
          // Certification Name: required, cannot be empty
          const certNameVal = String(
            memberData?.certificationName || "",
          ).trim();
          if (certNameVal.length === 0) {
            setEditMessage({
              type: "danger",
              message: "Certification Name is required and cannot be empty.",
            });
            setEditMessageSection("Personal Information");
            return;
          }
          // DOB: must be at least 16 years ago
          const dobVal = memberData?.dateOfBirth || "";
          if (dobVal) {
            const dobDate = new Date(dobVal);
            const today = new Date();
            const age = (today - dobDate) / (365.25 * 24 * 60 * 60 * 1000);
            if (age < 16) {
              setEditMessage({
                type: "danger",
                message:
                  "Date of Birth: Only members 16 years or older are allowed.",
              });
              setEditMessageSection("Personal Information");
              return;
            }
            if (dobDate > today) {
              setEditMessage({
                type: "danger",
                message: "Date of Birth cannot be in the future.",
              });
              setEditMessageSection("Personal Information");
              return;
            }
          }
          // Mobile: exactly 7 - 15 digits when provided
          const rawPhone =
            memberData?.mobileNumber ?? memberData?.phoneNumber ?? "";
          const phoneVal = String(rawPhone).trim().replace(/\D/g, "");
          if (
            phoneVal.length > 0 &&
            (phoneVal.length < 7 || phoneVal.length > 15)
          ) {
            setEditMessage({
              type: "danger",
              message: "Mobile number must be between 7 and 15 digits.",
            });
            setEditMessageSection("Personal Information");
            return;
          }
          if (mobileNumberError) {
            setEditMessage({
              type: "danger",
              message: "Please fix the mobile number error before saving.",
            });
            setEditMessageSection("Personal Information");
            return;
          }
          // Gender: required for Life Member - must select Male, Female, or Other
          if (userInfo?.rolename === "LifeMember") {
            const genderVal = (memberData?.gender || "").trim().toLowerCase();
            if (
              !genderVal ||
              !["male", "female", "other"].includes(genderVal)
            ) {
              setEditMessage({
                type: "danger",
                message:
                  "Please select a gender (Male, Female, or Other) to proceed.",
              });
              setEditMessageSection("Personal Information");
              return;
            }
          }

          // Only include fields that were actually changed
          const changedFieldsSet = new Set(changedFields);
          const hasField = (fieldNames) =>
            fieldNames.some(
              (f) =>
                changedFieldsSet.has(f) ||
                changedFields.some((cf) => cf.includes(f)),
            );

          if (hasField(["name", "fullName"])) {
            updateData.name = nameVal;
          }
          if (hasField(["certificationName", "nametobe_printed"])) {
            updateData.nametobe_printed = certNameVal;
            if (!certificationNameLocked && !isFullyApproved) {
              updateData.nametobe_printed = certNameVal;
            }
          }
          if (hasField(["gender"])) {
            updateData.gender = memberData?.gender || "";
          }
          if (hasField(["dateOfBirth", "dob"])) {
            updateData.dob =
              normalizeDateToYYYYMMDD(memberData?.dateOfBirth) ||
              memberData?.dateOfBirth ||
              "";
          }
          if (hasField(["mobileNumber", "phoneNumber", "phone"])) {
            updateData.phone =
              phoneVal.length === 10
                ? phoneVal
                : String(
                  memberData?.mobileNumber ?? memberData?.phoneNumber ?? "",
                )
                  .trim()
                  .replace(/\D/g, "");
          }
          if (hasField(["countryCode"])) {
            console.log("has fields countryCode");

            updateData.countryCode = memberData?.countryCode || "";
          }

          console.log(updateData);

          if (
            hasField(["aadhaarPassport", "aadhaar_passport", "adhar_passport"])
          ) {
            updateData.adhar_passport = memberData?.aadhaarPassport || "";
          }
        } else if (editingSection === "Address") {
          // Validate Address fields
          const permanentAddress = memberData?.permanentAddress || {};
          const correspondenceAddress = memberData?.correspondenceAddress || {};

          // Check if permanent address fields are being edited
          const permanentAddressChanged = changedFields.some(
            (f) =>
              f.startsWith("permanentAddress") ||
              f === "country" ||
              f === "state" ||
              f === "district",
          );

          // Check if correspondence address fields are being edited
          const correspondenceAddressChanged = changedFields.some(
            (f) =>
              f.startsWith("correspondenceAddress") ||
              f === "Co_country" ||
              f === "Co_state" ||
              f === "Co_district",
          );

          // Validate permanent address if it's being edited
          if (permanentAddressChanged) {
            if (
              !permanentAddress.country ||
              String(permanentAddress.country).trim() === ""
            ) {
              setEditMessage({
                type: "danger",
                message: "Please select a Country for Permanent Address.",
              });
              setEditMessageSection(editingSection);
              return;
            }
            if (
              !permanentAddress.state ||
              String(permanentAddress.state).trim() === ""
            ) {
              setEditMessage({
                type: "danger",
                message: "Please select a State for Permanent Address.",
              });
              setEditMessageSection(editingSection);
              return;
            }
            if (
              !permanentAddress.district ||
              String(permanentAddress.district).trim() === ""
            ) {
              setEditMessage({
                type: "danger",
                message: "Please select a District for Permanent Address.",
              });
              setEditMessageSection(editingSection);
              return;
            }
            const pin = (permanentAddress.pincode || "").replace(/\D/g, "");
            if (pin.length > 0 && pin.length !== 6) {
              setEditMessage({
                type: "danger",
                message: "Permanent Address pincode must be 6 digits.",
              });
              setEditMessageSection(editingSection);
              return;
            }
          }

          // Validate correspondence address if it's being edited and not same as permanent
          if (correspondenceAddressChanged && !sameAddress) {
            if (
              !correspondenceAddress.cocountry ||
              String(correspondenceAddress.cocountry).trim() === ""
            ) {
              setEditMessage({
                type: "danger",
                message: "Please select a Country for Correspondence Address.",
              });
              setEditMessageSection(editingSection);
              return;
            }
            if (
              !correspondenceAddress.costate ||
              String(correspondenceAddress.costate).trim() === ""
            ) {
              setEditMessage({
                type: "danger",
                message: "Please select a State for Correspondence Address.",
              });
              setEditMessageSection(editingSection);
              return;
            }
            if (
              !correspondenceAddress.codistrict ||
              String(correspondenceAddress.codistrict).trim() === ""
            ) {
              setEditMessage({
                type: "danger",
                message: "Please select a District for Correspondence Address.",
              });
              setEditMessageSection(editingSection);
              return;
            }
            const coPin = (correspondenceAddress.copincode || "").replace(
              /\D/g,
              "",
            );
            if (coPin.length > 0 && coPin.length !== 6) {
              setEditMessage({
                type: "danger",
                message: "Correspondence Address pincode must be 6 digits.",
              });
              setEditMessageSection(editingSection);
              return;
            }
          }

          // Only include fields that were actually changed
          const changedFieldsSet = new Set(changedFields);
          const hasField = (fieldNames) => {
            return fieldNames.some((f) => {
              if (changedFieldsSet.has(f)) return true;
              return changedFields.some((cf) => {
                const cfStr = String(cf);
                return (
                  cfStr === f ||
                  cfStr.includes(f) ||
                  cfStr.endsWith(`.${f}`) ||
                  cfStr.endsWith(`/${f}`)
                );
              });
            });
          };

          if (hasField(["permanentAddress.building", "flat_house_building"])) {
            updateData.flat_house_building = permanentAddress.building || "";
          }
          if (hasField(["permanentAddress.street", "colony_street_locality"])) {
            updateData.colony_street_locality = permanentAddress.street || "";
          }
          if (hasField(["permanentAddress.landmark", "landmark"])) {
            updateData.landmark = permanentAddress.landmark || "";
          }
          if (hasField(["permanentAddress.country", "country"])) {
            updateData.country = permanentAddress.country || "";
          }
          if (hasField(["permanentAddress.state", "state"])) {
            updateData.state = permanentAddress.state || "";
          }
          if (hasField(["permanentAddress.district", "district"])) {
            updateData.district = permanentAddress.district || "";
          }
          if (hasField(["permanentAddress.pincode", "pincode"])) {
            updateData.pincode = permanentAddress.pincode || "";
          }
          if (
            hasField([
              "correspondenceAddress.cobuilding",
              "co_flat_house_building",
            ])
          ) {
            updateData.co_flat_house_building =
              correspondenceAddress.cobuilding || "";
          }
          if (
            hasField([
              "correspondenceAddress.costreet",
              "Co_colony_street_locality",
            ])
          ) {
            updateData.Co_colony_street_locality =
              correspondenceAddress.costreet || "";
          }
          if (hasField(["correspondenceAddress.colandmark", "co_landmark"])) {
            updateData.co_landmark = correspondenceAddress.colandmark || "";
          }
          if (hasField(["correspondenceAddress.cocountry", "Co_country"])) {
            updateData.Co_country = correspondenceAddress.cocountry || "";
          }
          if (hasField(["correspondenceAddress.costate", "Co_state"])) {
            updateData.Co_state = correspondenceAddress.costate || "";
          }
          if (hasField(["correspondenceAddress.codistrict", "Co_district"])) {
            updateData.Co_district = correspondenceAddress.codistrict || "";
          }
          if (hasField(["correspondenceAddress.copincode", "Co_pincode"])) {
            updateData.Co_pincode = correspondenceAddress.copincode || "";
          }
        } else if (
          editingSection === "Academic Details" ||
          editingSection === "Academic Information"
        ) {
          updateData.section = "Academic Information";

          // Only include fields that were actually changed
          const changedFieldsSet = new Set(changedFields);
          const hasField = (fieldNames) => {
            return fieldNames.some((f) => {
              // Check exact match
              if (changedFieldsSet.has(f)) return true;
              // Check if any changed field contains this field name or ends with it
              return changedFields.some((cf) => {
                const cfStr = String(cf);
                return (
                  cfStr === f ||
                  cfStr.includes(f) ||
                  cfStr.endsWith(`.${f}`) ||
                  cfStr.endsWith(`/${f}`)
                );
              });
            });
          };

          if (
            hasField([
              "ugDetails.institution",
              "ug_institution",
              "ugDetails",
              "institution",
            ])
          ) {
            updateData.ug_institution =
              memberData?.ugDetails?.institution || "";
          }
          if (hasField(["ugDetails.degree", "ug_degree", "degree"])) {
            updateData.ug_degree = memberData?.ugDetails?.degree || "";
          }
          if (
            hasField(["ugDetails.university", "ug_university", "university"])
          ) {
            updateData.ug_university = memberData?.ugDetails?.university || "";
          }
          if (hasField(["ug_non_accredited_college_name"])) {
            updateData.ug_non_accredited_college_name =
              memberData?.ugDetails?.ug_non_accredited_college_name || "";
          }
          if (hasField(["pg_non_accredited_college_name"])) {
            updateData.pg_non_accredited_college_name =
              memberData?.pgDetails?.pg_non_accredited_college_name || "";
          }
          if (
            hasField([
              "ugDetails.completionDate",
              "ug_completion_date",
              "completionDate",
            ])
          ) {
            updateData.ug_completion_date =
              memberData?.ugDetails?.completionDate || "";
          }
          if (
            hasField([
              "pgDetails.pginstitution",
              "pg_institution",
              "pginstitution",
              "pgDetails",
            ])
          ) {
            updateData.pg_institution =
              memberData?.pgDetails?.pginstitution || "";
          }
          if (
            hasField([
              "pgDetails.abroadInstitution",
              "pg_institution_abroad",
              "abroadInstitution",
            ])
          ) {
            updateData.pg_institution_abroad =
              memberData?.pgDetails?.abroadInstitution || "";
          }
          if (hasField(["pgDetails.degree", "pg_degree"])) {
            updateData.pg_degree = memberData?.pgDetails?.degree || "";
          }
          if (
            hasField([
              "pgDetails.specialization",
              "pg_specialization",
              "specialization",
            ])
          ) {
            updateData.pg_specialization =
              memberData?.pgDetails?.specialization || "";
          }
          if (
            hasField([
              "pgDetails.otherSpecialization",
              "pg_other_specialization",
              "otherSpecialization",
            ])
          ) {
            updateData.pg_other_specialization =
              memberData?.pgDetails?.otherSpecialization || "";
          }
          if (hasField(["pgDetails.completionDate", "pg_completion_date"])) {
            updateData.pg_completion_date =
              memberData?.pgDetails?.completionDate || "";
          }
          if (
            hasField(["otherQualifications.qualification", "qualification"])
          ) {
            updateData.qualification =
              memberData?.otherQualifications?.qualification || "";
          }
          if (
            hasField([
              "otherQualifications.institution",
              "other_specialization",
            ])
          ) {
            updateData.other_specialization =
              memberData?.otherQualifications?.institution || "";
          }
          if (hasField(["otherQualifications.researchArea", "research_area"])) {
            updateData.research_area =
              memberData?.otherQualifications?.researchArea || "";
          }
          if (
            hasField([
              "otherQualifications.othercompletiondate",
              "other_completion_date",
            ])
          ) {
            updateData.other_completion_date =
              memberData?.otherQualifications?.othercompletiondate || "";
          }
          if (
            hasField([
              "otherQualifications.otherqualification",
              "other_qualification",
            ])
          ) {
            updateData.other_qualification =
              memberData?.otherQualifications?.otherqualification || "";
          }
        }
        console.log("Sending update data:", updateData);

        const response = await fetch(
          `${API_BASE_URL}/api/member-profile-edit/${userInfo.details_id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(updateData),
          },
        );

        const result = await response.json();

        console.log("Save response:", result);

        if (response.ok) {
          setEditMessage({
            type: "success",
            message: "Successfully edited the field",
          });
          setEditMessageSection(editingSection); // Set which section this message belongs to
          setIsEditing(false);
          setEditingSection("");
          setOriginalData(null);
          await fetchMemberData(userInfo, token);
        } else {
          setEditMessage({
            type: "danger",
            message: result.message || "Failed to save changes.",
          });
          setEditMessageSection(editingSection);
        }
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      setEditMessage({
        type: "danger",
        message: "Failed to save changes. Please try again.",
      });
      setEditMessageSection(editingSection);
    }
  };

  // New function to cancel editing
  const cancelEditing = () => {
    setIsEditing(false);
    setEditingSection("");
    setMemberData(originalData); // Restore original data
    setOriginalData(null);
    setEditMessage(null);
    setEditMessageSection(""); // Clear the message section
    setMobileNumberError("");
    setNameError("");
    setDobError("");
    setCertificationNameError("");
    // Reset sameAddress checkbox based on restored data
    if (originalData?.permanentAddress && originalData?.correspondenceAddress) {
      const permanent = originalData.permanentAddress;
      const correspondence = originalData.correspondenceAddress;
      const addressesMatch =
        permanent.building === correspondence.cobuilding &&
        permanent.street === correspondence.costreet &&
        permanent.landmark === correspondence.colandmark &&
        permanent.country === correspondence.cocountry &&
        permanent.state === correspondence.costate &&
        permanent.district === correspondence.codistrict &&
        permanent.pincode === correspondence.copincode;
      setSameAddress(addressesMatch);
    } else {
      setSameAddress(false);
    }
  };
  // Function to check if a field is editable (optionally lock individual fields that are still "pending" in edit request)
  const isFieldEditable = (section, backendFieldName) => {
    if (!isEditing || editingSection !== section) return false;
    if (!backendFieldName) return true;
    const status = getFieldStatusInSection(section, backendFieldName);
    return status !== "pending";
  };

  // Fetch Good Standing request history
  const fetchGoodStandingHistory = async () => {
    setLoadingRequests(true);
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        setGoodStandingRequests([]);
        return;
      }

      // Get user details_id from localStorage
      const userData = localStorage.getItem("user");
      if (!userData) {
        setGoodStandingRequests([]);
        return;
      }
      const user = JSON.parse(userData);
      const userDetailsId = user?.details_id;

      if (!userDetailsId) {
        console.error("User details_id not found");
        setGoodStandingRequests([]);
        return;
      }

      const url = `${API_BASE_URL}/goodstandingforms/paid`;
      console.log("Fetching paid Good Standing forms from:", url);
      console.log("Filtering by details_id:", userDetailsId);

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.success) {
        // Get all paid forms
        const allPaidForms = response.data.data || [];

        // Filter by details_id (handle both string and populated object)
        const filteredForms = allPaidForms.filter((form) => {
          const formDetailsId =
            typeof form.details_id === "object" && form.details_id !== null
              ? form.details_id._id || form.details_id
              : form.details_id;

          // Compare as strings to handle ObjectId comparison
          return String(formDetailsId) === String(userDetailsId);
        });

        console.log(
          `Found ${filteredForms.length} paid Good Standing forms for user`,
        );
        setGoodStandingRequests(
          Array.isArray(filteredForms) ? filteredForms : [],
        );
      } else {
        setGoodStandingRequests([]);
      }
    } catch (error) {
      // Handle different error scenarios
      if (error.response?.status === 400) {
        // Bad request
        console.error("Bad request:", error.response?.data?.message);
        setGoodStandingRequests([]);
      } else if (error.response?.status === 404) {
        // 404 might come from route not found or other middleware
        console.log("No records found or route not found");
        setGoodStandingRequests([]);
      } else if (error.response?.status === 500) {
        // Server error
        console.error(
          "Server error fetching Good Standing history:",
          error.response?.data?.message,
        );
        setGoodStandingRequests([]);
      } else {
        console.error("Error fetching Good Standing history:", error);
        setGoodStandingRequests([]);
      }
    } finally {
      setLoadingRequests(false);
    }
  };

  // Fetch License Verification request history
  const fetchLicenseVerificationHistory = async () => {
    setLoadingRequests(true);
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        setLicenseVerificationRequests([]);
        return;
      }

      // Get user details_id from localStorage
      const userData = localStorage.getItem("user");
      if (!userData) {
        setLicenseVerificationRequests([]);
        return;
      }
      const user = JSON.parse(userData);
      const userDetailsId = user?.details_id;

      if (!userDetailsId) {
        console.error("User details_id not found");
        setLicenseVerificationRequests([]);
        return;
      }

      const url = `${API_BASE_URL}/licenseverifications/paid`;
      console.log("Fetching paid License Verification forms from:", url);
      console.log("Filtering by details_id:", userDetailsId);

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.success) {
        // Get all paid verifications
        const allPaidVerifications = response.data.data || [];

        // Filter by details_id (handle both string and populated object)
        const filteredVerifications = allPaidVerifications.filter(
          (verification) => {
            const verificationDetailsId =
              typeof verification.details_id === "object" &&
                verification.details_id !== null
                ? verification.details_id._id || verification.details_id
                : verification.details_id;

            // Compare as strings to handle ObjectId comparison
            return String(verificationDetailsId) === String(userDetailsId);
          },
        );

        console.log(
          `Found ${filteredVerifications.length} paid License Verification forms for user`,
        );
        setLicenseVerificationRequests(
          Array.isArray(filteredVerifications) ? filteredVerifications : [],
        );
      } else {
        setLicenseVerificationRequests([]);
      }
    } catch (error) {
      // Handle different error scenarios
      if (error.response?.status === 400) {
        // Bad request
        console.error("Bad request:", error.response?.data?.message);
        setLicenseVerificationRequests([]);
      } else if (error.response?.status === 404) {
        // 404 might come from route not found or other middleware
        console.log("No records found or route not found");
        setLicenseVerificationRequests([]);
      } else if (error.response?.status === 500) {
        // Server error
        console.error(
          "Server error fetching License Verification history:",
          error.response?.data?.message,
        );
        setLicenseVerificationRequests([]);
      } else {
        console.error("Error fetching License Verification history:", error);
        setLicenseVerificationRequests([]);
      }
    } finally {
      setLoadingRequests(false);
    }
  };

  // Handler for same address checkbox
  const handleSameAddressChange = (e) => {
    const checked = e.target.checked;
    setSameAddress(checked);

    if (checked) {
      // Copy permanent address to correspondence address
      setMemberData((prev) => ({
        ...prev,
        correspondenceAddress: {
          cobuilding: prev?.permanentAddress?.building || "",
          costreet: prev?.permanentAddress?.street || "",
          colandmark: prev?.permanentAddress?.landmark || "",
          cocountry: prev?.permanentAddress?.country || "",
          costate: prev?.permanentAddress?.state || "",
          codistrict: prev?.permanentAddress?.district || "",
          copincode: prev?.permanentAddress?.pincode || "",
        },
      }));
    } else {
      // Clear correspondence address when unchecked
      setMemberData((prev) => ({
        ...prev,
        correspondenceAddress: {
          cobuilding: "",
          costreet: "",
          colandmark: "",
          cocountry: "",
          costate: "",
          codistrict: "",
          copincode: "",
        },
      }));
    }
  };

  // Initialize sameAddress state when memberData changes or when editing starts
  useEffect(() => {
    if (memberData?.permanentAddress && memberData?.correspondenceAddress) {
      const permanent = memberData.permanentAddress;
      const correspondence = memberData.correspondenceAddress;

      // Check if addresses match
      const addressesMatch =
        permanent.building === correspondence.cobuilding &&
        permanent.street === correspondence.costreet &&
        permanent.landmark === correspondence.colandmark &&
        permanent.country === correspondence.cocountry &&
        permanent.state === correspondence.costate &&
        permanent.district === correspondence.codistrict &&
        permanent.pincode === correspondence.copincode;

      setSameAddress(addressesMatch);
    }
  }, [memberData?.permanentAddress, memberData?.correspondenceAddress]);
  // Updated handleEditConfirm function
  const handleEditConfirm = async () => {
    await submitEditRequest();
  };
  const handleEditCancel = () => {
    setShowEditModal(false);
    setSelectedSection("");
  };
  const allMenuItems = [
    { name: "Approval Flow", key: "approval-flow" },
    { name: "Personal Information", key: "personal-info" },
    { name: "Address", key: "address" },
    { name: "Academic Details", key: "academic-details" },
    { name: "Uploaded Documents", key: "uploaded-docs" },
    { name: "Receipts", key: "receipts" },
    { name: "Certificate", key: "certificate", requiresApproval: true },
    // { name: "Good Standing", key: "good-standing", requiresApproval: true },
    // { name: "License Verification", key: "license-verification", requiresApproval: true },
    { name: "Renewal", key: "renewal", requiresApproval: true },
    { name: "Change Password", key: "change-password" },
    {
      name: "Professional status & License Verification",
      key: "good-standing-license",
      requiresApproval: true,
    },
    { name: "Sign Out", key: "sign-out", isSignOut: true },
  ];
  const getMenuItems = (rolename) => {
    // Check if all three approvals are complete
    const allApprovalsComplete =
      memberData?.secretaryApprovals === "approved" &&
      memberData?.treasurerApprovals === "approved" &&
      memberData?.presidentApprovals === "approved";

    // Filter items based on role
    const filteredItems = allMenuItems.filter((item) => {
      // For AssociateMember, only show specific items
      if (rolename === "AssociateMember") {
        return [
          "Approval Flow",
          "Personal Information",
          "Uploaded Documents",
          "Receipts",
          "Certificate",
          "Renewal",
          "Change Password",
          "Sign Out",
        ].includes(item.name);
      }
      // For LifeMember and other roles
      return true;
    });

    // Remove "Approval Flow" if all three approvals are complete
    if (allApprovalsComplete) {
      return filteredItems.filter((item) => item.name !== "Approval Flow");
    }

    // If not all approved, show all items including "Approval Flow"
    return filteredItems;
  };
  // Helper function to check if all membership approvals are completed
  const areAllMembershipApprovalsComplete = () => {
    if (!memberData) return false;

    return (
      memberData.secretaryApprovals === "approved" &&
      memberData.treasurerApprovals === "approved" &&
      memberData.presidentApprovals === "approved" &&
      memberData.certificateApproval === "approved"
    );
  };

  const getRejectionReasonsForSection = (sectionName) => {
    if (
      !memberData?.editRejections ||
      !Array.isArray(memberData.editRejections)
    ) {
      return null;
    }

    // Map section names to match database format
    const sectionNameMap = {
      "Personal Information": "Personal Information",
      Address: "Address",
      "Academic Details": "Academic Details",
      "Academic Information": "Academic Details", // Handle alternate name
      "Uploaded Documents": "Uploaded Documents",
    };

    const mappedSectionName = sectionNameMap[sectionName] || sectionName;

    // Find rejections for this section - try both mapped name and original name
    const sectionRejections = memberData.editRejections.filter((rejection) => {
      const rejectionSection = rejection.section || "";
      return (
        rejectionSection === mappedSectionName ||
        rejectionSection === sectionName ||
        (sectionName === "Academic Details" &&
          rejectionSection === "Academic Information") ||
        (sectionName === "Academic Information" &&
          rejectionSection === "Academic Details")
      );
    });

    if (sectionRejections.length === 0) return null;

    // Get the most recent rejection
    const latestRejection = sectionRejections.sort(
      (a, b) => new Date(b.rejectedAt) - new Date(a.rejectedAt),
    )[0];

    return latestRejection;
  };

  // Helper function to get approval reasons for a section from editApprovals
  const getApprovalReasonsForSection = (sectionName) => {
    if (
      !memberData?.editApprovals ||
      !Array.isArray(memberData.editApprovals)
    ) {
      return null;
    }

    // Map section names to match database format
    const sectionNameMap = {
      "Personal Information": "Personal Information",
      Address: "Address",
      "Academic Details": "Academic Information", // Map to API format
      "Academic Information": "Academic Information",
      "Uploaded Documents": "Uploaded Documents",
    };

    const mappedSectionName = sectionNameMap[sectionName] || sectionName;

    // Find approvals for this section - try both mapped name and original name
    const sectionApprovals = memberData.editApprovals.filter((approval) => {
      const approvalSection = approval.section || "";
      return (
        approvalSection === mappedSectionName ||
        approvalSection === sectionName ||
        (sectionName === "Academic Details" &&
          approvalSection === "Academic Information") ||
        (sectionName === "Academic Information" &&
          approvalSection === "Academic Details")
      );
    });

    if (sectionApprovals.length === 0) return null;

    // Get the most recent approval
    const latestApproval = sectionApprovals.sort(
      (a, b) => new Date(b.approvedAt) - new Date(a.approvedAt),
    )[0];

    return latestApproval;
  };

  // Helper function to get all edit statuses from useredited for a section
  const getAllEditStatusesForSection = (sectionName) => {
    if (!memberData?.useredited || !Array.isArray(memberData.useredited)) {
      return [];
    }

    // Map section names to match database format
    // The API returns "Academic Information" but our UI uses "Academic Details"
    const sectionNameMap = {
      "Personal Information": "Personal Information",
      Address: "Address",
      "Academic Details": "Academic Information", // Map to API format
      "Academic Information": "Academic Information",
      "Uploaded Documents": "Uploaded Documents",
    };

    const mappedSectionName = sectionNameMap[sectionName] || sectionName;

    // Find all edits for this section - check both the mapped name and original name
    const sectionEdits = memberData.useredited.filter(
      (edit) =>
        edit.section === mappedSectionName || edit.section === sectionName,
    );

    if (sectionEdits.length === 0) return [];

    // Sort by editedAt descending (newest first)
    return sectionEdits.sort(
      (a, b) => new Date(b.editedAt) - new Date(a.editedAt),
    );
  };

  // Helper function to get the latest edit status from useredited for a section (for backward compatibility)
  const getLatestEditStatusForSection = (sectionName) => {
    const allEdits = getAllEditStatusesForSection(sectionName);
    return allEdits.length > 0 ? allEdits[0] : null;
  };

  // Get per-field status (approved/rejected/pending) from latest edit's editedData for a section
  const getFieldStatusInSection = (sectionName, backendFieldName) => {
    const latestEdit = getLatestEditStatusForSection(sectionName);
    if (!latestEdit?.editedData || typeof latestEdit.editedData !== "object")
      return null;

    // Check for the field name, and also check alternative names for backward compatibility
    let fieldData = latestEdit.editedData[backendFieldName];

    // For adhar_passport, also check aadhaar_passport
    if (!fieldData && backendFieldName === "adhar_passport") {
      fieldData = latestEdit.editedData["aadhaar_passport"];
    }
    // For aadhaar_passport, also check adhar_passport
    if (!fieldData && backendFieldName === "aadhaar_passport") {
      fieldData = latestEdit.editedData["adhar_passport"];
    }

    if (!fieldData || typeof fieldData !== "object") return null;
    return (fieldData.status || "").toLowerCase() || null;
  };

  // True when the latest edit for this section has all fields in editedData approved or rejected (none pending)
  const hasAllFieldsProcessedForSection = (sectionName) => {
    const latestEdit = getLatestEditStatusForSection(sectionName);
    if (!latestEdit?.editedData || typeof latestEdit.editedData !== "object")
      return true;
    const values = Object.values(latestEdit.editedData);
    if (values.length === 0) return true;
    return values.every(
      (v) =>
        v &&
        ((v.status || "").toLowerCase() === "approved" ||
          (v.status || "").toLowerCase() === "rejected"),
    );
  };

  // List of { fieldName, status } for the latest edit in this section (for per-field approval display)
  const getFieldApprovalStatusList = (sectionName) => {
    const latest = getLatestEditStatusForSection(sectionName);
    if (!latest?.editedData || typeof latest.editedData !== "object") return [];
    return Object.entries(latest.editedData).map(([backendField, data]) => ({
      fieldName: getFieldDisplayName(
        backendField,
        latest.section || sectionName,
      ),
      status: (data?.status || "").toLowerCase(),
    }));
  };

  const handleTabClick = (tabName, requiresApproval) => {
    if (tabName === "Sign Out") {
      setShowSignOutModal(true);
      return;
    }

    if (tabName === "Professional status & License Verification") {
      if (!areAllMembershipApprovalsComplete()) {
        return;
      }
      // No modal here — tab opens directly
    }

    if (isEditing) {
      cancelEditing();
    }

    // Clear edit message when switching tabs
    setEditMessage(null);
    setEditMessageSection("");

    setActiveTab(tabName);
  };

  const handleProfessionalStatusConfirm = () => {
    setShowProfessionalStatusModal(false);
    navigate("/professional-status");
  };

  const handleProfessionalStatusCancel = () => {
    setShowProfessionalStatusModal(false);
    if (activeTab === "Professional status & License Verification") {
      const approvalsComplete = areAllMembershipApprovalsComplete();
      const defaultTab = approvalsComplete ? "Personal Information" : "Approval Flow";
      setActiveTab(defaultTab);
    }
  };

  const handleSignOutConfirm = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setShowSignOutModal(false);
    navigate("/member-login");
  };

  const handleSignOutCancel = () => {
    setShowSignOutModal(false);
  };

  const normalizeDateToYYYYMMDD = (date) => {
    if (!date) return "";
    const str = String(date).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const isoMatch = str.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const formatDateForInput = (date) => {
    return normalizeDateToYYYYMMDD(date);
  };

  const isNewLicenseVerificationApplicant = () => {
    const cutoffDate = "2026-04-30";
    const createdAt = normalizeDateToYYYYMMDD(memberData?.createdAt);
    const oldCertificateIssuedDate = normalizeDateToYYYYMMDD(
      memberData?.old_certificate_issued_date,
    );

    return [createdAt, oldCertificateIssuedDate].some(
      (date) => date && date >= cutoffDate,
    );
  };

  useEffect(() => {
    if (memberData) {
      const fullyApproved =
        memberData.secretaryApprovals === "approved" &&
        memberData.treasurerApprovals === "approved" &&
        memberData.presidentApprovals === "approved";
      setIsFullyApproved(fullyApproved);
    }
  }, [memberData]);

  // Profile edit restriction logic:
  // Allowed if:
  // 1. Secretary rejected the registration OR
  // 2. Registration is fully approved (including certificate generated)
  const isProfileEditRestricted =
    (userInfo?.rolename === "LifeMember" ||
      userInfo?.rolename === "AssociateMember") &&
    memberData?.secretaryApprovals !== "rejected" &&
    !areAllMembershipApprovalsComplete();

  const handleGenerateReceiptPDF = async () => {
    try {
      console.log("Starting receipt PDF generation...");
      console.log("Member Data:", memberData);

      // Check if the element exists
      const element = document.getElementById("receipt-pdf-content");
      if (!element) {
        console.error("Receipt PDF content element not found");
        alert("PDF content not found. Please refresh the page and try again.");
        return;
      }

      console.log("Receipt PDF content element found:", element);
      console.log("Element content length:", element.innerHTML.length);

      // Generate dynamic filename
      const receiptData = getLatestReceiptData();
      const membershipType =
        receiptData?.membershipType === "AssociateMember"
          ? "associate"
          : "life";
      const paymentType =
        receiptData?.type === "renewal" ? "renewal" : "registration";
      const filename = `Payment_Receipt.pdf`;

      await generateReceiptPDF(memberData, filename);
    } catch (error) {
      console.error("Error generating receipt PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
  };

  const handleOpenReceiptPDF = async () => {
    try {
      console.log("Starting receipt PDF view...");
      console.log("Member Data:", memberData);

      // Check if the element exists
      const element = document.getElementById("receipt-pdf-content");
      if (!element) {
        console.error("Receipt PDF content element not found");
        alert("PDF content not found. Please refresh the page and try again.");
        return;
      }

      console.log("Receipt PDF content element found:", element);
      console.log("Element content length:", element.innerHTML.length);

      // Generate dynamic filename
      const receiptData = getLatestReceiptData();
      const membershipType =
        receiptData?.membershipType === "AssociateMember"
          ? "associate"
          : "life";
      const paymentType =
        receiptData?.type === "renewal" ? "renewal" : "registration";
      const filename = `${membershipType}${paymentType}.pdf`;

      await openReceiptPDF(memberData, filename);
    } catch (error) {
      console.error("Error opening receipt PDF:", error);
      alert("Error opening PDF. Please try again.");
    }
  };

  // Helper function to get the latest receipt data
  const getLatestReceiptData = () => {
    if (!memberData) return null;

    // Debug logging to understand the data structure
    console.log("MemberData for receipt:", {
      renewal_payment_status: memberData.renewal_payment_status,
      payment_status: memberData.payment_status,
      amount_IN: memberData.amount_IN,
      amount_IN_renewal: memberData.amount_IN_renewal,
      valid_till: memberData.valid_till,
      renewalValidTill: memberData.renewalValidTill,
      membershipType: userInfo?.rolename,
    });

    // Check if there's renewal data (renewal takes precedence over registration)
    if (memberData.renewal_payment_status === "paid") {
      console.log("Found renewal payment data");
      return {
        type: "renewal",
        paymentDetails: {
          razorpay_payment_id:
            memberData.renewalPaymentDetails?.razorpay_payment_id ||
            "RENEWAL-" + Date.now(),
          payment_date:
            memberData.renewalPaymentDate || new Date().toISOString(),
        },
        paymentDate: memberData.renewalPaymentDate || new Date(),
        amount: memberData.amount_IN_renewal || 0,
        validTill: memberData.renewalValidTill || memberData.valid_till,
        membershipType: userInfo?.rolename || "LifeMember",
      };
    }

    // Check if there's initial registration payment (first payment)
    if (memberData.payment_status === "paid") {
      console.log("Found registration payment data");
      return {
        type: "registration",
        paymentDetails: {
          razorpay_payment_id:
            memberData.paymentDetails?.razorpay_payment_id ||
            "REG-" + Date.now(),
          payment_date: memberData.paymentDate || new Date().toISOString(),
        },
        paymentDate: memberData.paymentDate || new Date(),
        amount: memberData.amount_IN || 0,
        validTill: memberData.valid_till,
        membershipType: userInfo?.rolename || "LifeMember",
      };
    }

    console.log("No payment data found");
    return null;
  };
  // Map backend field names to frontend display names
  const getFieldDisplayName = (backendField, section) => {
    const fieldMappings = {
      "Personal Information": {
        name: "Name",
        nametobe_printed: "Certification Name",
        gender: "Gender",
        dob: "Date Of Birth",
        phone: "Mobile Number",
        aadhaar_passport: "Aadhaar/Passport",
        adhar_passport: "Aadhaar/Passport",
        email: "E-mail Address",
      },
      Address: {
        flat_house_building: "Flat/House NO/Floor/Building",
        colony_street_locality: "Colony/Street/Locality",
        landmark: "Landmark",
        country: "Country",
        state: "State",
        district: "District",
        pincode: "Pincode",
        co_flat_house_building: "Correspondence Flat/House NO/Floor/Building",
        Co_colony_street_locality: "Correspondence Colony/Street/Locality",
        co_landmark: "Correspondence Landmark",
        Co_country: "Correspondence Country",
        Co_state: "Correspondence State",
        Co_district: "Correspondence District",
        Co_pincode: "Correspondence Pincode",
      },
      "Academic Details": {
        ug_institution: "UG Name of Institution",
        other_institution: "UG Name of Institution",
        ug_degree: "UG Name of Degree",
        ug_university: "UG Name of University",
        ug_completion_date: "UG Date Of Completion",
        pg_institution: "PG Name of Institution",
        pg_institution_abroad: "PG Name and place of institution at abroad",
        pg_degree: "PG Name of the Degree",
        pg_specialization: "PG Specialization",
        pg_other_specialization: "PG Other Specialization",
        pg_completion_date: "PG Date Of Completion",
        qualification: "Other Qualifications - Qualification",
        other_specialization: "Other Qualifications - Name of Institution",
        research_area: "Other Qualifications - Research Area",
        other_completion_date: "Other Qualifications - Date Of Completion",
        other_qualification: "Other Qualifications - Other Qualification",
      },
      "Academic Information": {
        ug_institution: "UG Name of Institution",
        other_institution: "UG Name of Institution",
        ug_degree: "UG Name of Degree",
        ug_university: "UG Name of University",
        ug_completion_date: "UG Date Of Completion",
        pg_institution: "PG Name of Institution",
        pg_institution_abroad: "PG Name and place of institution at abroad",
        pg_degree: "PG Name of the Degree",
        pg_specialization: "PG Specialization",
        pg_other_specialization: "PG Other Specialization",
        pg_completion_date: "PG Date Of Completion",
        qualification: "Other Qualifications - Qualification",
        other_specialization: "Other Qualifications - Name of Institution",
        research_area: "Other Qualifications - Research Area",
        other_completion_date: "Other Qualifications - Date Of Completion",
        other_qualification: "Other Qualifications - Other Qualification",
      },
      "Uploaded Documents": {
        internship_completion_certficate: "Internship Certificate",
        provisional_certificate: "Provisional Certificate",
        character_certificate: "Character Certificate",
        Id_card: "ID Card",
        file_url: "Color Passport Size Photo",
        lifeCertiticate: "Life Certificate",
        iotr_certiticate: "IOTR Certificate",
        Aadhar_copy: "Aadhar Copy",
        membership_application: "Membership Application",
        college_leaving_certificate: "College Leaving Certificate",
        resume: "Resume",
        pay_resume: "Pay Resume",
        Acot_certiticate: "ACOT Certificate",
      },
    };

    // Get the section mapping (handle both "Academic Details" and "Academic Information")
    const sectionKey =
      section === "Academic Information" ? "Academic Details" : section;
    const mapping = fieldMappings[sectionKey] || {};

    // Return mapped name or fallback to formatted version
    return (
      mapping[backendField] ||
      backendField
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  };

  const RejectionAlert = ({ sectionName }) => {
    const rejection = getRejectionReasonsForSection(sectionName);

    if (!rejection) return null;

    // Extract field names from rejectedFields array - handle both object and string formats
    const getRejectedFieldNames = () => {
      if (
        !rejection.rejectedFields ||
        !Array.isArray(rejection.rejectedFields)
      ) {
        return [];
      }
      return rejection.rejectedFields.map((f) => {
        // Handle both object format {field: "fieldName", reason: "...", ...} and string format
        const fieldName =
          typeof f === "object" && f !== null ? f.field || f : f;
        return getFieldDisplayName(fieldName, rejection.section || sectionName);
      });
    };

    const rejectedFieldNames = getRejectedFieldNames();

    return (
      <Alert variant="danger" className="mb-4">
        <div className="d-flex align-items-start" style={{ gap: "0.75rem" }}>
          <div className="flex-shrink-0" style={{ marginTop: "0.125rem" }}>
            <AlertTriangle size={20} className="flex-shrink-0" />
          </div>
          <div className="flex-grow-1">
            <Alert.Heading
              className="mb-2"
              style={{ fontSize: "1rem", fontWeight: 600, lineHeight: 1.4 }}
            >
              <strong>Section:</strong> {rejection.section}
              <br />
              Edit Request: <strong>Rejected</strong>
            </Alert.Heading>
            {rejectedFieldNames.length > 0 && (
              <div className="mb-2" style={{ lineHeight: 1.5 }}>
                <div
                  className="d-flex justify-content-between align-items-center mb-2"
                  style={{ fontSize: "0.9375rem" }}
                >
                  <strong>Rejected Fields:</strong>
                  <strong style={{ minWidth: "200px", textAlign: "right" }}>
                    Reason
                  </strong>
                </div>
                <ul
                  className="mb-0 mt-1"
                  style={{
                    listStyle: "none",
                    paddingLeft: 0,
                    fontSize: "0.9375rem",
                    lineHeight: 1.5,
                  }}
                >
                  {rejection.rejectedFields.map((f, idx) => {
                    const fieldName =
                      typeof f === "object" && f !== null ? f.field || f : f;
                    const displayName = getFieldDisplayName(
                      fieldName,
                      rejection.section || sectionName,
                    );
                    const fieldReason =
                      typeof f === "object" && f !== null ? f.reason : null;
                    return (
                      <li
                        key={idx}
                        className="d-flex justify-content-between align-items-start mb-1 gap-2"
                        style={{ flexWrap: "wrap", marginBottom: "0.25rem" }}
                      >
                        <span style={{ flex: "0 1 auto", minWidth: "120px" }}>
                          {displayName}
                        </span>
                        {fieldReason && (
                          <span
                            className="text-muted"
                            style={{
                              textAlign: "right",
                              minWidth: "200px",
                              flex: "1 1 200px",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            {fieldReason}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <p
              className="mb-0 text-muted"
              style={{ fontSize: "0.875rem", lineHeight: 1.4 }}
            >
              <strong>Rejected on:</strong>{" "}
              {new Date(rejection.rejectedAt).toLocaleString("en-IN", {
                day: "2-digit",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
        </div>
      </Alert>
    );
  };

  // Component to display approved fields from editApprovals in a green success box
  const ApprovalAlert = ({ sectionName }) => {
    const approval = getApprovalReasonsForSection(sectionName);

    if (
      !approval ||
      !approval.approvedFields ||
      !Array.isArray(approval.approvedFields) ||
      approval.approvedFields.length === 0
    )
      return null;

    return (
      <Alert variant="success" className="mb-4">
        <div className="d-flex align-items-start" style={{ gap: "0.75rem" }}>
          <div className="flex-shrink-0" style={{ marginTop: "0.125rem" }}>
            <i className="fas fa-check-circle" style={{ fontSize: "1rem" }}></i>
          </div>
          <div className="flex-grow-1">
            <Alert.Heading
              className="mb-2"
              style={{ fontSize: "1rem", fontWeight: 600, lineHeight: 1.4 }}
            >
              <strong>Section:</strong> {approval.section || sectionName}
              <br />
              Edit Request: <strong>Approved</strong>
            </Alert.Heading>
            <div className="mb-2" style={{ lineHeight: 1.5 }}>
              <strong style={{ fontSize: "0.9375rem" }}>
                Approved Fields:
              </strong>
              <ul
                className="mb-0 mt-1 ps-3"
                style={{ lineHeight: 1.5, fontSize: "0.9375rem" }}
              >
                {approval.approvedFields.map((field, idx) => {
                  const displayName = getFieldDisplayName(
                    field.field,
                    approval.section || sectionName,
                  );
                  return (
                    <li key={idx} style={{ marginBottom: "0.25rem" }}>
                      {displayName}
                      {field.oldValue && field.newValue && (
                        <span className="text-muted ms-2">
                          ({field.oldValue} → {field.newValue})
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            <p
              className="mb-0 text-muted"
              style={{ fontSize: "0.875rem", lineHeight: 1.4 }}
            >
              <strong>Approved on:</strong>{" "}
              {new Date(approval.approvedAt).toLocaleString("en-IN", {
                day: "2-digit",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
              {approval.approvedByRole && (
                <span className="ms-2">
                  by <strong>{approval.approvedByRole}</strong>
                </span>
              )}
            </p>
          </div>
        </div>
      </Alert>
    );
  };

  // Component to display all edit statuses from useredited
  // Only shows pending and approved - rejected edits are shown separately via RejectionAlert
  const EditStatusAlert = ({ sectionName }) => {
    const allEdits = getAllEditStatusesForSection(sectionName);

    if (!allEdits || allEdits.length === 0) return null;

    // Filter out rejected edits - they will be shown by RejectionAlert component
    const nonRejectedEdits = allEdits.filter(
      (edit) => edit.status !== "rejected",
    );

    if (nonRejectedEdits.length === 0) return null;

    // Sort by editedAt ascending (oldest first) so first raised = #1, latest = last
    const sortedEdits = [...nonRejectedEdits].sort(
      (a, b) => new Date(a.editedAt) - new Date(b.editedAt),
    );

    const latestEdit = sortedEdits[sortedEdits.length - 1];

    const getVariant = (status) => {
      switch (status) {
        case "approved":
          return "success";
        case "rejected":
          return "danger";
        case "pending":
          return "warning";
        default:
          return "info";
      }
    };

    const getStatusIcon = (status) => {
      switch (status) {
        case "approved":
          return (
            <i className="fas fa-check-circle" style={{ fontSize: "1rem" }}></i>
          );
        case "rejected":
          return <AlertTriangle size={20} className="flex-shrink-0" />;
        case "pending":
          return <Clock size={20} />;
        default:
          return (
            <i className="fas fa-info-circle" style={{ fontSize: "1rem" }}></i>
          );
      }
    };

    const getStatusText = (status) => {
      switch (status) {
        case "approved":
          return "Approved";
        case "rejected":
          return "Rejected";
        case "pending":
          return "Pending";
        default:
          return status;
      }
    };

    // Extract changed/rejected fields from changedFields array or editedData object
    // Group fields by area (UG, PG, Other Qualifications, Address, etc.)
    const getChangedFields = (edit) => {
      const editSection = edit.section || sectionName;

      // Field grouping for Academic Details
      const academicFieldGroups = {
        ug: [
          "ug_institution",
          "other_institution",
          "ug_degree",
          "ug_university",
          "ug_completion_date",
        ],
        pg: [
          "pg_institution",
          "pg_institution_abroad",
          "pg_degree",
          "pg_specialization",
          "pg_other_specialization",
          "pg_completion_date",
        ],
        other: [
          "qualification",
          "other_specialization",
          "research_area",
          "other_completion_date",
          "other_qualification",
        ],
      };

      // Field grouping for Address
      const addressFieldGroups = {
        permanent: [
          "flat_house_building",
          "colony_street_locality",
          "landmark",
          "country",
          "state",
          "district",
          "pincode",
        ],
        correspondence: [
          "co_flat_house_building",
          "Co_colony_street_locality",
          "co_landmark",
          "Co_country",
          "Co_state",
          "Co_district",
          "Co_pincode",
        ],
      };

      // First try changedFields array
      if (
        edit.changedFields &&
        Array.isArray(edit.changedFields) &&
        edit.changedFields.length > 0
      ) {
        // If changedFields contains objects with 'field' property, extract them
        const fields = edit.changedFields.map((field) => {
          const fieldName =
            typeof field === "string" ? field : field.field || field;
          return fieldName;
        });
        // Remove duplicates
        const uniqueFields = [...new Set(fields)];

        // Group fields if it's Academic Details section
        if (
          editSection === "Academic Information" ||
          editSection === "Academic Details"
        ) {
          const grouped = {
            ug: [],
            pg: [],
            other: [],
            otherFields: [],
          };

          const ugShort = {
            ug_institution: "Name of Institution",
            other_institution: "Name of Institution",
            ug_degree: "Name of Degree",
            ug_university: "Name of University",
            ug_completion_date: "Date of Completion",
          };
          const pgShort = {
            pg_institution: "Name of Institution",
            pg_institution_abroad: "Institution Abroad",
            pg_degree: "Name of Degree",
            pg_specialization: "Specialization",
            pg_other_specialization: "Other Specialization",
            pg_completion_date: "Date of Completion",
          };
          const otherShort = {
            qualification: "Qualification",
            other_specialization: "Name of Institution",
            research_area: "Research Area",
            other_completion_date: "Date of Completion",
            other_qualification: "Other Qualification",
          };
          uniqueFields.forEach((field) => {
            if (academicFieldGroups.ug.includes(field)) {
              grouped.ug.push(
                ugShort[field] || getFieldDisplayName(field, editSection),
              );
            } else if (academicFieldGroups.pg.includes(field)) {
              grouped.pg.push(
                pgShort[field] || getFieldDisplayName(field, editSection),
              );
            } else if (academicFieldGroups.other.includes(field)) {
              grouped.other.push(
                otherShort[field] || getFieldDisplayName(field, editSection),
              );
            } else {
              grouped.otherFields.push(getFieldDisplayName(field, editSection));
            }
          });
          const result = [];
          if (grouped.ug.length > 0)
            result.push(`UG Details: ${grouped.ug.join(", ")}`);
          if (grouped.pg.length > 0)
            result.push(`PG Details: ${grouped.pg.join(", ")}`);
          if (grouped.other.length > 0)
            result.push(`Other Qualifications: ${grouped.other.join(", ")}`);
          if (grouped.otherFields.length > 0)
            result.push(...grouped.otherFields);
          return result;
        }

        // Group fields if it's Address section (short labels under Correspondence to avoid repetition)
        if (editSection === "Address") {
          const correspondenceShortNames = {
            co_flat_house_building: "Flat/House/Building",
            Co_colony_street_locality: "Colony/Street/Locality",
            co_landmark: "Landmark",
            Co_country: "Country",
            Co_state: "State",
            Co_district: "District",
            Co_pincode: "Pincode",
          };
          const grouped = {
            permanent: [],
            correspondence: [],
            otherFields: [],
          };
          uniqueFields.forEach((field) => {
            if (addressFieldGroups.permanent.includes(field)) {
              grouped.permanent.push(getFieldDisplayName(field, editSection));
            } else if (addressFieldGroups.correspondence.includes(field)) {
              grouped.correspondence.push(
                correspondenceShortNames[field] ||
                getFieldDisplayName(field, editSection),
              );
            } else {
              grouped.otherFields.push(getFieldDisplayName(field, editSection));
            }
          });
          const result = [];
          if (grouped.permanent.length > 0)
            result.push(`Permanent Address: ${grouped.permanent.join(", ")}`);
          if (grouped.correspondence.length > 0)
            result.push(
              `Correspondence Address: ${grouped.correspondence.join(", ")}`,
            );
          if (grouped.otherFields.length > 0)
            result.push(...grouped.otherFields);
          return result;
        }

        // For other sections, return as is
        return uniqueFields.map((field) =>
          getFieldDisplayName(field, editSection),
        );
      }

      // If changedFields is empty, extract field names from editedData
      if (edit.editedData && typeof edit.editedData === "object") {
        const fields = Object.keys(edit.editedData);
        const uniqueFields = [...new Set(fields)];

        // Group fields if it's Academic Details section
        if (
          editSection === "Academic Information" ||
          editSection === "Academic Details"
        ) {
          const grouped = {
            ug: [],
            pg: [],
            other: [],
            otherFields: [],
          };

          const ugShort = {
            ug_institution: "Name of Institution",
            other_institution: "Name of Institution",
            ug_degree: "Name of Degree",
            ug_university: "Name of University",
            ug_completion_date: "Date of Completion",
          };
          const pgShort = {
            pg_institution: "Name of Institution",
            pg_institution_abroad: "Institution Abroad",
            pg_degree: "Name of Degree",
            pg_specialization: "Specialization",
            pg_other_specialization: "Other Specialization",
            pg_completion_date: "Date of Completion",
          };
          const otherShort = {
            qualification: "Qualification",
            other_specialization: "Name of Institution",
            research_area: "Research Area",
            other_completion_date: "Date of Completion",
            other_qualification: "Other Qualification",
          };
          uniqueFields.forEach((field) => {
            if (academicFieldGroups.ug.includes(field))
              grouped.ug.push(
                ugShort[field] || getFieldDisplayName(field, editSection),
              );
            else if (academicFieldGroups.pg.includes(field))
              grouped.pg.push(
                pgShort[field] || getFieldDisplayName(field, editSection),
              );
            else if (academicFieldGroups.other.includes(field))
              grouped.other.push(
                otherShort[field] || getFieldDisplayName(field, editSection),
              );
            else
              grouped.otherFields.push(getFieldDisplayName(field, editSection));
          });
          const result = [];
          if (grouped.ug.length > 0)
            result.push(`UG Details: ${grouped.ug.join(", ")}`);
          if (grouped.pg.length > 0)
            result.push(`PG Details: ${grouped.pg.join(", ")}`);
          if (grouped.other.length > 0)
            result.push(`Other Qualifications: ${grouped.other.join(", ")}`);
          if (grouped.otherFields.length > 0)
            result.push(...grouped.otherFields);
          return result;
        }

        // Group fields if it's Address section (short labels for Correspondence)
        if (editSection === "Address") {
          const correspondenceShortNames = {
            co_flat_house_building: "Flat/House/Building",
            Co_colony_street_locality: "Colony/Street/Locality",
            co_landmark: "Landmark",
            Co_country: "Country",
            Co_state: "State",
            Co_district: "District",
            Co_pincode: "Pincode",
          };
          const grouped = {
            permanent: [],
            correspondence: [],
            otherFields: [],
          };
          uniqueFields.forEach((field) => {
            if (addressFieldGroups.permanent.includes(field))
              grouped.permanent.push(getFieldDisplayName(field, editSection));
            else if (addressFieldGroups.correspondence.includes(field))
              grouped.correspondence.push(
                correspondenceShortNames[field] ||
                getFieldDisplayName(field, editSection),
              );
            else
              grouped.otherFields.push(getFieldDisplayName(field, editSection));
          });
          const result = [];
          if (grouped.permanent.length > 0)
            result.push(`Permanent Address: ${grouped.permanent.join(", ")}`);
          if (grouped.correspondence.length > 0)
            result.push(
              `Correspondence Address: ${grouped.correspondence.join(", ")}`,
            );
          if (grouped.otherFields.length > 0)
            result.push(...grouped.otherFields);
          return result;
        }

        return uniqueFields.map((key) => getFieldDisplayName(key, editSection));
      }
      return [];
    };

    // Render a single edit alert
    const renderEditAlert = (edit, index) => {
      // Get rejected fields for THIS specific edit request ONLY (strict matching)
      let rejectedFieldSet = new Set();
      if (edit.status === "approved" && memberData?.editRejections) {
        const sectionRejections = memberData.editRejections.filter((rej) => {
          const rejectionSection = rej.section || "";
          const sectionMatch =
            rejectionSection === edit.section ||
            rejectionSection === sectionName ||
            (sectionName === "Academic Details" &&
              rejectionSection === "Academic Information") ||
            (sectionName === "Academic Information" &&
              rejectionSection === "Academic Details");
          if (!sectionMatch) return false;
          // Strictly match rejection to THIS specific edit request by ID first, then timestamp
          if (rej.editId && edit._id) {
            return String(rej.editId) === String(edit._id);
          }
          // If no edit ID, match by timestamp (within 1 minute) - but only if this is the ONLY rejection for this section
          if (rej.rejectedAt && edit.editedAt) {
            const timeDiff = Math.abs(
              new Date(rej.rejectedAt) - new Date(edit.editedAt),
            );
            // Only match if timestamp is very close (within 1 minute) AND there's only one rejection for this section
            const otherRejections = memberData.editRejections.filter((r) => {
              const rSection = r.section || "";
              return (
                (rSection === edit.section || rSection === sectionName) &&
                r !== rej
              );
            });
            return timeDiff < 60000 && otherRejections.length === 0;
          }
          // If no ID or timestamp, don't match (too ambiguous)
          return false;
        });

        sectionRejections.forEach((rej) => {
          if (rej.rejectedFields && Array.isArray(rej.rejectedFields)) {
            rej.rejectedFields.forEach((f) => {
              const fieldName =
                typeof f === "object" && f !== null ? f.field || f : f;
              rejectedFieldSet.add(fieldName);
            });
          }
        });
      }

      // Get changed fields, filtering out rejected ones for approved edits
      let changedFieldsDisplay = [];
      if (
        edit.changedFields &&
        Array.isArray(edit.changedFields) &&
        edit.changedFields.length > 0
      ) {
        const fields = edit.changedFields.map((field) => {
          const fieldName =
            typeof field === "string" ? field : field.field || field;
          return fieldName;
        });
        const uniqueFields = [...new Set(fields)];

        // Filter out rejected fields for approved edits
        const approvedFields =
          edit.status === "approved" && rejectedFieldSet.size > 0
            ? uniqueFields.filter((f) => !rejectedFieldSet.has(f))
            : uniqueFields;

        if (approvedFields.length > 0) {
          changedFieldsDisplay = getChangedFields({
            ...edit,
            changedFields: approvedFields,
          });
        }
      } else {
        changedFieldsDisplay = getChangedFields(edit);
        // For approved edits, we need to filter the display strings - simpler approach
        if (
          edit.status === "approved" &&
          rejectedFieldSet.size > 0 &&
          edit.changedFields
        ) {
          // Re-filter at display level if needed
          const originalFields = edit.changedFields.map((f) =>
            typeof f === "string" ? f : f.field || f,
          );
          const approvedFields = originalFields.filter(
            (f) => !rejectedFieldSet.has(f),
          );
          if (approvedFields.length > 0) {
            changedFieldsDisplay = getChangedFields({
              ...edit,
              changedFields: approvedFields,
            });
          } else {
            changedFieldsDisplay = [];
          }
        }
      }

      return (
        <Alert
          key={`edit-${index}-${edit.editedAt}`}
          variant={getVariant(edit.status)}
          className="mb-4"
        >
          <div className="d-flex align-items-start" style={{ gap: "0.75rem" }}>
            <div className="flex-shrink-0" style={{ marginTop: "0.125rem" }}>
              {getStatusIcon(edit.status)}
            </div>
            <div className="flex-grow-1">
              <Alert.Heading
                className="mb-2"
                style={{ fontSize: "1rem", fontWeight: 600, lineHeight: 1.4 }}
              >
                <strong>Section:</strong> {edit.section || sectionName}
                <br />
                Edit Request {sortedEdits.length > 1
                  ? `#${index + 1}`
                  : ""}: <strong>{getStatusText(edit.status)}</strong>
              </Alert.Heading>
              {changedFieldsDisplay.length > 0 && (
                <div className="mb-2" style={{ lineHeight: 1.5 }}>
                  <strong style={{ fontSize: "0.9375rem" }}>
                    Changed Fields:
                  </strong>
                  <ul
                    className="mb-0 mt-1 ps-3"
                    style={{ lineHeight: 1.5, fontSize: "0.9375rem" }}
                  >
                    {changedFieldsDisplay.map((line, i) => (
                      <li key={i} style={{ marginBottom: "0.25rem" }}>
                        {line}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p
                className="mb-0 text-muted"
                style={{ fontSize: "0.875rem", lineHeight: 1.4 }}
              >
                <strong>Last Updated:</strong>{" "}
                {new Date(edit.editedAt).toLocaleString("en-IN", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
          </div>
        </Alert>
      );
    };

    // Render all edit alerts
    return (
      <div>
        {sortedEdits.map((edit, index) => renderEditAlert(edit, index))}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading member data...</p>
        </div>
      );
    }
    if (error) {
      return (
        <Alert variant="danger">
          <h4>Error</h4>
          <p>{error}</p>
          <Button
            variant="outline-danger"
            onClick={() => {
              const token = localStorage.getItem("token");
              if (userInfo && token) {
                fetchMemberData(userInfo, token);
              }
            }}
          >
            Retry
          </Button>
        </Alert>
      );
    }
    switch (activeTab) {
      case "Approval Flow":
        return (
          <div className="pt-3">
            <div className="mb-4">
              <Row className="g-4 flex-column">
                <Col xs={12}>
                  <div className="text-center p-4 border rounded shadow-sm bg-white approval-card">
                    <h3
                      className={`approval-heading mb-3 ${memberData.secretaryApprovals === "rejected"
                        ? "text-danger"
                        : memberData.secretaryApprovals === "approved"
                          ? "text-success"
                          : "text-dark"
                        }`}
                    >
                      Secretary:{" "}
                      {memberData.secretaryApprovals.charAt(0).toUpperCase() +
                        memberData.secretaryApprovals.slice(1)}
                    </h3>

                    {memberData.secretaryApprovals === "rejected" &&
                      memberData?.secretaryRejectedReason && (
                        <p className="text-danger mt-2 mb-0">
                          <strong>Reason:</strong>{" "}
                          {memberData.secretaryRejectedReason}
                        </p>
                      )}

                    <div className="approval-row">
                      <div className="approval-icon-wrapper">
                        <i className="fa-regular fa-user text-primary fs-4"></i>
                      </div>
                      <div className="approval-text">
                        <h6 className="mb-0 fs-3">Secretary</h6>
                        <small className="text-muted">AIOTA Secretary</small>
                      </div>
                    </div>

                    <div className="approval-row">
                      <div className="approval-icon-wrapper">
                        <i className="fa-regular fa-envelope text-primary fs-4"></i>
                      </div>
                      <div className="approval-text">
                        <span className="text-muted">
                          Email: secretary@aiota.org
                        </span>
                      </div>
                    </div>

                    <div className="approval-row">
                      <div className="approval-icon-wrapper">
                        <i className="fa-solid fa-phone text-primary fs-4"></i>
                      </div>
                      <div className="approval-text">
                        <span className="text-muted">
                          Contact Number: +91 94330 98374
                        </span>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <Alert
                    variant="warning"
                    className="d-flex align-items-start"
                    style={{ backgroundColor: "#FFEFB159", border: "none" }}
                  >
                    <div className="me-3 mt-1">
                      <TriangleAlert size={32} color="#F8A734" />
                    </div>
                    <div>
                      <p className="text-muted mb-0 approval-warning">
                        <strong>Note:</strong> No other means of communication
                        (such as WhatsApp, calls, or instant messages) will be
                        entertained.
                      </p>
                    </div>
                  </Alert>
                </Col>

                <Col xs={12}>
                  <div className="text-center p-4 border rounded shadow-sm bg-white approval-card">
                    <h3
                      className={`approval-heading mb-3 ${memberData.treasurerApprovals === "rejected"
                        ? "text-danger"
                        : memberData.treasurerApprovals === "approved"
                          ? "text-success"
                          : "text-dark"
                        }`}
                    >
                      Treasurer:{" "}
                      {memberData.treasurerApprovals.charAt(0).toUpperCase() +
                        memberData.treasurerApprovals.slice(1)}
                    </h3>

                    {memberData.treasurerApprovals === "rejected" &&
                      memberData?.treasurerRejectedReasons && (
                        <p className="text-danger mt-2 mb-0">
                          <strong>Reason:</strong>{" "}
                          {memberData.treasurerRejectedReasons}
                        </p>
                      )}

                    <div className="approval-row">
                      <div className="approval-icon-wrapper">
                        <i className="fa-regular fa-user text-primary fs-4"></i>
                      </div>
                      <div className="approval-text">
                        <h6 className="mb-0 fs-3">Treasurer</h6>
                        <small className="text-muted">AIOTA Treasurer</small>
                      </div>
                    </div>

                    <div className="approval-row">
                      <div className="approval-icon-wrapper">
                        <i className="fa-regular fa-envelope text-primary fs-4"></i>
                      </div>
                      <div className="approval-text">
                        <span className="text-muted">
                          Email: treasurer@aiota.org
                        </span>
                      </div>
                    </div>

                    <div className="approval-row">
                      <div className="approval-icon-wrapper">
                        <i className="fa-solid fa-phone text-primary fs-4"></i>
                      </div>
                      <div className="approval-text">
                        <span className="text-muted">
                          Contact Number: +91 98765 43210
                        </span>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col xs={12}>
                  <div className="text-center p-4 border rounded shadow-sm bg-white approval-card">
                    <h3
                      className={`approval-heading mb-3 ${memberData.presidentApprovals === "rejected"
                        ? "text-danger"
                        : memberData.presidentApprovals === "approved"
                          ? "text-success"
                          : "text-dark"
                        }`}
                    >
                      President:{" "}
                      {memberData.presidentApprovals.charAt(0).toUpperCase() +
                        memberData.presidentApprovals.slice(1)}
                    </h3>

                    {memberData.presidentApprovals === "rejected" &&
                      memberData?.presidentRejectedReason && (
                        <p className="text-danger mt-2 mb-0">
                          <strong>Reason:</strong>{" "}
                          {memberData.presidentRejectedReason}
                        </p>
                      )}

                    <div className="approval-row">
                      <div className="approval-icon-wrapper">
                        <i className="fa-regular fa-user text-primary fs-4"></i>
                      </div>
                      <div className="approval-text">
                        <h6 className="mb-0 fs-3">President</h6>
                        <small className="text-muted">AIOTA President</small>
                      </div>
                    </div>

                    <div className="approval-row">
                      <div className="approval-icon-wrapper">
                        <i className="fa-regular fa-envelope text-primary fs-4"></i>
                      </div>
                      <div className="approval-text">
                        <span className="text-muted">
                          Email: president@aiota.org
                        </span>
                      </div>
                    </div>

                    <div className="approval-row">
                      <div className="approval-icon-wrapper">
                        <i className="fa-solid fa-phone text-primary fs-4"></i>
                      </div>
                      <div className="approval-text">
                        <span className="text-muted">
                          Contact Number: +91 87654 32109
                        </span>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            <Row className="mt-5">
              <Col md={6}></Col>
            </Row>
          </div>
        );

      case "Personal Information":
        return (
          <div>
            {approvalStatus !== "approved" && (
              <Alert variant="info" className="mb-4">
                <strong>Verification Under Process:</strong> Your membership is
                currently under verification.{" "}
                {isProfileEditRestricted
                  ? "You can view your information. Editing will be enabled if the Secretary requests changes or after final approval."
                  : "You can view and edit your information."}
              </Alert>
            )}
            <EditStatusAlert sectionName="Personal Information" />
            <ApprovalAlert sectionName="Personal Information" />
            <RejectionAlert sectionName="Personal Information" />
            {editMessage && editMessageSection === "Personal Information" && (
              <EditMessageToast
                editMessage={editMessage}
                sectionName="Personal Information"
              />
            )}
            <Form>
              <Container>
                <div className="d-flex justify-content-end mb-3">
                  {!isEditing || editingSection !== "Personal Information" ? (
                    hasPendingEditRequest("Personal Information") ? (
                      <div className="d-flex align-items-center gap-2">
                        <Lock size={20} className="text-muted" />
                        <span className="text-muted small">
                          Edit request pending
                        </span>
                      </div>
                    ) : isProfileEditRestricted ? (
                      <div className="d-flex align-items-center gap-2">
                        <Lock size={20} className="text-muted" />
                        <span className="text-muted small">
                          Editing locked during verification
                        </span>
                      </div>
                    ) : (
                      <PenLine
                        role="button"
                        onClick={() => handleEditClick("Personal Information")}
                        style={{ cursor: "pointer" }}
                      />
                    )
                  ) : (
                    <div className="d-flex gap-2">
                      {hasChanges() && (
                        <Button
                          size="sm"
                          variant="success"
                          onClick={saveChanges}
                        >
                          Save
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={cancelEditing}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
                <Row className="mb-3 mt-4 d-flex justify-content-between">
                  <Col md={2} className="d-flex justify-content-center">
                    <Form.Group className="mb-3">
                      <div className="mb-2 d-flex align-items-center justify-content-center avatar-personal">
                        {memberData?.name
                          ? memberData.name.charAt(0).toUpperCase()
                          : "User"}
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Membership Type:
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="text"
                        value={userInfo?.rolename || ""}
                        readOnly
                      />
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Name:
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="text"
                        value={memberData?.name || memberData?.fullName || ""}
                        readOnly={
                          !isFieldEditable("Personal Information", "name")
                        }
                        onChange={
                          isFieldEditable("Personal Information", "name")
                            ? (e) => {
                              const val = e.target.value;
                              if (
                                val.length > 0 &&
                                !/^[a-zA-Z\s\-']*$/.test(val)
                              ) {
                                setNameError(
                                  "Name can only contain letters, spaces, hyphens and apostrophes.",
                                );
                              } else {
                                setNameError("");
                              }
                              setMemberData((prev) => ({
                                ...prev,
                                name: val,
                                fullName: val,
                              }));
                            }
                            : undefined
                        }
                        style={{
                          backgroundColor: isFieldEditable(
                            "Personal Information",
                            "name",
                          )
                            ? "white"
                            : "#f8f9fa",
                          border: isFieldEditable(
                            "Personal Information",
                            "name",
                          )
                            ? nameError
                              ? "2px solid #dc3545"
                              : "2px solid #007bff"
                            : "1px solid #dee2e6",
                        }}
                      />
                      {nameError &&
                        isFieldEditable("Personal Information", "name") && (
                          <div
                            className="text-danger"
                            style={{
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {nameError}
                          </div>
                        )}
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Certification Name:
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="text"
                        value={memberData?.certificationName || ""}
                        readOnly={
                          !isFieldEditable(
                            "Personal Information",
                            "nametobe_printed",
                          )
                        }
                        onChange={
                          isFieldEditable(
                            "Personal Information",
                            "nametobe_printed",
                          )
                            ? (e) => {
                              const val = e.target.value;
                              if (val.trim().length === 0) {
                                setCertificationNameError(
                                  "Certification Name is required and cannot be empty.",
                                );
                              } else {
                                setCertificationNameError("");
                              }
                              setMemberData((prev) => ({
                                ...prev,
                                certificationName: val,
                              }));
                            }
                            : undefined
                        }
                        style={{
                          backgroundColor: isFieldEditable(
                            "Personal Information",
                            "nametobe_printed",
                          )
                            ? "white"
                            : "#f8f9fa",
                          border: isFieldEditable(
                            "Personal Information",
                            "nametobe_printed",
                          )
                            ? certificationNameError
                              ? "2px solid #dc3545"
                              : "2px solid #007bff"
                            : "1px solid #dee2e6",
                        }}
                      />
                      {certificationNameError &&
                        isFieldEditable(
                          "Personal Information",
                          "nametobe_printed",
                        ) && (
                          <div
                            className="text-danger"
                            style={{
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {certificationNameError}
                          </div>
                        )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Date Of Birth:
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="date"
                        value={formatDateForInput(memberData?.dateOfBirth)}
                        readOnly={
                          !isFieldEditable("Personal Information", "dob")
                        }
                        max={(() => {
                          const d = new Date();
                          d.setFullYear(d.getFullYear() - 16);
                          return d.toISOString().split("T")[0];
                        })()}
                        onChange={
                          isFieldEditable("Personal Information", "dob")
                            ? (e) => {
                              const selectedDate = e.target.value;
                              setDobError("");
                              if (selectedDate) {
                                const dobDate = new Date(selectedDate);
                                const today = new Date();
                                const age =
                                  (today - dobDate) /
                                  (365.25 * 24 * 60 * 60 * 1000);
                                if (age < 16) {
                                  setDobError(
                                    "Only members 16 years or older are allowed.",
                                  );
                                }
                                if (dobDate > today) {
                                  setDobError(
                                    "Date of Birth cannot be in the future.",
                                  );
                                }
                              }
                              setMemberData((prev) => ({
                                ...prev,
                                dateOfBirth: selectedDate,
                              }));
                            }
                            : undefined
                        }
                        style={{
                          backgroundColor: isFieldEditable(
                            "Personal Information",
                            "dob",
                          )
                            ? "white"
                            : "#f8f9fa",
                          border: isFieldEditable("Personal Information", "dob")
                            ? dobError
                              ? "2px solid #dc3545"
                              : "2px solid #007bff"
                            : "1px solid #dee2e6",
                        }}
                      />
                      {dobError &&
                        isFieldEditable("Personal Information", "dob") && (
                          <div
                            className="text-danger"
                            style={{
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {dobError}
                          </div>
                        )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member d-flex align-items-center gap-2">
                        E-mail Address:
                        <Lock
                          size={16}
                          className="text-muted"
                          title="Email cannot be edited"
                        />
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="email"
                        value={memberData?.email || userInfo?.email || ""}
                        readOnly
                        style={{
                          backgroundColor: "#f8f9fa",
                          cursor: "not-allowed",
                          border: "1px solid #dee2e6",
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Mobile Number:
                      </Form.Label>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <Form.Select
                          value={memberData?.countryCode || "+91"}
                          disabled={
                            !isFieldEditable("Personal Information", "phone")
                          }
                          onChange={(e) =>
                            setMemberData((prev) => ({
                              ...prev,
                              countryCode: e.target.value,
                            }))
                          }
                          style={{ width: "90px" }}
                        >
                          {countryCodes.map((c) => (
                            <option key={c.iso} value={c.code}>
                              {c.code}
                            </option>
                          ))}
                        </Form.Select>

                        <Form.Control
                          className="input-field-aiota-member fs-6"
                          type="tel"
                          value={String(
                            memberData?.mobileNumber ??
                            memberData?.phoneNumber ??
                            "",
                          )
                            .trim()
                            .replace(/\s/g, "")}
                          readOnly={
                            !isFieldEditable("Personal Information", "phone")
                          }
                          onChange={
                            isFieldEditable("Personal Information", "phone")
                              ? (e) => {
                                const raw = e.target.value;
                                const value = raw
                                  .replace(/\D/g, "")
                                  .slice(0, 15);

                                if (/\D/.test(raw)) {
                                  setMobileNumberError(
                                    "Only digits are allowed",
                                  );
                                } else if (
                                  value.length > 0 &&
                                  (value.length < 7 || value.length > 15)
                                ) {
                                  setMobileNumberError(
                                    "Mobile number must be 7–15 digits",
                                  );
                                } else {
                                  setMobileNumberError("");
                                }

                                setMemberData((prev) => ({
                                  ...prev,
                                  mobileNumber: value,
                                  phoneNumber: value,
                                }));
                              }
                              : undefined
                          }
                          style={{
                            backgroundColor: isFieldEditable(
                              "Personal Information",
                              "phone",
                            )
                              ? "white"
                              : "#f8f9fa",
                            border: isFieldEditable(
                              "Personal Information",
                              "phone",
                            )
                              ? mobileNumberError
                                ? "2px solid #dc3545"
                                : "2px solid #007bff"
                              : "1px solid #dee2e6",
                            textAlign: "left",
                          }}
                        />
                      </div>
                      {mobileNumberError &&
                        isFieldEditable("Personal Information", "phone") && (
                          <div
                            className="text-danger"
                            style={{
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {mobileNumberError}
                          </div>
                        )}
                    </Form.Group>
                  </Col>
                  {userInfo?.rolename === "AssociateMember" && (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label className="input-label-aiota-member">
                          Aadhaar/Passport:
                        </Form.Label>
                        <Form.Control
                          className="input-field-aiota-member fs-6"
                          type="text"
                          value={memberData?.aadhaarPassport || ""}
                          readOnly={
                            !isFieldEditable(
                              "Personal Information",
                              "adhar_passport",
                            )
                          }
                          onChange={
                            isFieldEditable(
                              "Personal Information",
                              "adhar_passport",
                            )
                              ? (e) => {
                                const value = e.target.value.trim();

                                setMemberData((prev) => ({
                                  ...prev,
                                  aadhaarPassport: value,
                                }));

                                if (!value) {
                                  setAdharPassportError("");
                                  return;
                                }

                                const firstChar = value.charAt(0);

                                // Passport validation (starts with letter)
                                if (/^[A-Za-z]/.test(firstChar)) {
                                  const passportRegex = /^[A-Za-z0-9]{6,10}$/;

                                  if (!passportRegex.test(value)) {
                                    setAdharPassportError(
                                      "Passport: 6–10 alphanumeric characters.",
                                    );
                                  } else {
                                    setAdharPassportError("");
                                  }
                                }
                                // Aadhaar validation (starts with digit)
                                else if (/^\d/.test(firstChar)) {
                                  const aadhaarRegex = /^\d{12}$/;

                                  if (!aadhaarRegex.test(value)) {
                                    setAdharPassportError(
                                      "Aadhaar: 12 digits required.",
                                    );
                                  } else {
                                    setAdharPassportError("");
                                  }
                                } else {
                                  setAdharPassportError(
                                    "Invalid input format.",
                                  );
                                }
                              }
                              : undefined
                          }
                          style={{
                            backgroundColor: isFieldEditable(
                              "Personal Information",
                              "adhar_passport",
                            )
                              ? "white"
                              : "#f8f9fa",
                            border: isFieldEditable(
                              "Personal Information",
                              "adhar_passport",
                            )
                              ? "2px solid #007bff"
                              : "1px solid #dee2e6",
                          }}
                        />

                        {adharPassportError &&
                          isFieldEditable(
                            "Personal Information",
                            "adhar_passport",
                          ) && (
                            <div
                              className="text-danger"
                              style={{
                                fontSize: "0.875rem",
                                marginTop: "0.25rem",
                              }}
                            >
                              {adharPassportError}
                            </div>
                          )}
                      </Form.Group>
                    </Col>
                  )}
                  {userInfo?.rolename === "LifeMember" && (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label className="input-label-aiota-member">
                          Gender:
                        </Form.Label>
                        <Form.Select
                          className="input-field-aiota-member fs-6"
                          value={memberData?.gender || ""}
                          disabled={
                            !isFieldEditable("Personal Information", "gender")
                          }
                          onChange={
                            isFieldEditable("Personal Information", "gender")
                              ? (e) => {
                                setMemberData((prev) => ({
                                  ...prev,
                                  gender: e.target.value,
                                }));
                              }
                              : undefined
                          }
                          style={{
                            backgroundColor: isFieldEditable(
                              "Personal Information",
                              "gender",
                            )
                              ? "white"
                              : "#f8f9fa",
                            border: isFieldEditable(
                              "Personal Information",
                              "gender",
                            )
                              ? "2px solid #007bff"
                              : "1px solid #dee2e6",
                          }}
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}
                </Row>
                <h5 className="mb-4 fw-bold mt-4">AIOTA Details:</h5>
                <Row className="mb-3">
                  {(memberData?.hideIotr || !memberData?.iotrNumber) ? null : (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label className="input-label-aiota-member">
                          IOTR Number:
                        </Form.Label>
                        <Form.Control
                          className="input-field-aiota-member fs-6"
                          type="text"
                          value={memberData?.iotrNumber || "N/A"}
                          readOnly
                          style={{ backgroundColor: "#f8f9fa" }}
                        />
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        {memberData?.isLegacyNum
                          ? "New License Number"
                          : "License Number"}
                        :
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="text"
                        value={
                          memberData?.liscenceNo ||
                          (memberData?.isLegacyNum ? "NOT ASSIGNED" : "N/A")
                        }
                        readOnly
                        style={{ backgroundColor: "#f8f9fa" }}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Valid Till:
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="text"
                        value={memberData?.validTill || "N/A"}
                        readOnly
                        style={{ backgroundColor: "#f8f9fa" }}
                      />
                    </Form.Group>
                  </Col>
                  {memberData?.isLegacyNum && (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label
                          className="input-label-aiota-member"
                          style={{ color: "red", fontWeight: "bold" }}
                        >
                          Old License Number:
                        </Form.Label>
                        <Form.Control
                          className="input-field-aiota-member fs-6"
                          type="text"
                          value={memberData?.legacyLicenseNo || "N/A"}
                          readOnly
                          style={{
                            backgroundColor: "#fff5f5",
                            border: "1px solid red",
                            color: "red",
                          }}
                        />
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              </Container>
            </Form>
          </div>
        );

      case "Address":
        // Only show Address section for LifeMember
        if (userInfo?.rolename == "AssociateMember") {
          return null;
        }
        return (
          <div>
            {approvalStatus !== "approved" && (
              <Alert variant="info" className="mb-4">
                <strong>Verification Under Process:</strong> Your membership is
                currently under verification.{" "}
                {isProfileEditRestricted
                  ? "You can view your information. Editing will be enabled if the Secretary requests changes or after final approval."
                  : "You can view and edit your information."}
              </Alert>
            )}
            <EditStatusAlert sectionName="Address" />
            <ApprovalAlert sectionName="Address" />
            <RejectionAlert sectionName="Address" />
            {editMessage && editMessageSection === "Address" && (
              <EditMessageToast
                editMessage={editMessage}
                sectionName="Address"
              />
            )}
            <Form>
              <div className="d-flex justify-content-end mb-3">
                {!isEditing || editingSection !== "Address" ? (
                  hasPendingEditRequest("Address") ? (
                    <div className="d-flex align-items-center gap-2">
                      <Lock size={20} className="text-muted" />
                      <span className="text-muted small">
                        Edit request pending
                      </span>
                    </div>
                  ) : isProfileEditRestricted ? (
                    <div className="d-flex align-items-center gap-2">
                      <Lock size={20} className="text-muted" />
                      <span className="text-muted small">
                        Editing locked during verification
                      </span>
                    </div>
                  ) : (
                    <PenLine
                      role="button"
                      onClick={() => handleEditClick("Address")}
                      style={{ cursor: "pointer" }}
                    />
                  )
                ) : (
                  <div className="d-flex gap-2">
                    {hasChanges() && (
                      <Button size="sm" variant="success" onClick={saveChanges}>
                        Save
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={cancelEditing}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              <Container>
                <h5 className="mb-4 fw-bold">Permanent Address:</h5>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Flat/House NO/Floor/Building:
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="text"
                        value={memberData?.permanentAddress?.building || ""}
                        readOnly={!isFieldEditable("Address")}
                        onChange={
                          isFieldEditable("Address")
                            ? (e) => {
                              setMemberData((prev) => {
                                const updated = {
                                  ...prev,
                                  permanentAddress: {
                                    ...prev.permanentAddress,
                                    building: e.target.value,
                                  },
                                };
                                // If same address is checked, update correspondence address too
                                if (sameAddress) {
                                  updated.correspondenceAddress = {
                                    ...prev.correspondenceAddress,
                                    cobuilding: e.target.value,
                                  };
                                }
                                return updated;
                              });
                            }
                            : undefined
                        }
                        style={{
                          backgroundColor: isFieldEditable("Address")
                            ? "white"
                            : "#f8f9fa",
                          border: isFieldEditable("Address")
                            ? "2px solid #007bff"
                            : "1px solid #dee2e6",
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Colony/Street/Locality:
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="text"
                        value={memberData?.permanentAddress?.street || ""}
                        readOnly={!isFieldEditable("Address")}
                        onChange={
                          isFieldEditable("Address")
                            ? (e) => {
                              setMemberData((prev) => {
                                const updated = {
                                  ...prev,
                                  permanentAddress: {
                                    ...prev.permanentAddress,
                                    street: e.target.value,
                                  },
                                };
                                // If same address is checked, update correspondence address too
                                if (sameAddress) {
                                  updated.correspondenceAddress = {
                                    ...prev.correspondenceAddress,
                                    costreet: e.target.value,
                                  };
                                }
                                return updated;
                              });
                            }
                            : undefined
                        }
                        style={{
                          backgroundColor: isFieldEditable("Address")
                            ? "white"
                            : "#f8f9fa",
                          border: isFieldEditable("Address")
                            ? "2px solid #007bff"
                            : "1px solid #dee2e6",
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Landmark:
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="text"
                        value={memberData?.permanentAddress?.landmark || ""}
                        readOnly={!isFieldEditable("Address")}
                        onChange={
                          isFieldEditable("Address")
                            ? (e) => {
                              setMemberData((prev) => {
                                const updated = {
                                  ...prev,
                                  permanentAddress: {
                                    ...prev.permanentAddress,
                                    landmark: e.target.value,
                                  },
                                };
                                // If same address is checked, update correspondence address too
                                if (sameAddress) {
                                  updated.correspondenceAddress = {
                                    ...prev.correspondenceAddress,
                                    colandmark: e.target.value,
                                  };
                                }
                                return updated;
                              });
                            }
                            : undefined
                        }
                        style={{
                          backgroundColor: isFieldEditable("Address")
                            ? "white"
                            : "#f8f9fa",
                          border: isFieldEditable("Address")
                            ? "2px solid #007bff"
                            : "1px solid #dee2e6",
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Country:
                      </Form.Label>
                      {isFieldEditable("Address") ? (
                        <Form.Select
                          name="country"
                          value={memberData?.permanentAddress?.country || ""}
                          onChange={(e) => {
                            setMemberData((prev) => {
                              const updated = {
                                ...prev,
                                permanentAddress: {
                                  ...prev.permanentAddress,
                                  country: e.target.value,
                                  state: "", // Reset dependent fields
                                  district: "",
                                },
                              };
                              // If same address is checked, update correspondence address too
                              if (sameAddress) {
                                updated.correspondenceAddress = {
                                  ...prev.correspondenceAddress,
                                  cocountry: e.target.value,
                                  costate: "",
                                  codistrict: "",
                                };
                              }
                              return updated;
                            });
                          }}
                          style={{
                            backgroundColor: "white",
                            border: "2px solid #007bff",
                          }}
                        >
                          <option value="">Select Country</option>
                          {countries.map((country) => (
                            <option key={country._id} value={country._id}>
                              {country.country_name}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <Form.Control
                          className="input-field-aiota-member fs-6"
                          type="text"
                          value={
                            memberData?.permanentAddress?.countryName ||
                            countries.find(
                              (c) =>
                                String(c._id) ===
                                String(memberData?.permanentAddress?.country),
                            )?.country_name ||
                            memberData?.permanentAddress?.country ||
                            ""
                          }
                          readOnly
                          style={{ backgroundColor: "#f8f9fa" }}
                        />
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        State:
                      </Form.Label>
                      {isFieldEditable("Address") ? (
                        <Form.Select
                          name="state"
                          value={memberData?.permanentAddress?.state || ""}
                          onChange={(e) => {
                            setMemberData((prev) => {
                              const updated = {
                                ...prev,
                                permanentAddress: {
                                  ...prev.permanentAddress,
                                  state: e.target.value,
                                  district: "", // Reset district
                                },
                              };
                              // If same address is checked, update correspondence address too
                              if (sameAddress) {
                                updated.correspondenceAddress = {
                                  ...prev.correspondenceAddress,
                                  costate: e.target.value,
                                  codistrict: "",
                                };
                              }
                              return updated;
                            });
                          }}
                          disabled={!memberData?.permanentAddress?.country}
                          style={{
                            backgroundColor: "white",
                            border: "2px solid #007bff",
                          }}
                        >
                          <option value="">Select State</option>
                          {statesPermanent.map((state) => (
                            <option key={state._id} value={state._id}>
                              {state.state_name}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <Form.Control
                          className="input-field-aiota-member fs-6"
                          type="text"
                          value={
                            memberData?.permanentAddress?.stateName ||
                            statesPermanent.find(
                              (s) =>
                                String(s._id) ===
                                String(memberData?.permanentAddress?.state),
                            )?.state_name ||
                            memberData?.permanentAddress?.state ||
                            ""
                          }
                          readOnly
                          style={{ backgroundColor: "#f8f9fa" }}
                        />
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        District:
                      </Form.Label>
                      {isFieldEditable("Address") ? (
                        <Form.Select
                          name="district"
                          value={memberData?.permanentAddress?.district || ""}
                          onChange={(e) => {
                            setMemberData((prev) => {
                              const updated = {
                                ...prev,
                                permanentAddress: {
                                  ...prev.permanentAddress,
                                  district: e.target.value,
                                },
                              };
                              // If same address is checked, update correspondence address too
                              if (sameAddress) {
                                updated.correspondenceAddress = {
                                  ...prev.correspondenceAddress,
                                  codistrict: e.target.value,
                                };
                              }
                              return updated;
                            });
                          }}
                          disabled={!memberData?.permanentAddress?.state}
                          style={{
                            backgroundColor: "white",
                            border: "2px solid #007bff",
                          }}
                        >
                          <option value="">Select District</option>
                          {districtsPermanent.map((district) => (
                            <option key={district._id} value={district._id}>
                              {district.district_name}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <Form.Control
                          className="input-field-aiota-member fs-6"
                          type="text"
                          value={
                            memberData?.permanentAddress?.districtName ||
                            districtsPermanent.find(
                              (d) =>
                                String(d._id) ===
                                String(memberData?.permanentAddress?.district),
                            )?.district_name ||
                            memberData?.permanentAddress?.district ||
                            ""
                          }
                          readOnly
                          style={{ backgroundColor: "#f8f9fa" }}
                        />
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Pincode:
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="text"
                        value={memberData?.permanentAddress?.pincode || ""}
                        readOnly={!isFieldEditable("Address")}
                        onChange={
                          isFieldEditable("Address")
                            ? (e) => {
                              const raw = e.target.value;
                              const val = raw.replace(/\D/g, "").slice(0, 6);
                              setMemberData((prev) => {
                                const updated = {
                                  ...prev,
                                  permanentAddress: {
                                    ...prev.permanentAddress,
                                    pincode: val,
                                  },
                                };
                                if (sameAddress) {
                                  updated.correspondenceAddress = {
                                    ...prev.correspondenceAddress,
                                    copincode: val,
                                  };
                                }
                                return updated;
                              });
                            }
                            : undefined
                        }
                        style={{
                          backgroundColor: isFieldEditable("Address")
                            ? "white"
                            : "#f8f9fa",
                          border: isFieldEditable("Address")
                            ? "2px solid #007bff"
                            : "1px solid #dee2e6",
                        }}
                      />
                      {isFieldEditable("Address") &&
                        (memberData?.permanentAddress?.pincode || "").length >
                        0 &&
                        (memberData?.permanentAddress?.pincode || "").length !==
                        6 && (
                          <div className="text-danger small mt-1">
                            Pincode must be 6 digits
                          </div>
                        )}
                    </Form.Group>
                  </Col>
                </Row>
                {memberData?.correspondenceAddress && (
                  <>
                    <div className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="same-address-checkbox"
                        label="Same as Permanent Address"
                        checked={sameAddress}
                        onChange={handleSameAddressChange}
                        disabled={!isFieldEditable("Address")}
                        style={{
                          cursor: isFieldEditable("Address")
                            ? "pointer"
                            : "not-allowed",
                        }}
                      />
                    </div>
                    <h5 className="mb-4 fw-bold">Correspondence Address:</h5>
                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Flat/House NO/Floor/Building:
                          </Form.Label>
                          <Form.Control
                            className="input-field-aiota-member fs-6"
                            type="text"
                            value={
                              sameAddress
                                ? memberData?.permanentAddress?.building || ""
                                : memberData?.correspondenceAddress
                                  ?.cobuilding || ""
                            }
                            readOnly={
                              !isFieldEditable("Address") || sameAddress
                            }
                            onChange={
                              isFieldEditable("Address") && !sameAddress
                                ? (e) => {
                                  setMemberData((prev) => ({
                                    ...prev,
                                    correspondenceAddress: {
                                      ...prev.correspondenceAddress,
                                      cobuilding: e.target.value,
                                    },
                                  }));
                                }
                                : undefined
                            }
                            style={{
                              backgroundColor:
                                isFieldEditable("Address") && !sameAddress
                                  ? "white"
                                  : "#f8f9fa",
                              border:
                                isFieldEditable("Address") && !sameAddress
                                  ? "2px solid #007bff"
                                  : "1px solid #dee2e6",
                              cursor: sameAddress ? "not-allowed" : "text",
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Colony/Street/Locality:
                          </Form.Label>
                          <Form.Control
                            className="input-field-aiota-member fs-6"
                            type="text"
                            value={
                              sameAddress
                                ? memberData?.permanentAddress?.street || ""
                                : memberData?.correspondenceAddress?.costreet ||
                                ""
                            }
                            readOnly={
                              !isFieldEditable("Address") || sameAddress
                            }
                            onChange={
                              isFieldEditable("Address") && !sameAddress
                                ? (e) => {
                                  setMemberData((prev) => ({
                                    ...prev,
                                    correspondenceAddress: {
                                      ...prev.correspondenceAddress,
                                      costreet: e.target.value,
                                    },
                                  }));
                                }
                                : undefined
                            }
                            style={{
                              backgroundColor:
                                isFieldEditable("Address") && !sameAddress
                                  ? "white"
                                  : "#f8f9fa",
                              border:
                                isFieldEditable("Address") && !sameAddress
                                  ? "2px solid #007bff"
                                  : "1px solid #dee2e6",
                              cursor: sameAddress ? "not-allowed" : "text",
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Landmark:
                          </Form.Label>
                          <Form.Control
                            className="input-field-aiota-member fs-6"
                            type="text"
                            value={
                              sameAddress
                                ? memberData?.permanentAddress?.landmark || ""
                                : memberData?.correspondenceAddress
                                  ?.colandmark || ""
                            }
                            readOnly={
                              !isFieldEditable("Address") || sameAddress
                            }
                            onChange={
                              isFieldEditable("Address") && !sameAddress
                                ? (e) => {
                                  setMemberData((prev) => ({
                                    ...prev,
                                    correspondenceAddress: {
                                      ...prev.correspondenceAddress,
                                      colandmark: e.target.value,
                                    },
                                  }));
                                }
                                : undefined
                            }
                            style={{
                              backgroundColor:
                                isFieldEditable("Address") && !sameAddress
                                  ? "white"
                                  : "#f8f9fa",
                              border:
                                isFieldEditable("Address") && !sameAddress
                                  ? "2px solid #007bff"
                                  : "1px solid #dee2e6",
                              cursor: sameAddress ? "not-allowed" : "text",
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Country:
                          </Form.Label>
                          {isFieldEditable("Address") && !sameAddress ? (
                            <Form.Select
                              name="cocountry"
                              value={
                                sameAddress
                                  ? memberData?.permanentAddress?.country || ""
                                  : memberData?.correspondenceAddress
                                    ?.cocountry || ""
                              }
                              onChange={(e) => {
                                setMemberData((prev) => ({
                                  ...prev,
                                  correspondenceAddress: {
                                    ...prev.correspondenceAddress,
                                    cocountry: e.target.value,
                                    costate: "", // Reset dependent fields
                                    codistrict: "",
                                  },
                                }));
                              }}
                              disabled={sameAddress}
                              style={{
                                backgroundColor: sameAddress
                                  ? "#f8f9fa"
                                  : "white",
                                border: sameAddress
                                  ? "1px solid #dee2e6"
                                  : "2px solid #007bff",
                                cursor: sameAddress ? "not-allowed" : "pointer",
                              }}
                            >
                              <option value="">Select Country</option>
                              {countries.map((country) => (
                                <option key={country._id} value={country._id}>
                                  {country.country_name}
                                </option>
                              ))}
                            </Form.Select>
                          ) : (
                            <Form.Control
                              className="input-field-aiota-member fs-6"
                              type="text"
                              value={
                                sameAddress
                                  ? memberData?.permanentAddress?.countryName ||
                                  countries.find(
                                    (c) =>
                                      c._id ===
                                      memberData?.permanentAddress?.country,
                                  )?.country_name ||
                                  memberData?.permanentAddress?.country ||
                                  ""
                                  : memberData?.correspondenceAddress
                                    ?.cocountryName ||
                                  countries.find(
                                    (c) =>
                                      String(c._id) ===
                                      String(
                                        memberData?.correspondenceAddress
                                          ?.cocountry,
                                      ),
                                  )?.country_name ||
                                  memberData?.correspondenceAddress
                                    ?.cocountry ||
                                  ""
                              }
                              readOnly
                              style={{ backgroundColor: "#f8f9fa" }}
                            />
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            State:
                          </Form.Label>
                          {isFieldEditable("Address") && !sameAddress ? (
                            <Form.Select
                              name="costate"
                              value={
                                sameAddress
                                  ? memberData?.permanentAddress?.state || ""
                                  : memberData?.correspondenceAddress
                                    ?.costate || ""
                              }
                              onChange={(e) => {
                                setMemberData((prev) => ({
                                  ...prev,
                                  correspondenceAddress: {
                                    ...prev.correspondenceAddress,
                                    costate: e.target.value,
                                    codistrict: "", // Reset district
                                  },
                                }));
                              }}
                              disabled={
                                sameAddress ||
                                !memberData?.correspondenceAddress?.cocountry
                              }
                              style={{
                                backgroundColor: sameAddress
                                  ? "#f8f9fa"
                                  : "white",
                                border: sameAddress
                                  ? "1px solid #dee2e6"
                                  : "2px solid #007bff",
                                cursor: sameAddress ? "not-allowed" : "pointer",
                              }}
                            >
                              <option value="">Select State</option>
                              {statesCorrespondence.map((state) => (
                                <option key={state._id} value={state._id}>
                                  {state.state_name}
                                </option>
                              ))}
                            </Form.Select>
                          ) : (
                            <Form.Control
                              className="input-field-aiota-member fs-6"
                              type="text"
                              value={
                                sameAddress
                                  ? memberData?.permanentAddress?.stateName ||
                                  statesPermanent.find(
                                    (s) =>
                                      s._id ===
                                      memberData?.permanentAddress?.state,
                                  )?.state_name ||
                                  memberData?.permanentAddress?.state ||
                                  ""
                                  : memberData?.correspondenceAddress
                                    ?.costateName ||
                                  statesCorrespondence.find(
                                    (s) =>
                                      String(s._id) ===
                                      String(
                                        memberData?.correspondenceAddress
                                          ?.costate,
                                      ),
                                  )?.state_name ||
                                  memberData?.correspondenceAddress
                                    ?.costate ||
                                  ""
                              }
                              readOnly
                              style={{ backgroundColor: "#f8f9fa" }}
                            />
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            District:
                          </Form.Label>
                          {isFieldEditable("Address") && !sameAddress ? (
                            <Form.Select
                              name="codistrict"
                              value={
                                sameAddress
                                  ? memberData?.permanentAddress?.district || ""
                                  : memberData?.correspondenceAddress
                                    ?.codistrict || ""
                              }
                              onChange={(e) => {
                                setMemberData((prev) => ({
                                  ...prev,
                                  correspondenceAddress: {
                                    ...prev.correspondenceAddress,
                                    codistrict: e.target.value,
                                  },
                                }));
                              }}
                              disabled={
                                sameAddress ||
                                !memberData?.correspondenceAddress?.costate
                              }
                              style={{
                                backgroundColor: sameAddress
                                  ? "#f8f9fa"
                                  : "white",
                                border: sameAddress
                                  ? "1px solid #dee2e6"
                                  : "2px solid #007bff",
                                cursor: sameAddress ? "not-allowed" : "pointer",
                              }}
                            >
                              <option value="">Select District</option>
                              {districtsCorrespondence.map((district) => (
                                <option key={district._id} value={district._id}>
                                  {district.district_name}
                                </option>
                              ))}
                            </Form.Select>
                          ) : (
                            <Form.Control
                              className="input-field-aiota-member fs-6"
                              type="text"
                              value={
                                sameAddress
                                  ? memberData?.permanentAddress
                                    ?.districtName ||
                                  districtsPermanent.find(
                                    (d) =>
                                      d._id ===
                                      memberData?.permanentAddress?.district,
                                  )?.district_name ||
                                  memberData?.permanentAddress?.district ||
                                  ""
                                  : memberData?.correspondenceAddress
                                    ?.codistrictName ||
                                  districtsCorrespondence.find(
                                    (d) =>
                                      String(d._id) ===
                                      String(
                                        memberData?.correspondenceAddress
                                          ?.codistrict,
                                      ),
                                  )?.district_name ||
                                  memberData?.correspondenceAddress
                                    ?.codistrict ||
                                  ""
                              }
                              readOnly
                              style={{ backgroundColor: "#f8f9fa" }}
                            />
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Pincode:
                          </Form.Label>
                          <Form.Control
                            className="input-field-aiota-member fs-6"
                            type="text"
                            value={
                              sameAddress
                                ? memberData?.permanentAddress?.pincode || ""
                                : memberData?.correspondenceAddress
                                  ?.copincode || ""
                            }
                            readOnly={
                              !isFieldEditable("Address") || sameAddress
                            }
                            onChange={
                              isFieldEditable("Address") && !sameAddress
                                ? (e) => {
                                  const raw = e.target.value;
                                  const val = raw
                                    .replace(/\D/g, "")
                                    .slice(0, 6);
                                  setMemberData((prev) => ({
                                    ...prev,
                                    correspondenceAddress: {
                                      ...prev.correspondenceAddress,
                                      copincode: val,
                                    },
                                  }));
                                }
                                : undefined
                            }
                            style={{
                              backgroundColor:
                                isFieldEditable("Address") && !sameAddress
                                  ? "white"
                                  : "#f8f9fa",
                              border:
                                isFieldEditable("Address") && !sameAddress
                                  ? "2px solid #007bff"
                                  : "1px solid #dee2e6",
                              cursor: sameAddress ? "not-allowed" : "text",
                            }}
                          />
                          {isFieldEditable("Address") &&
                            !sameAddress &&
                            (memberData?.correspondenceAddress?.copincode || "")
                              .length > 0 &&
                            (memberData?.correspondenceAddress?.copincode || "")
                              .length !== 6 && (
                              <div className="text-danger small mt-1">
                                Pincode must be 6 digits
                              </div>
                            )}
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
              </Container>
            </Form>
          </div>
        );
      case "Academic Details":
        // Only show Academic Details section for LifeMember
        if (userInfo?.rolename !== "LifeMember") {
          return null;
        }
        return (
          <div>
            {approvalStatus !== "approved" && (
              <Alert variant="info" className="mb-4">
                <strong>Verification Under Process:</strong> Your membership is
                currently under verification.{" "}
                {isProfileEditRestricted
                  ? "You can view your information. Editing will be enabled if the Secretary requests changes or after final approval."
                  : "You can view and edit your information."}
              </Alert>
            )}
            <EditStatusAlert sectionName="Academic Details" />
            <ApprovalAlert sectionName="Academic Details" />
            <RejectionAlert sectionName="Academic Details" />
            {editMessage && editMessageSection === "Academic Details" && (
              <EditMessageToast
                editMessage={editMessage}
                sectionName="Academic Details"
              />
            )}
            <Form>
              <div className="d-flex justify-content-end mb-3">
                {!isEditing || editingSection !== "Academic Details" ? (
                  hasPendingEditRequest("Academic Details") ? (
                    <div className="d-flex align-items-center gap-2">
                      <Lock size={20} className="text-muted" />
                      <span className="text-muted small">
                        Edit request pending
                      </span>
                    </div>
                  ) : isProfileEditRestricted ? (
                    <div className="d-flex align-items-center gap-2">
                      <Lock size={20} className="text-muted" />
                      <span className="text-muted small">
                        Editing locked during verification
                      </span>
                    </div>
                  ) : (
                    <PenLine
                      role="button"
                      onClick={() => handleEditClick("Academic Details")}
                      style={{ cursor: "pointer" }}
                    />
                  )
                ) : (
                  <div className="d-flex gap-2">
                    {hasChanges() && (
                      <Button size="sm" variant="success" onClick={saveChanges}>
                        Save
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={cancelEditing}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              <Container>
                <h5 className="mb-4 fw-bold">UG Details:</h5>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Institution:
                      </Form.Label>
                      {isFieldEditable("Academic Details") ? (
                        <Form.Select
                          name="ug_institution"
                          value={memberData?.ugDetails?.institution || ""}
                          onChange={(e) => {
                            setMemberData((prev) => ({
                              ...prev,
                              ugDetails: {
                                ...prev.ugDetails,
                                institution: e.target.value,
                              },
                            }));
                          }}
                          style={{
                            backgroundColor: "white",
                            border: "2px solid #007bff",
                          }}
                        >
                          <option value="">Select Institution</option>
                          {institutions.map((institution) => (
                            <option
                              key={institution._id}
                              value={institution._id}
                            >
                              {institution.name_and_place_institution}
                            </option>
                          ))}
                          {memberData?.ugDetails?.institution &&
                            memberData?.ugDetails?.institution !== "Other" &&
                            !institutions.some(
                              (i) =>
                                String(i._id) ===
                                String(memberData.ugDetails.institution),
                            ) && (
                              <option value={memberData.ugDetails.institution}>
                                {memberData.ugDetails.institution}
                              </option>
                            )}
                        </Form.Select>
                      ) : (
                        <Form.Control
                          className="input-field-aiota-member fs-6"
                          type="text"
                          value={
                            memberData?.ugDetails?.institutionName ||
                            institutions.find(
                              (i) =>
                                String(i._id) ===
                                String(memberData?.ugDetails?.institution),
                            )?.name_and_place_institution ||
                            ""
                          }
                          readOnly
                          style={{ backgroundColor: "#f8f9fa" }}
                        />
                      )}
                    </Form.Group>
                  </Col>
                  {ugIntitutionName === "Others" && (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label className="input-label-aiota-member">
                          Name of the Institution:
                        </Form.Label>
                        <Form.Control
                          className="input-field-aiota-member fs-6"
                          type="text"
                          value={
                            memberData?.ugDetails
                              ?.ug_non_accredited_college_name || ""
                          }
                          readOnly={!isFieldEditable("Academic Details")}
                          onChange={
                            isFieldEditable("Academic Details")
                              ? (e) => {
                                setMemberData((prev) => ({
                                  ...prev,
                                  ugDetails: {
                                    ...prev.ugDetails,
                                    ug_non_accredited_college_name:
                                      e.target.value,
                                  },
                                }));
                              }
                              : undefined
                          }
                          style={{
                            backgroundColor: isFieldEditable("Academic Details")
                              ? "white"
                              : "#f8f9fa",
                            border: isFieldEditable("Academic Details")
                              ? "2px solid #007bff"
                              : "1px solid #dee2e6",
                          }}
                        />
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Name of Degree:
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="text"
                        value={memberData?.ugDetails?.degree || ""}
                        readOnly={!isFieldEditable("Academic Details")}
                        onChange={
                          isFieldEditable("Academic Details")
                            ? (e) => {
                              setMemberData((prev) => ({
                                ...prev,
                                ugDetails: {
                                  ...prev.ugDetails,
                                  degree: e.target.value,
                                },
                              }));
                            }
                            : undefined
                        }
                        style={{
                          backgroundColor: isFieldEditable("Academic Details")
                            ? "white"
                            : "#f8f9fa",
                          border: isFieldEditable("Academic Details")
                            ? "2px solid #007bff"
                            : "1px solid #dee2e6",
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Name of University:
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="text"
                        value={memberData?.ugDetails?.university || ""}
                        readOnly={!isFieldEditable("Academic Details")}
                        onChange={
                          isFieldEditable("Academic Details")
                            ? (e) => {
                              setMemberData((prev) => ({
                                ...prev,
                                ugDetails: {
                                  ...prev.ugDetails,
                                  university: e.target.value,
                                },
                              }));
                            }
                            : undefined
                        }
                        style={{
                          backgroundColor: isFieldEditable("Academic Details")
                            ? "white"
                            : "#f8f9fa",
                          border: isFieldEditable("Academic Details")
                            ? "2px solid #007bff"
                            : "1px solid #dee2e6",
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="input-label-aiota-member">
                        Date Of Completion:
                      </Form.Label>
                      <Form.Control
                        className="input-field-aiota-member fs-6"
                        type="date"
                        value={
                          memberData?.ugDetails?.completionDate
                            ? new Date(memberData.ugDetails.completionDate)
                              .toISOString()
                              .split("T")[0]
                            : ""
                        }
                        readOnly={!isFieldEditable("Academic Details")}
                        onChange={
                          isFieldEditable("Academic Details")
                            ? (e) => {
                              setMemberData((prev) => ({
                                ...prev,
                                ugDetails: {
                                  ...prev.ugDetails,
                                  completionDate: e.target.value,
                                },
                              }));
                            }
                            : undefined
                        }
                        style={{
                          backgroundColor: isFieldEditable("Academic Details")
                            ? "white"
                            : "#f8f9fa",
                          border: isFieldEditable("Academic Details")
                            ? "2px solid #007bff"
                            : "1px solid #dee2e6",
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {memberData?.pgDetails && (
                  <>
                    <h5 className="mb-4 fw-bold">PG Details:</h5>
                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Institution:
                          </Form.Label>
                          {isFieldEditable("Academic Details") ? (
                            <Form.Select
                              name="pg_institution"
                              value={memberData?.pgDetails?.pginstitution || ""}
                              onChange={(e) => {
                                setMemberData((prev) => ({
                                  ...prev,
                                  pgDetails: {
                                    ...prev.pgDetails,
                                    pginstitution: e.target.value,
                                  },
                                }));
                              }}
                              style={{
                                backgroundColor: "white",
                                border: "2px solid #007bff",
                              }}
                            >
                              <option value="">Select Institution</option>
                              {institutions.map((institution) => (
                                <option
                                  key={institution._id}
                                  value={institution._id}
                                >
                                  {institution.name_and_place_institution}
                                </option>
                              ))}
                              {memberData?.pgDetails?.pginstitution &&
                                memberData?.pgDetails?.institution !==
                                "Other" &&
                                !institutions.some(
                                  (i) =>
                                    String(i._id) ===
                                    String(memberData.pgDetails.pginstitution),
                                ) && (
                                  <option
                                    value={memberData.pgDetails.pginstitution}
                                  >
                                    {memberData.pgDetails.pginstitution}
                                  </option>
                                )}
                            </Form.Select>
                          ) : (
                            <Form.Control
                              className="input-field-aiota-member fs-6"
                              type="text"
                              value={
                                memberData?.pgDetails?.pginstitutionName ||
                                institutions.find(
                                  (i) =>
                                    String(i._id) ===
                                    String(
                                      memberData?.pgDetails?.pginstitution,
                                    ),
                                )?.name_and_place_institution ||
                                memberData?.pgDetails?.pginstitution ||
                                ""
                              }
                              readOnly
                              style={{ backgroundColor: "#f8f9fa" }}
                            />
                          )}
                        </Form.Group>
                      </Col>
                      {pgIntitutionName === "Others" && (
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label className="input-label-aiota-member">
                              Name of the Institution:
                            </Form.Label>
                            <Form.Control
                              className="input-field-aiota-member fs-6"
                              type="text"
                              value={
                                memberData?.pgDetails
                                  ?.pg_non_accredited_college_name || ""
                              }
                              readOnly={!isFieldEditable("Academic Details")}
                              onChange={
                                isFieldEditable("Academic Details")
                                  ? (e) => {
                                    setMemberData((prev) => ({
                                      ...prev,
                                      pgDetails: {
                                        ...prev.pgDetails,
                                        pg_non_accredited_college_name:
                                          e.target.value,
                                      },
                                    }));
                                  }
                                  : undefined
                              }
                              style={{
                                backgroundColor: isFieldEditable(
                                  "Academic Details",
                                )
                                  ? "white"
                                  : "#f8f9fa",
                                border: isFieldEditable("Academic Details")
                                  ? "2px solid #007bff"
                                  : "1px solid #dee2e6",
                              }}
                            />
                          </Form.Group>
                        </Col>
                      )}
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Name of the Degree:
                          </Form.Label>
                          <Form.Control
                            className="input-field-aiota-member fs-6"
                            type="text"
                            value={memberData?.pgDetails?.degree || ""}
                            readOnly={!isFieldEditable("Academic Details")}
                            onChange={
                              isFieldEditable("Academic Details")
                                ? (e) => {
                                  setMemberData((prev) => ({
                                    ...prev,
                                    pgDetails: {
                                      ...prev.pgDetails,
                                      degree: e.target.value,
                                    },
                                  }));
                                }
                                : undefined
                            }
                            style={{
                              backgroundColor: isFieldEditable(
                                "Academic Details",
                              )
                                ? "white"
                                : "#f8f9fa",
                              border: isFieldEditable("Academic Details")
                                ? "2px solid #007bff"
                                : "1px solid #dee2e6",
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Name & place (abroad institution):
                          </Form.Label>
                          <Form.Control
                            className="input-field-aiota-member fs-6"
                            type="text"
                            value={
                              memberData?.pgDetails?.abroadInstitution || ""
                            }
                            readOnly={!isFieldEditable("Academic Details")}
                            onChange={
                              isFieldEditable("Academic Details")
                                ? (e) => {
                                  setMemberData((prev) => ({
                                    ...prev,
                                    pgDetails: {
                                      ...prev.pgDetails,
                                      abroadInstitution: e.target.value,
                                    },
                                  }));
                                }
                                : undefined
                            }
                            style={{
                              backgroundColor: isFieldEditable(
                                "Academic Details",
                              )
                                ? "white"
                                : "#f8f9fa",
                              border: isFieldEditable("Academic Details")
                                ? "2px solid #007bff"
                                : "1px solid #dee2e6",
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Specialization:
                          </Form.Label>
                          {isFieldEditable("Academic Details") ? (
                            (() => {
                              const specValue = (
                                memberData?.pgDetails?.specialization || ""
                              ).trim();
                              const inList =
                                PG_SPECIALIZATION_OPTIONS.includes(specValue);
                              return (
                                <Form.Select
                                  name="pg_specialization"
                                  value={specValue}
                                  onChange={(e) => {
                                    setMemberData((prev) => ({
                                      ...prev,
                                      pgDetails: {
                                        ...(prev.pgDetails || {}),
                                        specialization: e.target.value,
                                      },
                                    }));
                                  }}
                                  style={{
                                    backgroundColor: "white",
                                    border: "2px solid #007bff",
                                  }}
                                >
                                  <option value="">
                                    Select Specialization
                                  </option>
                                  {PG_SPECIALIZATION_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                  {specValue && !inList ? (
                                    <option value={specValue}>
                                      {specValue}
                                    </option>
                                  ) : null}
                                </Form.Select>
                              );
                            })()
                          ) : (
                            <Form.Control
                              className="input-field-aiota-member fs-6"
                              type="text"
                              value={
                                memberData?.pgDetails?.specialization || ""
                              }
                              readOnly
                              style={{ backgroundColor: "#f8f9fa" }}
                            />
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Other Specialization:
                          </Form.Label>
                          <Form.Control
                            className="input-field-aiota-member fs-6"
                            type="text"
                            value={
                              memberData?.pgDetails?.otherSpecialization || ""
                            }
                            readOnly={!isFieldEditable("Academic Details")}
                            onChange={
                              isFieldEditable("Academic Details")
                                ? (e) => {
                                  setMemberData((prev) => ({
                                    ...prev,
                                    pgDetails: {
                                      ...prev.pgDetails,
                                      otherSpecialization: e.target.value,
                                    },
                                  }));
                                }
                                : undefined
                            }
                            style={{
                              backgroundColor: isFieldEditable(
                                "Academic Details",
                              )
                                ? "white"
                                : "#f8f9fa",
                              border: isFieldEditable("Academic Details")
                                ? "2px solid #007bff"
                                : "1px solid #dee2e6",
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Date Of Completion:
                          </Form.Label>
                          <Form.Control
                            className="input-field-aiota-member fs-6"
                            type="date"
                            value={
                              memberData?.pgDetails?.completionDate
                                ? new Date(memberData.pgDetails.completionDate)
                                  .toISOString()
                                  .split("T")[0]
                                : ""
                            }
                            readOnly={!isFieldEditable("Academic Details")}
                            onChange={
                              isFieldEditable("Academic Details")
                                ? (e) => {
                                  try {
                                    setMemberData((prev) => ({
                                      ...prev,
                                      pgDetails: {
                                        ...prev.pgDetails,
                                        completionDate: e.target.value,
                                      },
                                    }));
                                  } catch (error) {
                                    console.error(
                                      "Error updating PG completion date:",
                                      error,
                                    );
                                  }
                                }
                                : undefined
                            }
                            style={{
                              backgroundColor: isFieldEditable(
                                "Academic Details",
                              )
                                ? "white"
                                : "#f8f9fa",
                              border: isFieldEditable("Academic Details")
                                ? "2px solid #007bff"
                                : "1px solid #dee2e6",
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
                {memberData?.otherQualifications && (
                  <>
                    <h5 className="mb-4 fw-bold">Other Qualifications:</h5>
                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Qualification:
                          </Form.Label>
                          {isFieldEditable("Academic Details") ? (
                            <div className="d-flex gap-3 align-items-center">
                              <Form.Check
                                type="radio"
                                id="qualification-phd"
                                name="qualification"
                                label="PHD"
                                value="PHD"
                                checked={
                                  memberData?.otherQualifications
                                    ?.qualification === "PHD"
                                }
                                onChange={(e) => {
                                  setMemberData((prev) => ({
                                    ...prev,
                                    otherQualifications: {
                                      ...prev.otherQualifications,
                                      qualification: e.target.value,
                                    },
                                  }));
                                }}
                                style={{ cursor: "pointer" }}
                              />
                              <Form.Check
                                type="radio"
                                id="qualification-pgdr"
                                name="qualification"
                                label="PGDR"
                                value="PGDR"
                                checked={
                                  memberData?.otherQualifications
                                    ?.qualification === "PGDR"
                                }
                                onChange={(e) => {
                                  setMemberData((prev) => ({
                                    ...prev,
                                    otherQualifications: {
                                      ...prev.otherQualifications,
                                      qualification: e.target.value,
                                    },
                                  }));
                                }}
                                style={{ cursor: "pointer" }}
                              />
                            </div>
                          ) : (
                            <Form.Control
                              className="input-field-aiota-member fs-6"
                              type="text"
                              value={
                                memberData?.otherQualifications
                                  ?.qualification || ""
                              }
                              readOnly
                              style={{
                                backgroundColor: "#f8f9fa",
                                border: "1px solid #dee2e6",
                              }}
                            />
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Name of Institution:
                          </Form.Label>
                          <Form.Control
                            className="input-field-aiota-member fs-6"
                            type="text"
                            value={
                              memberData?.otherQualifications
                                ?.institutionName ||
                              memberData?.otherQualifications?.institution ||
                              ""
                            }
                            readOnly={!isFieldEditable("Academic Details")}
                            onChange={
                              isFieldEditable("Academic Details")
                                ? (e) => {
                                  setMemberData((prev) => ({
                                    ...prev,
                                    otherQualifications: {
                                      ...prev.otherQualifications,
                                      institution: e.target.value,
                                    },
                                  }));
                                }
                                : undefined
                            }
                            style={{
                              backgroundColor: isFieldEditable(
                                "Academic Details",
                              )
                                ? "white"
                                : "#f8f9fa",
                              border: isFieldEditable("Academic Details")
                                ? "2px solid #007bff"
                                : "1px solid #dee2e6",
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Research Area:
                          </Form.Label>
                          <Form.Control
                            className="input-field-aiota-member fs-6"
                            type="text"
                            value={
                              memberData?.otherQualifications?.researchArea ||
                              ""
                            }
                            readOnly={!isFieldEditable("Academic Details")}
                            onChange={
                              isFieldEditable("Academic Details")
                                ? (e) => {
                                  setMemberData((prev) => ({
                                    ...prev,
                                    otherQualifications: {
                                      ...prev.otherQualifications,
                                      researchArea: e.target.value,
                                    },
                                  }));
                                }
                                : undefined
                            }
                            style={{
                              backgroundColor: isFieldEditable(
                                "Academic Details",
                              )
                                ? "white"
                                : "#f8f9fa",
                              border: isFieldEditable("Academic Details")
                                ? "2px solid #007bff"
                                : "1px solid #dee2e6",
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Date Of Completion:
                          </Form.Label>
                          <Form.Control
                            className="input-field-aiota-member fs-6"
                            type="date"
                            value={
                              memberData?.otherQualifications
                                ?.othercompletiondate
                                ? new Date(
                                  memberData.otherQualifications
                                    .othercompletiondate,
                                )
                                  .toISOString()
                                  .split("T")[0]
                                : ""
                            }
                            readOnly={!isFieldEditable("Academic Details")}
                            onChange={
                              isFieldEditable("Academic Details")
                                ? (e) => {
                                  try {
                                    setMemberData((prev) => ({
                                      ...prev,
                                      otherQualifications: {
                                        ...prev.otherQualifications,
                                        othercompletiondate: e.target.value,
                                      },
                                    }));
                                  } catch (error) {
                                    console.error(
                                      "Error updating Other Qualifications completion date:",
                                      error,
                                    );
                                  }
                                }
                                : undefined
                            }
                            style={{
                              backgroundColor: isFieldEditable(
                                "Academic Details",
                              )
                                ? "white"
                                : "#f8f9fa",
                              border: isFieldEditable("Academic Details")
                                ? "2px solid #007bff"
                                : "1px solid #dee2e6",
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            Other Qualification:
                          </Form.Label>
                          <Form.Control
                            className="input-field-aiota-member fs-6"
                            type="text"
                            value={
                              memberData?.otherQualifications
                                ?.otherqualification || ""
                            }
                            readOnly={!isFieldEditable("Academic Details")}
                            onChange={
                              isFieldEditable("Academic Details")
                                ? (e) => {
                                  setMemberData((prev) => ({
                                    ...prev,
                                    otherQualifications: {
                                      ...prev.otherQualifications,
                                      otherqualification: e.target.value,
                                    },
                                  }));
                                }
                                : undefined
                            }
                            style={{
                              backgroundColor: isFieldEditable(
                                "Academic Details",
                              )
                                ? "white"
                                : "#f8f9fa",
                              border: isFieldEditable("Academic Details")
                                ? "2px solid #007bff"
                                : "1px solid #dee2e6",
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
              </Container>
            </Form>
          </div>
        );
      case "Uploaded Documents":
        // For AssociateMember, show only profile photo
        if (userInfo?.rolename === "AssociateMember") {
          return (
            <div>
              {approvalStatus !== "approved" && (
                <Alert variant="info" className="mb-4">
                  <strong>Verification Under Process:</strong> Your membership
                  is currently under verification.{" "}
                  {isProfileEditRestricted
                    ? "You can view your information. Editing will be enabled if the Secretary requests changes or after final approval."
                    : "You can view and edit your information."}
                </Alert>
              )}
              <EditStatusAlert sectionName="Uploaded Documents" />
              <ApprovalAlert sectionName="Uploaded Documents" />
              <RejectionAlert sectionName="Uploaded Documents" />
              {editMessage && editMessageSection === "Uploaded Documents" && (
                <EditMessageToast
                  editMessage={editMessage}
                  sectionName="Uploaded Documents"
                />
              )}
              <Form>
                <div className="d-flex justify-content-end mb-3">
                  {!isEditing || editingSection !== "Uploaded Documents" ? (
                    hasPendingEditRequest("Uploaded Documents") ? (
                      <div className="d-flex align-items-center gap-2">
                        <Lock size={20} className="text-muted" />
                        <span className="text-muted small">
                          Edit request pending
                        </span>
                      </div>
                    ) : isProfileEditRestricted ? (
                      <div className="d-flex align-items-center gap-2">
                        <Lock size={20} className="text-muted" />
                        <span className="text-muted small">
                          Editing locked during verification
                        </span>
                      </div>
                    ) : (
                      <PenLine
                        role="button"
                        onClick={() => handleEditClick("Uploaded Documents")}
                        style={{ cursor: "pointer" }}
                      />
                    )
                  ) : (
                    <div className="d-flex gap-2">
                      {hasChanges() && (
                        <Button
                          size="sm"
                          variant="success"
                          onClick={saveChanges}
                        >
                          Save
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={cancelEditing}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
                <Container>
                  <h5 className="mb-4 fw-bold">Uploaded Documents:</h5>
                  <Row className="mb-4">
                    {memberData?.documents
                      ?.filter((doc) => {
                        if (userInfo?.rolename === "AssociateMember") {
                          return [
                            "Color Passport Size Photo",
                            "Highest Qualification",
                            "Internship Certificate",
                            "ID Card",
                          ].includes(doc.type);
                        }
                        return doc.type !== "Character Certificate";
                      })
                      .map((doc, index) => (
                        <Col md={4} key={index} style={{ display: "flex" }}>
                          <Form.Group
                            className="mb-3"
                            style={{
                              width: "100%",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <Form.Label className="input-label-aiota-member">
                              {doc.type || `Document ${index + 1}`}:
                            </Form.Label>

                            {isFieldEditable("Uploaded Documents") ? (
                              <div
                                style={{
                                  flexGrow: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                {/* FILE BOX */}
                                <div
                                  style={{
                                    padding: "8px",
                                    border: "2px solid #dee2e6",
                                    borderRadius: "6px",
                                    marginBottom: "8px",
                                    minHeight: "120px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  {doc.url ? (
                                    <>
                                      <div className="d-flex align-items-center text-muted fw-normal mb-2">
                                        <FileText className="me-2 text-danger" />
                                        <span
                                          style={{
                                            display: "-webkit-box",
                                            WebkitLineClamp: 1,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            wordBreak: "break-word",
                                            whiteSpace: "normal",
                                            maxWidth: "180px",
                                          }}
                                        >
                                          {doc.name ||
                                            `document_${index + 1}.pdf`}
                                        </span>
                                      </div>

                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "center",
                                          gap: "8px",
                                        }}
                                      >
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          style={{
                                            fontSize: "0.7rem",
                                            padding: "0.25rem 0.4rem",
                                            lineHeight: "1.2",
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDocumentView(doc.url);
                                          }}
                                        >
                                          <Eye size={11} className="me-1" />{" "}
                                          View
                                        </Button>

                                        <Button
                                          variant="primary"
                                          size="sm"
                                          style={{
                                            fontSize: "0.7rem",
                                            padding: "0.25rem 0.4rem",
                                            lineHeight: "1.2",
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDocumentDownload(
                                              doc.url,
                                              doc.name ||
                                              doc.type ||
                                              `Document_${index + 1}`,
                                            );
                                          }}
                                        >
                                          <Download
                                            size={11}
                                            className="me-1"
                                          />{" "}
                                          Download
                                        </Button>
                                      </div>
                                    </>
                                  ) : (
                                    <div
                                      className="d-flex align-items-center justify-content-center text-muted fw-normal"
                                      style={{ height: "100%" }}
                                    >
                                      <FileText className="me-2 text-danger" />
                                      <span>No document uploaded</span>
                                    </div>
                                  )}
                                </div>

                                {/* SELECTED FILE */}
                                {doc.newFile && (
                                  <Alert
                                    variant="success"
                                    style={{
                                      padding: "6px",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <small>
                                      ✓ Selected: {doc.newFile.name}
                                    </small>
                                  </Alert>
                                )}

                                {/* FILE INPUT */}
                                <Form.Control
                                  type="file"
                                  className="input-field-aiota-member fs-6"
                                  accept={
                                    doc.type === "Color Passport Size Photo"
                                      ? "image/*"
                                      : ".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                  }
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;

                                    if (
                                      doc.type === "Color Passport Size Photo"
                                    ) {
                                      if (
                                        file.type === "application/pdf" ||
                                        file.name
                                          .toLowerCase()
                                          .endsWith(".pdf") ||
                                        !file.type.startsWith("image/")
                                      ) {
                                        setEditMessage({
                                          type: "danger",
                                          message:
                                            "Color Passport Size Photo accepts only image files (JPG, PNG).",
                                        });
                                        setEditMessageSection(
                                          "Uploaded Documents",
                                        );
                                        e.target.value = "";
                                        return;
                                      }
                                    }

                                    const maxSize = 10 * 1024 * 1024;
                                    if (file.size > maxSize) {
                                      setEditMessage({
                                        type: "danger",
                                        message: `${doc.type} size exceeds 10MB.`,
                                      });
                                      setEditMessageSection(
                                        "Uploaded Documents",
                                      );
                                      e.target.value = "";
                                      return;
                                    }

                                    setMemberData((prev) => ({
                                      ...prev,
                                      documents: (prev?.documents || []).map(
                                        (d, i) =>
                                          i === index
                                            ? { ...d, newFile: file }
                                            : d,
                                      ),
                                    }));
                                  }}
                                  style={{
                                    backgroundColor: "white",
                                    border: "2px solid #007bff",
                                  }}
                                />

                                <small className="text-muted">
                                  {doc.type === "Color Passport Size Photo"
                                    ? "Image files only (JPG, PNG)"
                                    : "Upload new file to replace existing document"}
                                </small>
                              </div>
                            ) : (
                              <div
                                style={{
                                  flexGrow: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                {/* VIEW MODE BOX */}
                                <div
                                  style={{
                                    padding: "8px",
                                    border: "2px solid #dee2e6",
                                    borderRadius: "6px",
                                    minHeight: "120px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  {doc.url ? (
                                    <>
                                      <div className="d-flex align-items-center text-muted fw-normal mb-2">
                                        <FileText className="me-2 text-danger" />
                                        <span
                                          style={{
                                            display: "-webkit-box",
                                            WebkitLineClamp: 1,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            wordBreak: "break-word",
                                            whiteSpace: "normal",
                                            maxWidth: "180px",
                                          }}
                                        >
                                          {doc.name ||
                                            `document_${index + 1}.pdf`}
                                        </span>
                                      </div>

                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "center",
                                          gap: "8px",
                                        }}
                                      >
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          style={{
                                            fontSize: "0.7rem",
                                            padding: "0.25rem 0.4rem",
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDocumentView(doc.url);
                                          }}
                                        >
                                          <Eye size={11} className="me-1" />{" "}
                                          View
                                        </Button>

                                        <Button
                                          variant="primary"
                                          size="sm"
                                          style={{
                                            fontSize: "0.7rem",
                                            padding: "0.25rem 0.4rem",
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDocumentDownload(
                                              doc.url,
                                              doc.name ||
                                              doc.type ||
                                              `Document_${index + 1}`,
                                            );
                                          }}
                                        >
                                          <Download
                                            size={11}
                                            className="me-1"
                                          />{" "}
                                          Download
                                        </Button>
                                      </div>
                                    </>
                                  ) : (
                                    <div
                                      className="d-flex align-items-center justify-content-center text-muted fw-normal"
                                      style={{ height: "100%" }}
                                    >
                                      <FileText className="me-2 text-danger" />
                                      <span>No document uploaded</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                      ))}
                  </Row>
                </Container>
              </Form>
            </div>
          );
        }

        // For LifeMember - full documents section
        return (
          <div>
            {approvalStatus !== "approved" && (
              <Alert variant="info" className="mb-4">
                <strong>Verification Under Process:</strong> Your membership is
                currently under verification.{" "}
                {isProfileEditRestricted
                  ? "You can view your information. Editing will be enabled if the Secretary requests changes or after final approval."
                  : "You can view and edit your information."}
              </Alert>
            )}
            <EditStatusAlert sectionName="Uploaded Documents" />
            <ApprovalAlert sectionName="Uploaded Documents" />
            <RejectionAlert sectionName="Uploaded Documents" />
            {editMessage && <EditMessageToast editMessage={editMessage} />}
            <Form>
              <div className="d-flex justify-content-end mb-3">
                {!isEditing || editingSection !== "Uploaded Documents" ? (
                  hasPendingEditRequest("Uploaded Documents") ? (
                    <div className="d-flex align-items-center gap-2">
                      <Lock size={20} className="text-muted" />
                      <span className="text-muted small">
                        Edit request pending
                      </span>
                    </div>
                  ) : isProfileEditRestricted ? (
                    <div className="d-flex align-items-center gap-2">
                      <Lock size={20} className="text-muted" />
                      <span className="text-muted small">
                        Editing locked during verification
                      </span>
                    </div>
                  ) : (
                    <PenLine
                      role="button"
                      onClick={() => handleEditClick("Uploaded Documents")}
                      style={{ cursor: "pointer" }}
                    />
                  )
                ) : (
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="success" onClick={saveChanges}>
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={cancelEditing}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              <Container>
                <h5 className="mb-4 fw-bold">Uploaded Documents:</h5>

                <Row className="mb-4">
                  {[
                    "Internship Certificate",
                    "Provisional Certificate",
                    "ID Card",
                    "Color Passport Size Photo",
                  ].map((docType) => {
                    const existingDoc = memberData?.documents?.find(
                      (d) => d.type === docType,
                    ) || {
                      type: docType,
                      name: "",
                      url: "",
                    };
                    const doc = existingDoc;

                    return (
                      <Col md={4} key={docType}>
                        <Form.Group className="mb-3">
                          <Form.Label className="input-label-aiota-member">
                            {doc.type}:
                          </Form.Label>
                          {isFieldEditable("Uploaded Documents") ? (
                            <div>
                              {doc.url && (
                                <div className="p-2 border border-2 rounded mb-2">
                                  <div className="d-flex align-items-center text-muted fw-normal mb-2">
                                    <FileText className="me-2 text-danger" />
                                    <span
                                      style={{
                                        display: "-webkit-box",
                                        WebkitLineClamp: 1,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        wordBreak: "break-word",
                                        whiteSpace: "normal",
                                        maxWidth: "180px",
                                      }}
                                    >
                                      {doc.name || `document_${doc.type}.pdf`}
                                    </span>
                                  </div>
                                  <div className="d-flex gap-1 mt-1">
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      className="doc-action-btn"
                                      style={{
                                        fontSize: "0.7rem",
                                        padding: "0.25rem 0.4rem",
                                        lineHeight: "1.2",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDocumentView(doc.url);
                                      }}
                                    >
                                      <Eye size={11} className="me-1" /> View
                                    </Button>
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      className="doc-action-btn"
                                      style={{
                                        fontSize: "0.7rem",
                                        padding: "0.25rem 0.4rem",
                                        lineHeight: "1.2",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDocumentDownload(
                                          doc.url,
                                          doc.name || doc.type,
                                        );
                                      }}
                                    >
                                      <Download size={11} className="me-1" />{" "}
                                      Download
                                    </Button>
                                  </div>
                                </div>
                              )}

                              {/* Show selected file indicator */}
                              {doc.newFile && (
                                <Alert variant="success" className="p-2 mb-2">
                                  <small>
                                    ✓ Selected: {doc.newFile.name} (
                                    {(doc.newFile.size / 1024).toFixed(2)} KB)
                                  </small>
                                </Alert>
                              )}

                              <Form.Control
                                type="file"
                                className="input-field-aiota-member fs-6"
                                accept={
                                  doc.type === "Color Passport Size Photo"
                                    ? "image/*"
                                    : ".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                }
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;

                                  if (
                                    doc.type === "Color Passport Size Photo"
                                  ) {
                                    // Enforce image-only
                                    if (!file.type.startsWith("image/")) {
                                      setEditMessage({
                                        type: "danger",
                                        message:
                                          "Color Passport Size Photo accepts only image files (e.g. JPG, PNG).",
                                      });
                                      setEditMessageSection(
                                        "Uploaded Documents",
                                      );
                                      e.target.value = "";
                                      return;
                                    }
                                  }

                                  // 10MB size validation
                                  const maxSize = 10 * 1024 * 1024;
                                  if (file.size > maxSize) {
                                    setEditMessage({
                                      type: "danger",
                                      message: `${doc.type} size exceeds 10MB. Please upload a smaller file.`,
                                    });
                                    setEditMessageSection("Uploaded Documents");
                                    e.target.value = "";
                                    return;
                                  }

                                  setMemberData((prev) => {
                                    const docs = [...(prev?.documents || [])];
                                    const idx = docs.findIndex(
                                      (d) => d.type === doc.type,
                                    );
                                    const updated = {
                                      ...(idx >= 0 ? docs[idx] : doc),
                                      newFile: file,
                                    };
                                    if (idx >= 0) {
                                      docs[idx] = updated;
                                    } else {
                                      docs.push(updated);
                                    }
                                    return {
                                      ...prev,
                                      documents: docs,
                                    };
                                  });
                                }}
                                style={{
                                  backgroundColor: "white",
                                  border: "2px solid #007bff",
                                }}
                              />
                              <small className="text-muted">
                                {doc.type === "Color Passport Size Photo"
                                  ? "Image files only (JPG, PNG)"
                                  : doc.url
                                    ? "Upload new file to replace existing document"
                                    : "Upload document"}
                              </small>
                            </div>
                          ) : (
                            <div>
                              {doc.url && (
                                <div className="p-2 border border-2 rounded mb-2">
                                  <div className="d-flex align-items-center text-muted fw-normal mb-2">
                                    <FileText className="me-2 text-danger" />
                                    <span
                                      style={{
                                        display: "-webkit-box",
                                        WebkitLineClamp: 1,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        wordBreak: "break-word",
                                        whiteSpace: "normal",
                                        maxWidth: "180px",
                                      }}
                                    >
                                      {doc.name || `document_${doc.type}.pdf`}
                                    </span>
                                  </div>
                                  <div className="d-flex gap-1 mt-1">
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      className="doc-action-btn"
                                      style={{
                                        fontSize: "0.7rem",
                                        padding: "0.25rem 0.4rem",
                                        lineHeight: "1.2",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDocumentView(doc.url);
                                      }}
                                    >
                                      <Eye size={11} className="me-1" /> View
                                    </Button>
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      className="doc-action-btn"
                                      style={{
                                        fontSize: "0.7rem",
                                        padding: "0.25rem 0.4rem",
                                        lineHeight: "1.2",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDocumentDownload(
                                          doc.url,
                                          doc.name || doc.type,
                                        );
                                      }}
                                    >
                                      <Download size={11} className="me-1" />{" "}
                                      Download
                                    </Button>
                                  </div>
                                </div>
                              )}
                              {!doc.url && (
                                <div className="p-2 border border-2 rounded">
                                  <div className="d-flex align-items-center text-muted fw-normal">
                                    <FileText className="me-2 text-danger" />
                                    <span>No document uploaded</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    );
                  })}

                  {/* Show default document types if no documents exist */}
                  {(!memberData?.documents ||
                    memberData.documents.length === 0) && (
                      <>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="input-label-aiota-member">
                              Internship Certificate:
                            </Form.Label>
                            {isFieldEditable("Uploaded Documents") ? (
                              <div>
                                <Form.Control
                                  type="file"
                                  className="input-field-aiota-member fs-6"
                                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const type = "Internship Certificate";
                                    const defaultTypes = [
                                      "Provisional Certificate",
                                      "Internship Certificate",
                                      "ID Card",
                                      "Color Passport Size Photo",
                                    ];
                                    setMemberData((prev) => {
                                      const docs = [...(prev?.documents || [])];
                                      defaultTypes.forEach((t) => {
                                        if (!docs.some((d) => d.type === t))
                                          docs.push({
                                            type: t,
                                            name: "",
                                            url: "",
                                          });
                                      });
                                      const idx = docs.findIndex(
                                        (d) => d.type === type,
                                      );
                                      const entry = {
                                        ...(idx >= 0
                                          ? docs[idx]
                                          : { type, name: "", url: "" }),
                                        newFile: file,
                                      };
                                      if (idx >= 0) docs.splice(idx, 1, entry);
                                      else docs.push(entry);
                                      return { ...prev, documents: docs };
                                    });
                                  }}
                                  style={{
                                    backgroundColor: "white",
                                    border: "2px solid #007bff",
                                  }}
                                />
                                <small className="text-muted">
                                  Upload internship certificate
                                </small>
                              </div>
                            ) : (
                              <div className="d-flex align-items-center p-2 border border-2 rounded text-muted fw-normal">
                                <FileText className="me-2 text-danger" />
                                <span>No document uploaded</span>
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="input-label-aiota-member">
                              Provisional Certificate:
                            </Form.Label>
                            {isFieldEditable("Uploaded Documents") ? (
                              <div>
                                <Form.Control
                                  type="file"
                                  className="input-field-aiota-member fs-6"
                                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const type = "Provisional Certificate";
                                    const defaultTypes = [
                                      "Provisional Certificate",
                                      "Internship Certificate",
                                      "ID Card",
                                      "Color Passport Size Photo",
                                    ];
                                    setMemberData((prev) => {
                                      const docs = [...(prev?.documents || [])];
                                      defaultTypes.forEach((t) => {
                                        if (!docs.some((d) => d.type === t))
                                          docs.push({
                                            type: t,
                                            name: "",
                                            url: "",
                                          });
                                      });
                                      const idx = docs.findIndex(
                                        (d) => d.type === type,
                                      );
                                      const entry = {
                                        ...(idx >= 0
                                          ? docs[idx]
                                          : { type, name: "", url: "" }),
                                        newFile: file,
                                      };
                                      if (idx >= 0) docs.splice(idx, 1, entry);
                                      else docs.push(entry);
                                      return { ...prev, documents: docs };
                                    });
                                  }}
                                  style={{
                                    backgroundColor: "white",
                                    border: "2px solid #007bff",
                                  }}
                                />
                                <small className="text-muted">
                                  Upload provisional certificate
                                </small>
                              </div>
                            ) : (
                              <div className="d-flex align-items-center p-2 border border-2 rounded text-muted fw-normal">
                                <FileText className="me-2 text-danger" />
                                <span>No document uploaded</span>
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="input-label-aiota-member">
                              ID Card:
                            </Form.Label>
                            {isFieldEditable("Uploaded Documents") ? (
                              <div>
                                <Form.Control
                                  type="file"
                                  className="input-field-aiota-member fs-6"
                                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const type = "ID Card";
                                    const defaultTypes = [
                                      "Provisional Certificate",
                                      "Internship Certificate",
                                      "ID Card",
                                      "Color Passport Size Photo",
                                    ];
                                    setMemberData((prev) => {
                                      const docs = [...(prev?.documents || [])];
                                      defaultTypes.forEach((t) => {
                                        if (!docs.some((d) => d.type === t))
                                          docs.push({
                                            type: t,
                                            name: "",
                                            url: "",
                                          });
                                      });
                                      const idx = docs.findIndex(
                                        (d) => d.type === type,
                                      );
                                      const entry = {
                                        ...(idx >= 0
                                          ? docs[idx]
                                          : { type, name: "", url: "" }),
                                        newFile: file,
                                      };
                                      if (idx >= 0) docs.splice(idx, 1, entry);
                                      else docs.push(entry);
                                      return { ...prev, documents: docs };
                                    });
                                  }}
                                  style={{
                                    backgroundColor: "white",
                                    border: "2px solid #007bff",
                                  }}
                                />
                                <small className="text-muted">
                                  Upload ID card
                                </small>
                              </div>
                            ) : (
                              <div className="d-flex align-items-center p-2 border border-2 rounded text-muted fw-normal">
                                <FileText className="me-2 text-danger" />
                                <span>No document uploaded</span>
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="input-label-aiota-member">
                              Color Passport Size Photo:
                            </Form.Label>
                            {isFieldEditable("Uploaded Documents") ? (
                              <div>
                                <Form.Control
                                  type="file"
                                  className="input-field-aiota-member fs-6"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;

                                    // Enforce image-only for passport photo
                                    if (!file.type.startsWith("image/")) {
                                      setEditMessage({
                                        type: "danger",
                                        message:
                                          "Color Passport Size Photo accepts only image files (e.g. JPG, PNG).",
                                      });
                                      setEditMessageSection("Uploaded Documents");
                                      e.target.value = "";
                                      return;
                                    }

                                    const type = "Color Passport Size Photo";
                                    const defaultTypes = [
                                      "Provisional Certificate",
                                      "Internship Certificate",
                                      "ID Card",
                                      "Color Passport Size Photo",
                                    ];
                                    setMemberData((prev) => {
                                      const docs = [...(prev?.documents || [])];
                                      defaultTypes.forEach((t) => {
                                        if (!docs.some((d) => d.type === t))
                                          docs.push({
                                            type: t,
                                            name: "",
                                            url: "",
                                          });
                                      });
                                      const idx = docs.findIndex(
                                        (d) => d.type === type,
                                      );
                                      const entry = {
                                        ...(idx >= 0
                                          ? docs[idx]
                                          : { type, name: "", url: "" }),
                                        newFile: file,
                                      };
                                      if (idx >= 0) docs.splice(idx, 1, entry);
                                      else docs.push(entry);
                                      return { ...prev, documents: docs };
                                    });
                                  }}
                                  style={{
                                    backgroundColor: "white",
                                    border: "2px solid #007bff",
                                  }}
                                />
                                <small className="text-muted">
                                  Image files only (JPG, PNG)
                                </small>
                              </div>
                            ) : (
                              <div className="d-flex align-items-center p-2 border border-2 rounded text-muted fw-normal">
                                <FileText className="me-2 text-danger" />
                                <span>No document uploaded</span>
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                      </>
                    )}
                </Row>
              </Container>
            </Form>
          </div>
        );
      case "Receipts":
        // Check if receipt certificate URL exists from backend
        if (memberData?.receiptCertificateUrl) {
          return (
            <div className="profile-content">
              <div className="section-header">
                <h3>Payment Receipt</h3>
              </div>
              <Row className="mb-4">
                <Col md={12} className="mb-3">
                  <Form.Group>
                    <Form.Label className="fw-bold">Payment Invoice</Form.Label>
                    <div className="border border-2 rounded p-3">
                      <embed
                        src={memberData.receiptCertificateUrl}
                        type="application/pdf"
                        width="100%"
                        height="600px"
                      />
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <span className="text-muted">
                          <FileText className="me-2 text-danger" />
                          AIOTA Payment Receipt
                        </span>
                        <Button
                          variant="primary"
                          className="download-btn w-auto"
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href = memberData.receiptCertificateUrl;
                            link.download = "AIOTA_Payment_Receipt.pdf";
                            link.target = "_blank";
                            link.click();
                          }}
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Alert variant="info" className="mt-3">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Note:</strong> This receipt was automatically generated
                after your payment was processed.
              </Alert>
            </div>
          );
        }
        // Fallback when no receipt is available
        return (
          <div className="profile-content">
            <div className="section-header">
              <h3>Receipts</h3>
            </div>
            <div className="text-center py-5">
              <FileText size={48} className="text-muted mb-3" />
              <h5 className="text-muted">No Receipt Available</h5>
              <p className="text-muted">
                Receipt will be available after successful payment completion
                and approval.
              </p>
            </div>
          </div>
        );

      case "Certificate":
        if (approvalStatus !== "approved") {
          return (
            <Alert variant="warning">
              <strong>Verification Under Process:</strong> Your membership is
              currently under verification. The certificate will be available
              once your membership is approved by the secretary.
            </Alert>
          );
        }
        if (memberData.certificateApproval === "pending") {
          return <Alert variant="info">Certificate approval is pending.</Alert>;
        } else if (memberData.certificateApproval === "rejected") {
          return (
            <Alert variant="danger">
              Certificate approval rejected. Reason:{" "}
              {memberData.rejectionReason}
            </Alert>
          );
        } else {
          // Prepare renewal certificates (sorted by issued_at descending - newest first)
          const renewalCertificates = [];
          if (memberData?.certificateIssuedRenewal) {
            if (
              userInfo?.rolename === "AssociateMember" &&
              memberData?.renewalAssociateCertificates?.length > 0
            ) {
              // For Associate members, use renewalAssociateCertificates array
              const sortedRenewals = [
                ...memberData.renewalAssociateCertificates,
              ].sort((a, b) => new Date(b.issued_at) - new Date(a.issued_at));
              sortedRenewals.forEach((cert, index) => {
                renewalCertificates.push({
                  url: cert.certificate_url,
                  issuedAt: cert.issued_at,
                  type: "Renewed Membership Certificate",
                  downloadName: `AIOTA_Renewed_Membership_Certificate_${index + 1}`,
                });
              });
            } else if (
              userInfo?.rolename === "LifeMember" &&
              memberData?.renewedlifeCertiticate
            ) {
              // For Life members, use renewedlifeCertiticate
              // Check if it's an array or single value
              const lifeRenewals = Array.isArray(
                memberData.renewedlifeCertiticate,
              )
                ? memberData.renewedlifeCertiticate
                : [memberData.renewedlifeCertiticate];

              // Sort by issued_at if available (newest first)
              const sortedLifeRenewals = lifeRenewals.sort((a, b) => {
                const dateA =
                  typeof a === "string"
                    ? null
                    : a.issued_at
                      ? new Date(a.issued_at)
                      : null;
                const dateB =
                  typeof b === "string"
                    ? null
                    : b.issued_at
                      ? new Date(b.issued_at)
                      : null;
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return dateB - dateA;
              });

              sortedLifeRenewals.forEach((cert, index) => {
                const certObj =
                  typeof cert === "string"
                    ? {
                      certificate_url: cert,
                      issued_at: new Date().toISOString(),
                    }
                    : cert;
                renewalCertificates.push({
                  url: certObj.certificate_url || certObj,
                  issuedAt: certObj.issued_at || new Date().toISOString(),
                  type: "Renewed Membership Certificate",
                  downloadName: `AIOTA_Renewed_Membership_Certificate_${index + 1}`,
                });
              });
            }
          }

          // Helper function to check if URL is an image
          const isImageUrl = (url) => {
            if (!url) return false;
            return (
              url.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/) !==
              null ||
              url.toLowerCase().includes("/image/") ||
              url.toLowerCase().includes("image/")
            );
          };

          // Helper function for downloading certificates (images or PDFs)
          const handleCertificateDownload = async (
            fileUrl,
            defaultFileName,
          ) => {
            if (!fileUrl) return;

            const urlParts = fileUrl.split("/");
            const lastPart = urlParts[urlParts.length - 1];
            const fileNameWithoutParams = lastPart.split("?")[0];
            const nameWithoutExt = fileNameWithoutParams.split(".")[0];

            // Use filename from URL or fallback
            let certName = nameWithoutExt || defaultFileName;

            try {
              const response = await fetch(fileUrl, { mode: "cors" });
              const blob = await response.blob();

              const blobUrl = window.URL.createObjectURL(blob);

              const extension = blob.type.includes("png")
                ? ".png"
                : blob.type.includes("jpeg")
                  ? ".jpg"
                  : blob.type.includes("gif")
                    ? ".gif"
                    : blob.type.includes("webp")
                      ? ".webp"
                      : blob.type.includes("pdf")
                        ? ".pdf"
                        : "";

              const link = document.createElement("a");
              link.href = blobUrl;
              link.download = `${certName}${extension}`;

              document.body.appendChild(link);
              link.click();

              document.body.removeChild(link);
              window.URL.revokeObjectURL(blobUrl);
            } catch (error) {
              console.error("Download failed:", error);
              window.open(fileUrl, "_blank"); // fallback
            }
          };

          return (
            <div>
              <h5 className="mb-4 fw-bold">Certificates:</h5>
              <Row className="mb-4">
                {/* Renewal Certificates - Display at top if exists */}
                {renewalCertificates.length > 0 &&
                  renewalCertificates.map((renewalCert, index) => (
                    <Col md={12} className="mb-3" key={`renewal-${index}`}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {renewalCert.type}{" "}
                          {renewalCertificates.length > 1
                            ? `#${index + 1}`
                            : ""}
                          {renewalCert.issuedAt && (
                            <span
                              className="text-muted ms-2"
                              style={{
                                fontSize: "0.9rem",
                                fontWeight: "normal",
                              }}
                            >
                              (Issued:{" "}
                              {new Date(
                                renewalCert.issuedAt,
                              ).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              })}
                              )
                            </span>
                          )}
                        </Form.Label>
                        <div className="border border-2 rounded p-3">
                          {renewalCert.url ? (
                            <>
                              {isImageUrl(renewalCert.url) ? (
                                // For images: display as image tag
                                <div className="text-center">
                                  <img
                                    src={renewalCert.url}
                                    alt={renewalCert.type}
                                    style={{
                                      maxWidth: "100%",
                                      maxHeight: "500px",
                                      objectFit: "contain",
                                    }}
                                    className="border rounded"
                                  />
                                </div>
                              ) : (
                                // For PDFs: use embed tag
                                <embed
                                  src={renewalCert.url}
                                  type="application/pdf"
                                  width="100%"
                                  height="500px"
                                />
                              )}
                              <div className="d-flex justify-content-between align-items-center mt-2">
                                <span className="text-muted">
                                  <FileText className="me-2 text-danger" />
                                  {renewalCert.type}
                                  {isImageUrl(renewalCert.url) && (
                                    <span className="ms-2 badge bg-info">
                                      Image
                                    </span>
                                  )}
                                </span>
                                <Button
                                  variant="primary"
                                  className="download-btn w-auto"
                                  onClick={() =>
                                    handleCertificateDownload(
                                      renewalCert.url,
                                      renewalCert.downloadName,
                                    )
                                  }
                                >
                                  <Download size={20} className="me-1" />
                                  Download
                                </Button>
                              </div>
                            </>
                          ) : (
                            <div className="text-center p-4">
                              <FileText size={48} className="text-muted mb-3" />
                              <p className="text-muted">
                                Renewed certificate will be available shortly.
                              </p>
                            </div>
                          )}
                        </div>
                      </Form.Group>
                    </Col>
                  ))}

                {/* Membership Certificate */}
                {(memberData?.lifeCertificateUrl || memberData?.associateCertificate) && (
                  <Col md={12} className="mb-3">
                    <Form.Group>
                      <Form.Label className="fw-bold">
                        Membership Certificate
                      </Form.Label>
                      <div className="border border-2 rounded p-3">
                        {isImageUrl(
                          memberData.lifeCertificateUrl ||
                          memberData.associateCertificate,
                        ) ? (
                          // For images: display as image tag
                          <div className="text-center">
                            <img
                              src={
                                memberData.lifeCertificateUrl ||
                                memberData.associateCertificate
                              }
                              alt="AIOTA Membership Certificate"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "500px",
                                objectFit: "contain",
                              }}
                              className="border rounded"
                            />
                          </div>
                        ) : (
                          // For PDFs: use embed tag
                          <embed
                            src={
                              memberData.lifeCertificateUrl ||
                              memberData.associateCertificate
                            }
                            type="application/pdf"
                            width="100%"
                            height="500px"
                          />
                        )}
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <span className="text-muted">
                            <FileText className="me-2 text-danger" />
                            AIOTA Membership Certificate
                            {isImageUrl(
                              memberData.lifeCertificateUrl ||
                              memberData.associateCertificate,
                            ) && (
                              <span className="ms-2 badge bg-info">
                                Image
                              </span>
                            )}
                          </span>
                          <Button
                            variant="primary"
                            className="download-btn w-auto"
                            onClick={() =>
                              handleCertificateDownload(
                                memberData.lifeCertificateUrl ||
                                memberData.associateCertificate,
                                "AIOTA_Membership_Certificate",
                              )
                            }
                          >
                            <Download size={20} className="me-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </Form.Group>
                  </Col>
                )}

                {/* IOTR Certificate */}
                {userInfo?.rolename !== "AssociateMember" && (!memberData?.hideIotr && memberData?.iotrCertificateUrl ? (
                  <Col md={12} className="mb-3">
                    <Form.Group>
                      <Form.Label className="fw-bold">
                        IOTR Certificate
                      </Form.Label>
                      <div className="border border-2 rounded p-3">
                        {memberData?.iotrCertificateUrl ? (
                          <>
                            {isImageUrl(memberData.iotrCertificateUrl) ? (
                              // For images: display as image tag
                              <div className="text-center">
                                <img
                                  src={memberData.iotrCertificateUrl}
                                  alt="IOTR Certificate"
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "500px",
                                    objectFit: "contain",
                                    }}
                                  className="border rounded"
                                />
                              </div>
                            ) : (
                              // For PDFs: use embed tag
                              <embed
                                src={memberData.iotrCertificateUrl}
                                type="application/pdf"
                                width="100%"
                                height="500px"
                              />
                            )}
                            <div className="d-flex justify-content-between align-items-center mt-2">
                              <span className="text-muted">
                                <FileText className="me-2 text-danger" />
                                Indian Occupational Therapy Registration
                                Certificate
                                {isImageUrl(memberData.iotrCertificateUrl) && (
                                  <span className="ms-2 badge bg-info">
                                    Image
                                  </span>
                                )}
                              </span>
                              <Button
                                variant="primary"
                                className="download-btn w-auto"
                                onClick={() =>
                                  handleCertificateDownload(
                                    memberData.iotrCertificateUrl,
                                    "IOTR_Certificate",
                                  )
                                }
                              >
                                <Download size={20} className="me-1" />
                                Download
                              </Button>
                            </div>
                          </>
                        ) : (
                          <div className="text-center p-4">
                            <FileText size={48} className="text-muted mb-3" />
                            <p className="text-muted">
                              IOTR certificate will be generated after final
                              approval.
                            </p>
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                ) : null)}

                {/* ID Card */}
                {memberData?.idCardUrl && (
                  <Col md={12} className="mb-3">
                    <Form.Group>
                      <Form.Label className="fw-bold">Member ID Card</Form.Label>
                      <div className="border border-2 rounded p-3">
                        {isImageUrl(memberData.idCardUrl) ? (
                          // For images: display as image tag
                          <div className="text-center">
                            <img
                              src={memberData.idCardUrl}
                              alt="AIOTA Member ID Card"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "500px",
                                objectFit: "contain",
                              }}
                              className="border rounded"
                            />
                          </div>
                        ) : (
                          // For PDFs: use embed tag
                          <embed
                            src={memberData.idCardUrl}
                            type="application/pdf"
                            width="100%"
                            height="500px"
                          />
                        )}
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <span className="text-muted">
                            <FileText className="me-2 text-danger" />
                            AIOTA Member ID Card
                            {isImageUrl(memberData.idCardUrl) && (
                              <span className="ms-2 badge bg-info">
                                Image
                              </span>
                            )}
                          </span>
                          <Button
                            variant="primary"
                            className="download-btn w-auto"
                            onClick={() =>
                              handleCertificateDownload(
                                memberData.idCardUrl,
                                "AIOTA_ID_Card",
                              )
                            }
                          >
                            <Download size={20} className="me-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              {/* Show message if no certificates are available */}
              {!memberData?.lifeCertificateUrl &&
                !memberData?.associateCertificate &&
                !memberData?.iotrCertificateUrl &&
                !memberData?.idCardUrl &&
                renewalCertificates.length === 0 && (
                  <Alert variant="info" className="mt-3 text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    No certificates are available.
                  </Alert>
                )}
            </div>
          );
        }
      case "Good Standing":
        return (
          <div className="pt-3">
            <h3 className="fw-bold text-dark mb-4">
              Certificate for Professional status
            </h3>

            {!goodStandingStatus.isGoodStandingDone ? (
              <div className="text-center">
                <Alert variant="info" className="mb-4">
                  <h5>
                    <i className="fas fa-info-circle me-2"></i>Apply for
                    Certificate for Professional status
                  </h5>
                  <p className="mb-0">
                    Certificate for Professional status are provided to Life
                    Members upon request. Click the button below to start your
                    application.
                  </p>
                </Alert>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate("/professional-status")}
                  className="px-4 py-2"
                >
                  <i className="fas fa-file-certificate me-2"></i>
                  Apply for Certificate for Professional status
                </Button>
              </div>
            ) : !goodStandingStatus.isGoodStandingApproved ? (
              <Alert variant="warning" className="mb-4">
                <h5>
                  <i className="fas fa-clock me-2"></i>Application Submitted
                </h5>
                <p className="mb-0">
                  Your Certificate for Professional status has been successfully
                  submitted and is waiting for approval from the authority. You
                  will be notified once the approval process is complete.
                </p>
              </Alert>
            ) : (
              <Alert variant="success" className="mb-4">
                <h5>
                  <i className="fas fa-check-circle me-2"></i>Certificate
                  Approved
                </h5>
                <p className="mb-0">
                  Your Certificate for Professional status has been approved by
                  the authority and will be provided to you shortly.
                </p>
              </Alert>
            )}
          </div>
        );
      case "License Verification":
        return (
          <div className="pt-3">
            <h3 className="fw-bold text-dark mb-4">License Verification</h3>

            {!licenseVerificationStatus.isLicenseVerificationDone ? (
              <div className="text-center">
                <Alert variant="info" className="mb-4">
                  <h5>
                    <i className="fas fa-info-circle me-2"></i>Apply for License
                    Verification
                  </h5>
                  <p className="mb-0">
                    License verification certificates are provided to Life
                    Members upon request. Click the button below to start your
                    application.
                  </p>
                </Alert>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate("/license-verification")}
                  className="px-4 py-2"
                >
                  <i className="fas fa-certificate me-2"></i>
                  Apply for License Verification
                </Button>
              </div>
            ) : !licenseVerificationStatus.isLicenseVerificationApproved ? (
              <Alert variant="warning" className="mb-4">
                <h5>
                  <i className="fas fa-clock me-2"></i>Application Submitted
                </h5>
                <p className="mb-0">
                  Your License Verification application has been successfully
                  submitted and is waiting for approval from the authority. You
                  will be notified once the approval process is complete.
                </p>
              </Alert>
            ) : (
              <Alert variant="success" className="mb-4">
                <h5>
                  <i className="fas fa-check-circle me-2"></i>Certificate
                  Approved
                </h5>
                <p className="mb-0">
                  Your License Verification certificate has been approved by the
                  authority and will be provided to you shortly.
                </p>
              </Alert>
            )}
          </div>
        );
      case "Renewal":
        // Check if all membership approvals are completed
        const allRenewalApprovalsComplete = areAllMembershipApprovalsComplete();

        // If not all approvals are complete, show eligibility message
        if (!allRenewalApprovalsComplete) {
          return (
            <div>
              <h5 className="mb-4 fw-bold">Membership Renewal</h5>
              <Alert variant="warning" className="mb-4">
                <Alert.Heading>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Not Eligible
                </Alert.Heading>
                <p className="mb-2">
                  <strong>You are not eligible for membership renewal</strong>{" "}
                  until all membership approvals are completed.
                </p>
                <div className="mt-3">
                  <strong>Required Approvals Status:</strong>
                  <ul className="mb-0 mt-2">
                    <li>
                      Secretary Approval:{" "}
                      <Badge
                        bg={
                          memberData?.secretaryApprovals === "approved"
                            ? "success"
                            : "warning"
                        }
                      >
                        {memberData?.secretaryApprovals || "pending"}
                      </Badge>
                    </li>
                    <li>
                      Treasurer Approval:{" "}
                      <Badge
                        bg={
                          memberData?.treasurerApprovals === "approved"
                            ? "success"
                            : "warning"
                        }
                      >
                        {memberData?.treasurerApprovals || "pending"}
                      </Badge>
                    </li>
                    <li>
                      President Approval:{" "}
                      <Badge
                        bg={
                          memberData?.presidentApprovals === "approved"
                            ? "success"
                            : "warning"
                        }
                      >
                        {memberData?.presidentApprovals || "pending"}
                      </Badge>
                    </li>
                    <li>
                      Certificate Approval:{" "}
                      <Badge
                        bg={
                          memberData?.certificateApproval === "approved"
                            ? "success"
                            : "warning"
                        }
                      >
                        {memberData?.certificateApproval || "pending"}
                      </Badge>
                    </li>
                  </ul>
                </div>
                <p className="mt-3 mb-0">
                  Please complete all membership approvals before applying for
                  membership renewal.
                </p>
              </Alert>
            </div>
          );
        }

        return (
          <div>
            {editMessage && <EditMessageToast editMessage={editMessage} />}

            {/* Renewal status banners */}
            {memberData && (
              <>
                {/* Certificate issued - renewal completed */}
                {memberData.certificateIssuedRenewal &&
                  memberData.treasurerApproval === "approved" &&
                  memberData.secretaryApproval === "approved" && (
                    <Alert variant="success" className="mb-4">
                      <strong>Renewal Completed Successfully:</strong> Your
                      renewed certificate and ID card have been generated and
                      are available in the Certificate section.
                    </Alert>
                  )}

                {/* Both approved but certificate not issued yet */}
                {memberData.renewalPaymentStatus === "paid" &&
                  !memberData.certificateIssuedRenewal &&
                  memberData.treasurerApproval === "approved" &&
                  memberData.secretaryApproval === "approved" && (
                    <Alert variant="info" className="mb-4">
                      <strong>Issuance In Progress:</strong> Your renewal has
                      been approved. Certificate and ID card will be issued
                      shortly.
                    </Alert>
                  )}

                {/* Secretary rejected - old way: resubmit without new payment */}

                {/* Treasurer rejected */}

                {/* Treasurer pending */}
              </>
            )}

            <Form>
              <Container>
                <h5 className="mb-4 fw-bold">Membership Renewal</h5>

                {/* Show membership status and renewal info after certificate is issued */}
                {memberData?.certificateIssuedRenewal &&
                  memberData?.validTill && (
                    <Alert variant="info" className="mb-4">
                      <strong>Current Membership Status:</strong>
                      <br />
                      Valid until: {memberData.validTill}
                    </Alert>
                  )}

                {memberData.renewalPaymentStatus === "paid" &&
                  memberData.treasurerApproval === "rejected" &&
                  memberData.secretaryApproval !== "rejected" && (
                    <Alert variant="danger" className="mb-4">
                      <strong>Treasurer Rejected:</strong>{" "}
                      {memberData.treasurerRejectedReason ||
                        "Your renewal was rejected by Treasurer."}
                      <br />
                      <strong>
                        Please contact AIOTA Treasurer:For assistance, please
                        contact the AIOTA Treasurer (treasurer@aiota.org) or
                        AIOTA Admin (admin@aiota.org).
                      </strong>
                      {/* <br />
                       <strong>Please contact AIOTA Admin:</strong>{" "}
                      admin@aiota.org */}
                    </Alert>
                  )}

                {/* Secretary pending (only show if treasurer approved) */}
                {memberData.renewalPaymentStatus === "paid" &&
                  memberData.treasurerApproval === "approved" &&
                  memberData.secretaryApproval === "pending" &&
                  !memberData.certificateIssuedRenewal && (
                    <Alert variant="warning" className="mb-4">
                      <strong>Secretary Approval Pending:</strong> Treasurer has
                      approved your renewal. Awaiting Secretary approval.
                    </Alert>
                  )}

                {memberData.renewalPaymentStatus === "paid" &&
                  memberData.treasurerApproval === "pending" &&
                  memberData.secretaryApproval !== "rejected" &&
                  !memberData.certificateIssuedRenewal && (
                    <Alert variant="warning" className="mb-4">
                      <strong>Treasurer Approval Pending:</strong> Your renewal
                      request is awaiting Treasurer approval.
                    </Alert>
                  )}
                {/* Show renewal button only when:
                    1. Payment not paid (initial renewal), OR
                    2. Certificate already issued (renewal flow completed - can renew again) */}
                {(memberData?.renewalPaymentStatus !== "paid" ||
                  memberData?.certificateIssuedRenewal) && (
                    <>
                      <Button
                        variant="primary"
                        size="md"
                        className="w-auto mb-4"
                        onClick={() => {
                          if (restrictionConfig.isApplicationPaused) {
                            setShowRestrictionModal(true);
                            return;
                          }
                          navigate("/membership-renewal-upload", {
                            state: {
                              memberData,
                              membershipType:
                                userInfo?.rolename === "LifeMember"
                                  ? "full"
                                  : "associate",
                            },
                          });
                        }}
                      >
                        {memberData?.certificateIssuedRenewal
                          ? "Renew Membership Again"
                          : "Check Renewal Eligibility & Proceed"}
                      </Button>

                      {/* Show renewal process info when button is visible */}
                      <Alert variant="info" className="mb-4">
                        <strong>Renewal Process:</strong>
                        <br />
                        • Renewal is allowed only 30 days before your memberships
                        expires
                        <br />
                        • You will be required to upload your membership
                        certificate and a passport-size photo
                        <br />
                        • You must accept the terms and conditions before payment
                        <br />• Life memberships are renewed for 15 years,
                        Associate memberships for 1 year
                      </Alert>
                    </>
                  )}

                {/* Resubmit button for secretary rejection (old way - no new payment) */}
                {memberData?.renewalPaymentStatus === "paid" &&
                  memberData?.secretaryApproval === "rejected" &&
                  !memberData?.certificateIssuedRenewal && (
                    <>
                      <Button
                        variant="primary"
                        size="md"
                        className="w-auto mb-4"
                        onClick={() => {
                          navigate("/membership-renewal-upload", {
                            state: {
                              memberData,
                              membershipType:
                                userInfo?.rolename === "LifeMember"
                                  ? "full"
                                  : "associate",
                              // Old way: resubmission doesn't require new payment
                              isResubmission: true,
                              requiresPayment: false,
                            },
                          });
                        }}
                      >
                        Resubmit Documents
                      </Button>
                      {memberData.renewalPaymentStatus === "paid" &&
                        memberData.secretaryApproval === "rejected" && (
                          <Alert variant="danger" className="mb-4">
                            <strong>Secretary Rejected:</strong>{" "}
                            {memberData.secretary_rejected_reason ||
                              memberData.secretary_renewal_rejection_reason ||
                              "Your renewal was rejected by Secretary."}
                            <br />
                            {/* <strong>Resubmission Required:</strong> Please upload
                      updated documents (certificate and passport photo) for
                      resubmission. Payment is not required again. */}
                          </Alert>
                        )}
                      <Alert variant="info" className="mb-4">
                        <strong>Resubmission Process:</strong>
                        <br />
                        • Please upload updated documents (certificate and
                        passport photo)
                        <br />
                        • Payment is not required again for resubmission
                        <br />• Your documents will be reviewed again by the
                        Secretary
                      </Alert>
                    </>
                  )}

                {/* Show waiting message when payment is paid and approvals are pending */}
                {memberData?.renewalPaymentStatus === "paid" &&
                  !memberData?.certificateIssuedRenewal &&
                  memberData?.treasurerApproval !== "rejected" &&
                  memberData?.secretaryApproval !== "rejected" &&
                  (memberData?.treasurerApproval === "pending" ||
                    memberData?.secretaryApproval === "pending") && (
                    <Alert variant="info" className="mb-4">
                      <strong>Payment Completed:</strong>{" "}
                      {memberData?.treasurerApproval === "pending" &&
                        memberData?.secretaryApproval === "pending"
                        ? "Your renewal request is under review. Please wait for Treasurer and Secretary approvals."
                        : memberData?.treasurerApproval === "approved" &&
                          memberData?.secretaryApproval === "pending"
                          ? "Your renewal request is under review. Please wait for Secretary approval."
                          : memberData?.treasurerApproval === "pending" &&
                            memberData?.secretaryApproval === "approved"
                            ? "Your renewal request is under review. Please wait for Treasurer approval."
                            : "Your renewal request is under review."}
                    </Alert>
                  )}
              </Container>
            </Form>
          </div>
        );
      case "Change Password":
        return (
          <div className="aiota-member-change-password-section">
            <h2 className="text-center aiota-member-change-password-heading mb-4 pt-4">
              Change Password
            </h2>
            {passwordError && (
              <Alert variant="danger" className="mt-3">
                {passwordError}
              </Alert>
            )}
            {passwordSuccess && (
              <Alert variant="success" className="mt-3">
                {passwordSuccess}
              </Alert>
            )}
            <Row className="justify-content-center">
              <Col lg={6}>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (validateForm()) {
                      handlePasswordSubmit(e);
                    }
                  }}
                >
                  <Form.Group controlId="currentPassword" className="mb-3">
                    <Form.Label className="form-label-aiota-member-change-password">
                      Current Password *
                    </Form.Label>
                    <Form.Control
                      type="password"
                      name="currentPassword"
                      className="form-control-change-password"
                      value={passwordData.currentPassword}
                      onChange={(e) =>
                        setPasswordData((prev) => ({
                          ...prev,
                          currentPassword: e.target.value,
                        }))
                      }
                      placeholder="Enter current password"
                      required
                      disabled={passwordLoading}
                    />
                  </Form.Group>

                  <Form.Group controlId="newPassword" className="mb-3">
                    <Form.Label className="form-label-change-password">
                      New Password *
                    </Form.Label>
                    <Form.Control
                      type="password"
                      name="newPassword"
                      className="form-control-change-password"
                      value={passwordData.newPassword}
                      onChange={(e) =>
                        setPasswordData((prev) => ({
                          ...prev,
                          newPassword: e.target.value,
                        }))
                      }
                      placeholder="Enter new password"
                      required
                      disabled={passwordLoading}
                    />
                  </Form.Group>

                  <Form.Group controlId="confirmPassword" className="mb-4">
                    <Form.Label className="form-label-change-password">
                      Confirm New Password *
                    </Form.Label>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      className="form-control-change-password"
                      value={passwordData.confirmPassword}
                      onChange={(e) =>
                        setPasswordData((prev) => ({
                          ...prev,
                          confirmPassword: e.target.value,
                        }))
                      }
                      placeholder="Confirm new password"
                      required
                      disabled={passwordLoading}
                    />
                  </Form.Group>

                  {/* {passwordError && (
                    <p className="text-danger text-center mb-3">
                      {passwordError}
                    </p>
                  )}
                  {passwordSuccess && (
                    <p className="text-success text-center mb-3">
                      {passwordSuccess}
                    </p>
                  )} */}

                  <Row className="d-flex gap-2 justify-content-center">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setPasswordData({
                          currentPassword: "",
                          newPassword: "",
                          confirmPassword: "",
                        });
                        setPasswordError("");
                        setPasswordSuccess("");
                        setActiveTab("Approval Flow");
                      }}
                      className="w-auto"
                      disabled={passwordLoading}
                    >
                      Cancel
                    </Button>

                    <Button
                      variant="primary"
                      type="submit"
                      className="change-password-button"
                      disabled={passwordLoading}
                    >
                      {passwordLoading ? "Changing..." : "Change Password"}
                    </Button>
                  </Row>
                </Form>
              </Col>
            </Row>
          </div>
        );
      case "Professional status & License Verification":
        // Only show for Life Members
        if (userInfo?.rolename !== "LifeMember") {
          return (
            <Alert variant="warning">
              <Alert.Heading>Access Restricted</Alert.Heading>
              <p>This section is only available for Life Members.</p>
            </Alert>
          );
        }

        // Check if all membership approvals are completed
        const allApprovalsComplete = areAllMembershipApprovalsComplete();

        // If not all approvals are complete, show eligibility message
        if (!allApprovalsComplete) {
          return (
            <div>
              <h5 className="mb-4 fw-bold">
                Professional status & License Verification
              </h5>
              <Alert variant="warning" className="mb-4">
                <Alert.Heading>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Not Eligible
                </Alert.Heading>
                <p className="mb-2">
                  <strong>
                    You are not eligible for Good Standing or License
                    Verification
                  </strong>{" "}
                  until all membership approvals are completed.
                </p>
                <div className="mt-3">
                  <strong>Required Approvals Status:</strong>
                  <ul className="mb-0 mt-2">
                    <li>
                      Secretary Approval:{" "}
                      <Badge
                        bg={
                          memberData?.secretaryApprovals === "approved"
                            ? "success"
                            : "warning"
                        }
                      >
                        {memberData?.secretaryApprovals || "pending"}
                      </Badge>
                    </li>
                    <li>
                      Treasurer Approval:{" "}
                      <Badge
                        bg={
                          memberData?.treasurerApprovals === "approved"
                            ? "success"
                            : "warning"
                        }
                      >
                        {memberData?.treasurerApprovals || "pending"}
                      </Badge>
                    </li>
                    <li>
                      President Approval:{" "}
                      <Badge
                        bg={
                          memberData?.presidentApprovals === "approved"
                            ? "success"
                            : "warning"
                        }
                      >
                        {memberData?.presidentApprovals || "pending"}
                      </Badge>
                    </li>
                    <li>
                      Certificate Approval:{" "}
                      <Badge
                        bg={
                          memberData?.certificateApproval === "approved"
                            ? "success"
                            : "warning"
                        }
                      >
                        {memberData?.certificateApproval || "pending"}
                      </Badge>
                    </li>
                  </ul>
                </div>
                <p className="mt-3 mb-0">
                  Please complete all membership approvals before applying for
                  Professional status or License Verification certificates.
                </p>
              </Alert>
            </div>
          );
        }

        return (
          <div>
            <h5 className="mb-4 fw-bold">
              Certificate for Professional status & License Verification
            </h5>

            {/* Certificate for Professional status Section */}
            <div className="mb-5">
              <h6 className="mb-3 fw-bold">
                Certificate for Professional status
              </h6>
              <Row className="gap-3 mb-3">
                <Col xs="auto">
                  <Button
                    variant="primary"
                    onClick={() => {
                      if (restrictionConfig.isApplicationPaused) {
                        setShowRestrictionModal(true);
                        return;
                      }
                      setShowProfessionalStatusModal(true);
                    }}
                  >
                    Apply Now
                  </Button>
                </Col>
                <Col xs="auto">
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      setShowGoodStandingHistory(true);
                      fetchGoodStandingHistory();
                    }}
                  >
                    Request History
                  </Button>
                </Col>
              </Row>
            </div>

            {/* License Verification Section */}
            <div className="mb-5">
              <h6 className="mb-3 fw-bold">License Verification</h6>
              <Row className="gap-3 mb-3">
                <Col xs="auto">
                  <Button
                    variant="primary"
                    onClick={() => {
                      if (restrictionConfig.isApplicationPaused) {
                        setShowRestrictionModal(true);
                        return;
                      }
                      if (isNewLicenseVerificationApplicant()) {
                        setShowLicenseVerificationIneligibleModal(true);
                        return;
                      }
                      navigate("/license-verification");
                    }}
                  >
                    Apply Now
                  </Button>
                </Col>
                <Col xs="auto">
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      setShowLicenseVerificationHistory(true);
                      fetchLicenseVerificationHistory();
                    }}
                  >
                    Request History
                  </Button>
                </Col>
              </Row>
            </div>

            {/* Good Standing History Modal */}
            <Modal
              show={showGoodStandingHistory}
              onHide={() => setShowGoodStandingHistory(false)}
              size="lg"
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  Certificate for Professional status - Request History
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {loadingRequests ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" />
                    <p className="mt-2">Loading request history...</p>
                  </div>
                ) : goodStandingRequests.length === 0 ? (
                  <Alert variant="info">
                    <p className="mb-0">
                      No Certificate for Professional status requests found.
                    </p>
                  </Alert>
                ) : (
                  <ListGroup variant="flush">
                    {goodStandingRequests.map((request, index) => {
                      // Check approval/rejection flags
                      const allApproved =
                        request.is_secretary_approved === true &&
                        request.is_president_approved === true &&
                        request.is_treasurer_approved === true;

                      const allRejected =
                        request.is_secretary_rejected === true &&
                        request.is_president_rejected === true &&
                        request.is_treasurer_rejected === true;

                      let status, badgeBg, statusText;

                      if (allApproved) {
                        status = "approved";
                        badgeBg = "success";
                        statusText = "Approved";
                      } else if (allRejected) {
                        status = "rejected";
                        badgeBg = "danger";
                        statusText = "Rejected";
                      } else {
                        // Fallback to existing logic
                        const paymentStatus =
                          request.payment_info?.razorpay_status || "pending";
                        const verificationStatus =
                          request.payment_info?.verification_status ||
                          "pending";
                        status =
                          verificationStatus || paymentStatus || "pending";
                        statusText =
                          status.charAt(0).toUpperCase() + status.slice(1);

                        badgeBg =
                          status === "approved" ||
                            status === "completed" ||
                            status === "captured"
                            ? "success"
                            : status === "rejected" || status === "failed"
                              ? "danger"
                              : status === "pending" || status === "created"
                                ? "warning"
                                : "secondary";
                      }

                      return (
                        <ListGroup.Item
                          key={request._id || index}
                          className="border-bottom"
                        >
                          <Row>
                            <Col md={8}>
                              <strong>Request #{index + 1}</strong>
                              <br />
                              <small className="text-muted">
                                Applied on:{" "}
                                {new Date(request.createdAt).toLocaleString(
                                  "en-IN",
                                  {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )}
                              </small>
                              {request.payment_info && (
                                <>
                                  <br />
                                  <small className="text-muted">
                                    Payment:{" "}
                                    {request.payment_info.razorpay_status ||
                                      "Pending"}
                                    {request.payment_info.amount_rupees &&
                                      ` - ₹${request.payment_info.amount_rupees}`}
                                  </small>
                                </>
                              )}
                            </Col>
                            <Col md={4} className="text-end">
                              <Badge bg={badgeBg}>{statusText}</Badge>
                            </Col>
                          </Row>
                          {request.remarks && (
                            <div className="mt-2">
                              <small className="text-muted">
                                <strong>Remarks:</strong> {request.remarks}
                              </small>
                            </div>
                          )}
                          {
                            <FileList
                              files={request.treasurer_approval_files}
                              title="Reciept"
                            />
                          }
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowGoodStandingHistory(false)}
                >
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

            {/* License Verification History Modal */}
            <Modal
              show={showLicenseVerificationHistory}
              onHide={() => setShowLicenseVerificationHistory(false)}
              size="lg"
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  License Verification - Request History
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {loadingRequests ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" />
                    <p className="mt-2">Loading request history...</p>
                  </div>
                ) : licenseVerificationRequests.length === 0 ? (
                  <Alert variant="info">
                    <p className="mb-0">
                      No License Verification requests found.
                    </p>
                  </Alert>
                ) : (
                  <ListGroup variant="flush">
                    {licenseVerificationRequests.map((request, index) => {
                      // Check approval/rejection flags
                      const allApproved =
                        request.is_secretary_approved === true &&
                        request.is_president_approved === true &&
                        request.is_treasurer_approved === true;

                      const allRejected =
                        request.is_secretary_rejected === true &&
                        request.is_president_rejected === true &&
                        request.is_treasurer_rejected === true;

                      let status, badgeBg, statusText;

                      if (allApproved) {
                        status = "approved";
                        badgeBg = "success";
                        statusText = "Approved";
                      } else if (allRejected) {
                        status = "rejected";
                        badgeBg = "danger";
                        statusText = "Rejected";
                      } else {
                        // Fallback to existing logic
                        const paymentStatus =
                          request.payment_info?.razorpay_status || "pending";
                        const verificationStatus =
                          request.payment_info?.verification_status ||
                          "pending";
                        status =
                          verificationStatus || paymentStatus || "pending";
                        statusText =
                          status.charAt(0).toUpperCase() + status.slice(1);

                        badgeBg =
                          status === "approved" ||
                            status === "completed" ||
                            status === "captured"
                            ? "success"
                            : status === "rejected" || status === "failed"
                              ? "danger"
                              : status === "pending" || status === "created"
                                ? "warning"
                                : "secondary";
                      }

                      return (
                        <ListGroup.Item
                          key={request._id || index}
                          className="border-bottom"
                        >
                          <Row>
                            <Col md={8}>
                              <strong>Request #{index + 1}</strong>
                              <br />
                              <small className="text-muted">
                                Applied on:{" "}
                                {new Date(request.createdAt).toLocaleString(
                                  "en-IN",
                                  {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )}
                              </small>
                              {request.payment_info && (
                                <>
                                  <br />
                                  <small className="text-muted">
                                    Payment:{" "}
                                    {request.payment_info.razorpay_status ||
                                      "Pending"}
                                    {request.payment_info.amount_rupees &&
                                      ` - ₹${request.payment_info.amount_rupees}`}
                                  </small>
                                </>
                              )}
                            </Col>
                            <Col md={4} className="text-end">
                              <Badge bg={badgeBg}>{statusText}</Badge>
                            </Col>
                          </Row>
                          {request.remarks && (
                            <div className="mt-2">
                              <small className="text-muted">
                                <strong>Remarks:</strong> {request.remarks}
                              </small>
                            </div>
                          )}
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowLicenseVerificationHistory(false)}
                >
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        );
      default:
        return (
          <div>
            <h3 className="fw-bold text-dark mb-4">Select a section</h3>
            <Alert variant="light">
              <p className="mb-0">
                Please select a section from the navigation menu.
              </p>
            </Alert>
          </div>
        );
    }
  };

  const ugIntitutionName = institutions.find(
    (el) => el._id === memberData?.ugDetails?.institution,
  )?.name_and_place_institution;
  const pgIntitutionName = institutions.find(
    (el) => el._id === memberData?.pgDetails?.pginstitution,
  )?.name_and_place_institution;

  return (
    <div>
      {/* Fixed Hero Section */}
      <section
        className="hero-fixed"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <div className="hero-scroll-content">
          <div className="hero-content-home poppins-regular">
            <h1 className="hero-heading">
              All India Occupational Therapists' Association
            </h1>
          </div>
        </div>
      </section>
      {/* Scrollable Content Container */}
      <div className="scrollable-content-home">
        <div className="content-container">
          <Container className="">
            <Row>
              <Col md={12} className="heading-left mt-5 mb-4 mb-md-0">
                <div className="login-form-container">
                  <h1 className="login-title">
                    AIOTA Member Profile -{" "}
                    {userInfo?.rolename
                      ? userInfo.rolename.replace(/([a-z])([A-Z])/g, "$1 $2")
                      : "Member"}
                  </h1>
                  {userInfo && (
                    <p className="text-muted">
                      Welcome,{" "}
                      {memberData?.name ||
                        userInfo?.data?.name ||
                        userInfo?.name}
                    </p>
                  )}
                </div>
              </Col>
            </Row>
            <Row className="row-gap-4 gx-3 d-flex align-items-start justify-content-center">
              <Col md={2} className="d-none d-xl-block">
                <Card
                  className="border-1 shadow-sm member-nav-card"
                  style={{ borderRadius: "20px" }}
                >
                  <Card.Body className="p-0">
                    <Nav variant="pills" className="flex-column">
                      {getMenuItems(userInfo?.rolename).map((item, index) => {
                        let isDisabled = false;
                        if (item.requiresApproval) {
                          if (
                            item.name ===
                            "Professional status & License Verification" ||
                            item.name === "Renewal"
                          ) {
                            // For Good Standing & License Verification and Renewal, check all 4 approvals
                            isDisabled = !areAllMembershipApprovalsComplete();
                          } else if (item.name === "Certificate") {
                            isDisabled =
                              approvalStatus !== "approved" ||
                              memberData?.certificateApproval !== "approved";
                          } else {
                            isDisabled = approvalStatus !== "approved";
                          }
                        }
                        const hasPendingEdits = hasPendingEditsInSection(
                          item.name,
                        );

                        return (
                          <Nav.Link
                            key={index}
                            active={!item.isSignOut && activeTab === item.name}
                            className={`nav-link-aiota-member text-start border-0 mb-2 ${item.isSignOut ? "signout-link" : ""
                              } ${isDisabled ? "disabled-link" : ""}`}
                            style={{
                              borderRadius: "25px",
                              width: "100%",
                              backgroundColor:
                                activeTab === item.name && !item.isSignOut
                                  ? "#0031b61a"
                                  : "transparent",
                              color:
                                !item.isSignOut && activeTab === item.name
                                  ? "white"
                                  : isDisabled
                                    ? "#adb5bd"
                                    : "#6c757d",
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              opacity: isDisabled ? 0.6 : 1,
                            }}
                            onClick={() =>
                              handleTabClick(item.name, item.requiresApproval)
                            }
                          >
                            <div className="d-flex align-items-center justify-content-between w-100">
                              <div
                                className="d-flex align-items-center"
                                style={{ minWidth: 0, flex: 1 }}
                              >
                                <div
                                  style={{
                                    width: "24px",
                                    display: "flex",
                                    alignItems: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  {isDisabled && (
                                    <Lock size={16} className="me-2" />
                                  )}
                                </div>
                                <span style={{ wordBreak: "break-word" }}>
                                  {item.name}
                                </span>
                              </div>
                              {hasPendingEdits && (
                                <Badge bg="warning" className="ms-2">
                                  <Clock size={12} className="me-1" />
                                  Pending
                                </Badge>
                              )}
                            </div>
                          </Nav.Link>
                        );
                      })}
                    </Nav>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={10}>
                <Card
                  className="border-1 shadow-sm member-content-card"
                  style={{ borderRadius: "20px" }}
                >
                  {" "}
                  {/* Toggle Button for <1200px */}
                  <div className="d-xl-none mb-3">
                    {/* <GoSidebarCollapse size={32} onClick={handleShow} />Profile Menu */}
                    <Button
                      className="d-flex align-items-center px-3 py-1 profile-btn w-auto"
                      onClick={handleShow}
                    >
                      <RxHamburgerMenu size={16} />
                      <span className="ms-1">Profile Menu</span>
                    </Button>
                  </div>
                  <Card.Body className="p-1">{renderContent()}</Card.Body>
                </Card>
              </Col>
            </Row>
            {/* Offcanvas Sidebar for <1200px */}
            <Offcanvas
              show={showSidebar}
              onHide={handleClose}
              placement="start"
            >
              <Offcanvas.Header closeButton>
                <Offcanvas.Title>Profile Menu</Offcanvas.Title>
              </Offcanvas.Header>
              <Offcanvas.Body>
                <Card
                  className="border-1 shadow-sm member-nav-card"
                  style={{ borderRadius: "20px" }}
                >
                  <Card.Body className="p-0">
                    <Nav variant="pills" className="flex-column">
                      {getMenuItems(userInfo?.rolename).map((item, index) => {
                        let isDisabled = false;
                        if (item.requiresApproval) {
                          if (
                            item.name ===
                            "Professional status & License Verification" ||
                            item.name === "Renewal"
                          ) {
                            // For Good Standing & License Verification and Renewal, check all 4 approvals
                            isDisabled = !areAllMembershipApprovalsComplete();
                          } else if (item.name === "Certificate") {
                            isDisabled =
                              approvalStatus !== "approved" ||
                              memberData?.certificateApproval !== "approved";
                          } else {
                            isDisabled = approvalStatus !== "approved";
                          }
                        }
                        return (
                          <Nav.Link
                            key={index}
                            active={!item.isSignOut && activeTab === item.name}
                            className={`nav-link-aiota-member text-start border-0 mb-2 ${item.isSignOut ? "signout-link" : ""
                              } ${isDisabled ? "disabled-link" : ""}`}
                            style={{
                              borderRadius: "25px",
                              width: "100%",
                              backgroundColor:
                                activeTab === item.name && !item.isSignOut
                                  ? "#0031b61a"
                                  : "transparent",
                              color:
                                !item.isSignOut && activeTab === item.name
                                  ? "white"
                                  : isDisabled
                                    ? "#adb5bd"
                                    : "#6c757d",
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              opacity: isDisabled ? 0.6 : 1,
                            }}
                            onClick={() => {
                              handleTabClick(item.name, item.requiresApproval);
                              handleClose();
                            }}
                          >
                            <div
                              className="d-flex align-items-center"
                              style={{ minWidth: 0 }}
                            >
                              <div
                                style={{
                                  width: "24px",
                                  display: "flex",
                                  alignItems: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {isDisabled && (
                                  <Lock size={16} className="me-2" />
                                )}
                              </div>
                              <span style={{ wordBreak: "break-word" }}>
                                {item.name}
                              </span>
                            </div>
                          </Nav.Link>
                        );
                      })}
                    </Nav>
                  </Card.Body>
                </Card>
              </Offcanvas.Body>
            </Offcanvas>
            <Modal
              className="modal-content-aiota-member"
              show={showSignOutModal}
              onHide={handleSignOutCancel}
              dialogClassName="d-flex justify-content-center align-items-center"
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Confirm Sign Out</Modal.Title>
              </Modal.Header>
              <Modal.Body className="text-center">
                Are you sure you want to sign out?
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleSignOutCancel}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleSignOutConfirm}>
                  Sign Out
                </Button>
              </Modal.Footer>
            </Modal>
            <Modal
              className="modal-content-aiota-member professional-status-modal"
              show={showProfessionalStatusModal}
              onHide={handleProfessionalStatusCancel}
              dialogClassName="d-flex justify-content-center align-items-center"
              centered
            >
              <Modal.Header closeButton className="border-0 pb-0 pt-3">
                <Modal.Title className="d-flex align-items-center gap-2" style={{ color: "#0031b6", fontWeight: 700, fontSize: "1.25rem" }}>
                  <AlertTriangle className="text-warning flex-shrink-0" size={24} />
                  <span>Important Note</span>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="pt-2 pb-2">
                <div
                  style={{
                    backgroundColor: "#fff8eb",
                    borderLeft: "4px solid #ffc107",
                    borderRadius: "8px",
                    padding: "0.85rem 1rem",
                    marginBottom: "0.75rem",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                  }}
                >
                  <p style={{ fontSize: "0.925rem", lineHeight: "1.5", color: "#333", fontWeight: "500", margin: 0, textAlign: "left" }}>
                    Members who have been issued an IOTR Certificate on or before 30 April 2026 are required to provide their IOTR Number and upload a copy of the corresponding IOTR Certificate while applying for Certificate for Professional Status in place of Good standing certificate.
                  </p>
                </div>
                <div
                  style={{
                    backgroundColor: "#f4f7fe",
                    borderLeft: "4px solid #0031b6",
                    borderRadius: "8px",
                    padding: "0.85rem 1rem",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                  }}
                >
                  <p style={{ fontSize: "0.925rem", lineHeight: "1.5", color: "#333", fontWeight: "500", margin: 0, textAlign: "left" }}>
                    Members who do not possess an IOTR Certificate may proceed with the application without providing these details.
                  </p>
                </div>
              </Modal.Body>
              <Modal.Footer className="border-0 pt-0 pb-3 d-flex justify-content-center">
                <Button
                  variant="primary"
                  onClick={handleProfessionalStatusConfirm}
                  style={{
                    backgroundColor: "#0031b6",
                    borderColor: "#0031b6",
                    borderRadius: "30px",
                    width: "auto",
                    height: "auto",
                    padding: "0.5rem 2rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.95rem",
                    fontWeight: "600",
                    transition: "all 0.2s ease",
                    boxShadow: "0 4px 12px rgba(0, 49, 182, 0.2)",
                    whiteSpace: "nowrap"
                  }}
                >
                  Acknowledge & Proceed
                </Button>
              </Modal.Footer>
            </Modal>
            <Modal
              className="modal-content-aiota-member professional-status-modal"
              show={showLicenseVerificationIneligibleModal}
              onHide={() => setShowLicenseVerificationIneligibleModal(false)}
              dialogClassName="d-flex justify-content-center align-items-center"
              centered
            >
              <Modal.Header closeButton className="border-0 pb-0 pt-3">
                <Modal.Title className="d-flex align-items-center gap-2" style={{ color: "#0031b6", fontWeight: 700, fontSize: "1.25rem" }}>
                  <AlertTriangle className="text-warning flex-shrink-0" size={24} />
                  <span>Important Note</span>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="pt-2 pb-2">
                <div
                  style={{
                    backgroundColor: "#fff8eb",
                    borderLeft: "4px solid #ffc107",
                    borderRadius: "8px",
                    padding: "0.85rem 1rem",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                  }}
                >
                  <p style={{ fontSize: "0.925rem", lineHeight: "1.5", color: "#333", fontWeight: "500", margin: 0, textAlign: "left" }}>
                    Dear User,
                    <br />
                    AIOTA has discontinued the issuance of IOTR Certificates and License Verification services for new applicants.
                    <br />
                   As a result, you are currently not eligible to apply for Licence Verification through this portal.
                    <br />
                    Users who were issued an IOTR Certificate on or before 30 April 2026 will continue to remain eligible to apply for Licence Verification.
                    <br/>
                    We appreciate your understanding and cooperation.
                  </p>
                </div>
              </Modal.Body>
              <Modal.Footer className="border-0 pt-0 pb-3 d-flex justify-content-center">
                <Button
                  variant="primary"
                  onClick={() => setShowLicenseVerificationIneligibleModal(false)}
                  style={{
                    backgroundColor: "#0031b6",
                    borderColor: "#0031b6",
                    borderRadius: "30px",
                    width: "auto",
                    height: "auto",
                    padding: "0.5rem 2rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.95rem",
                    fontWeight: "600",
                    transition: "all 0.2s ease",
                    boxShadow: "0 4px 12px rgba(0, 49, 182, 0.2)",
                    whiteSpace: "nowrap"
                  }}
                >
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
            {/* <Modal
              className='modal-content-aiota-member'
              show={showEditModal}
              onHide={handleEditCancel}
              dialogClassName='d-flex justify-content-center align-items-center'
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Confirm Edit Request</Modal.Title>
              </Modal.Header>
              <Modal.Body className='text-center'>
                Are you sure you want to request to edit the "{selectedSection}"
                section? This will send a request to the secretary for approval.
              </Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' onClick={handleEditCancel}>
                  Cancel
                </Button>
                <Button variant='primary' onClick={handleEditConfirm}>
                  Confirm
                </Button>
              </Modal.Footer>
            </Modal> */}
          </Container>
        </div>
      </div>
      <RestrictionModal
        show={showRestrictionModal}
        onHide={() => setShowRestrictionModal(false)}
        title={restrictionConfig.popupTitle}
        message={restrictionConfig.popupMessage}
        buttonText={restrictionConfig.buttonText}
        userName={memberData?.name || userInfo?.name || "User"}
      />
    </div>
  );
};
export default AiotaMemberProfile;
